import React, { Component, useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import { compose } from 'redux';
import { useHistory, useLocation } from 'react-router-dom';
import omit from 'lodash/omit';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { useIntl, FormattedMessage } from '../../util/reactIntl';
import { JOB_WORK_AUTHORIZATION_FILTER_OPTIONS } from '../../util/jobWorkAuthorizationFilter';
import {
  isAnyFilterActive,
  isMainSearchTypeKeywords,
  isOriginInUse,
  getQueryParamNames,
  constructQueryParamName,
} from '../../util/search';
import {
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
  parse,
} from '../../util/urlHelpers';
import { createResourceLocatorString } from '../../util/routes';
import { propTypes } from '../../util/types';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers';
import { userTypeOf } from '../../util/userType';
import { teamRoleOf } from '../../util/teamRole';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';

import { initiateApplyTransaction, saveRFPPayments } from '../ListingPage/ListingPage.duck';
import { uploadBlobFile, uploadResumeBlobFile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';

import { FieldSingleDatePicker, FieldTextInput, H3, H5, LocationAutocompleteInput, LoginPopupModal, NamedRedirect, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import {
  groupListingFieldConfigs,
  initialValues,
  searchParamsPicker,
  validUrlQueryParamsFromProps,
  validFilterParams,
  cleanSearchFromConflictingParams,
  createSearchResultSchema,
  pickListingFieldFilters,
  omitLimitedListingFieldParams,
  getDatesAndSeatsMaybe,
  getSearchPageResourceLocatorStringParams,
  getActiveListingTypes,
} from './SearchPage.shared';

import FilterComponent from './FilterComponent';
import MainPanelHeader from './MainPanelHeader/MainPanelHeader';
import SearchFiltersMobile from './SearchFiltersMobile/SearchFiltersMobile';
import SortBy from './SortBy/SortBy';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';

import css from './SearchPage.module.css';
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';

import * as validators from '../../util/validators';
import { Divider, Affix, Row, Col, Button, message, Select } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { Country, State } from 'country-state-city';
import moment from 'moment';
import { FieldSelect } from '../../components';
import IconPlus from './IconPlus/IconPlus';
import { getAllFieldOptions } from '../../util/getAllFieldOptions';
import { isObject } from 'lodash';
import { payForRFPDuck } from './SearchPage.duck';
import { searchAcceptedConsultants } from '../../ducks/linkedContacts.duck';
import fieldOptions from '../../config/fieldOptions';
import { getAllApplyTransactions } from '../../ducks/user.duck';
import { loadListingsProgressively, searchWithFilters } from './SearchPage.duck';
import { handleGetSubscriptionPrice } from '../../ducks/stripe.duck';
import { getListingWithPrivateData } from '../../util/api';
import { isInvitedToPosting } from '../../util/postingInvitation';

const MODAL_BREAKPOINT = 768; // Search is in modal on mobile layout
const identity = v => v;

// SortBy component has its content in dropdown-popup.
// With this offset we move the dropdown a few pixels on desktop layout.
const FILTER_DROPDOWN_OFFSET = -14;

// Helper to parse filter param values - strips scope prefix before ':' if present
// Replaces the repetitive pattern: let x = String(val || ""); if (x.includes(':')) x = x.split(':')[1];
const parseFilterParam = (value) => {
  const str = String(value || '');
  return str && str.includes(':') ? str.split(':')[1] : str;
};

// Job search's Work Authorization filter. Its options are the shared list the API server also
// matches against, so the panel can only offer codes the server knows.
export const WORK_AUTHORIZATION_FILTER = {
  key: 'work_authorization',
  schemaType: "enum",
  scope: "public",
  categoryConfig: {
    limitToCategoryIds: false,
  },
  enumOptions: JOB_WORK_AUTHORIZATION_FILTER_OPTIONS,
  filterConfig: {
    filterType: "SelectMultipleFilter",
    group: "primary",
    indexForSearch: true,
    label: "Work Authorization Requirements",
    searchMode: 'has_all',
  },
  listingTypeConfig: {
    limitToListingTypeIds: true,
    listingTypeIds: ['job_listing'],
  },
  saveConfig: {
    isRequired: false,
    label: "Work Authorization Requirements",
    placeholderMessage: undefined,
    requiredMessage: undefined,
  },
  showConfig: {
    isDetail: true,
    label: "Work Authorization Requirements",
    unselectedOptions: true,
  },
};

export const SearchPageComponent = props => {
  const {
    intl,
    currentUser,
    listings = [],
    location,
    onManageDisableScrolling,
    pagination,
    scrollingDisabled,
    searchInProgress,
    searchListingsError,
    searchParams = {},
    routeConfiguration,
    config,
    params = {
    },
    history,
    manualFieldOptions,
    currentUserAdminId,
    acceptedConsultantsExist,
    onSearchAcceptedConsultants,
    profileListing,
    onInitiateApplyTransaction,
    onSaveRFPPayment,
    onFetchAllApplyTxns,
    allApplyTransactions,
    // Progressive loading props
    progressiveLoading,
    progressiveLoadingError,
    hasAllListings,
    onLoadListingsProgressively,
    onSearchWithFilters,
    subscriptionsOfUser,
    onPayForRFP,
    subscriptionPrices,
    getSubscriptionPrices,
    personaRecords = [],
    currentUserPersonaRecords,
    onUploadBlobFile,
    onUploadResumeBlobFile,
  } = props;

  const [manualPagination, setManualPagination] = useState(null);

  // Refs for backend fetch debouncing and URL change detection
  const backendFetchDebounceRef = useRef(null);
  const prevLocationSearchRef = useRef(undefined);

  const [isMobileModalOpen, setIsMobileModalOpen] = useState(false);
  const [isLoginPopupOpen, setIsLoginPopupOpen] = useState(false);
  const [applyWorkId, setApplyWorkId] = useState(null);
  const [loginPopupAgencyOnly, setLoginPopupAgencyOnly] = useState(false);

  // Ref to track which listing types we've already loaded to avoid triggering progressive loading on pagination changes
  const loadedListingTypesRef = useRef(new Set());
  const [currentQueryParams, setCurrentQueryParams] = useState(validUrlQueryParamsFromProps(props));
  let selectedFilters = {};
  // tagFilters is now a useMemo (defined after filter state declarations) instead of useState

  // Keyword search state
  const [keyword, setKeyword] = useState('');

  const allOptions = manualFieldOptions ? getAllFieldOptions(manualFieldOptions) : [];

  const [expectedStartDate, setExpectedStartDate] = useState(null);
  const [salaryRange, setSalaryRange] = useState({ min: null, max: null });
  const [billRateRange, setBillRateRange] = useState({ min: null, max: null });
  const [submissionDeadlineRange, setSubmissionDeadlineRange] = useState({ min: null, max: null });
  const [availableToStartRange, setAvailableToStartRange] = useState({ min: null, max: null });
  const [workAuthExpiryRange, setWorkAuthExpiryRange] = useState({ min: null, max: null });
  const [currentEmployer, setCurrentEmployer] = useState('');
  const [countryOfCitizenship, setCountryOfCitizenship] = useState(null);
  const [countryOfResidence, setCountryOfResidence] = useState(null);
  const [billingRateTargetRange, setBillingRateTargetRange] = useState({ min: null, max: null });
  const [postingDateRange, setPostingDateRange] = useState({ min: null, max: null });

  const [showExpectedStartDate, setShowExpectedStartDate] = useState(false);
  const [showLocationSearch, setShowLocationSearch] = useState(false);
  const [showSalaryRange, setShowSalaryRange] = useState(false);
  const [showBillRateRange, setShowBillRateRange] = useState(false);
  const [showAvailableToStartRange, setShowAvailableToStartRange] = useState(false);
  const [showWorkAuthExpiryRange, setShowWorkAuthExpiryRange] = useState(false);
  const [showCurrentEmployer, setShowCurrentEmployer] = useState(false);
  const [showCountryOfCitizenship, setShowCountryOfCitizenship] = useState(false);
  const [showCountryOfResidence, setShowCountryOfResidence] = useState(false);
  const [showBillingRateTargetRange, setShowBillingRateTargetRange] = useState(false);
  const [showSubmissionDeadlineRange, setShowSubmissionDeadlineRange] = useState(false);
  const [showPostingDateRange, setShowPostingDateRange] = useState(false);
  const [showCountryOfFormation, setShowCountryOfFormation] = useState(false);
  const [showStateOfIncorporation, setShowStateOfIncorporation] = useState(false);
  const [showStateOfIncorpDomestic, setShowStateOfIncorpDomestic] = useState(false);
  const [showRegisteredToOperateForeign, setShowRegisteredToOperateForeign] = useState(false);

  const [countryOfFormation, setCountryOfFormation] = useState(null);
  const [stateOfIncorporation, setStateOfIncorporation] = useState(null);
  const [stateOfIncorpDomestic, setStateOfIncorpDomestic] = useState(null);
  const [registeredToOperateForeign, setRegisteredToOperateForeign] = useState(null);

  const [selectedExpectedStartDateJob, setSelectedExpectedStartDateJob] = useState(null);
  const [candidateSkillName, setCandidateSkillName] = useState('');
  const [candidateSkillExp, setCandidateSkillExp] = useState(0);
  const [showSkillsFilter, setShowSkillsFilter] = useState(false);
  const [candidateSkillFilter, setCandidateSkillFilter] = useState([]);
  const [requiredSkillName, setRequiredSkillName] = useState('');
  const [requiredSkillExp, setRequiredSkillExp] = useState(0);
  const [showRequiredSkillsFilter, setShowRequiredSkillsFilter] = useState(false);
  const [requiredSkillFilter, setRequiredSkillFilter] = useState([]);
  const [preferredSkillName, setPreferredSkillName] = useState('');
  const [preferredSkillExp, setPreferredSkillExp] = useState(0);
  const [showPreferredSkillsFilter, setShowPreferredSkillsFilter] = useState(false);
  const [preferredSkillFilter, setPreferredSkillFilter] = useState([]);

  // New custom filters for candidate search
  const [educationLevel, setEducationLevel] = useState('');
  const [showEducationLevel, setShowEducationLevel] = useState(false);
  const [candidateCertifications, setCandidateCertifications] = useState('');
  const [showCandidateCertifications, setShowCandidateCertifications] = useState(false);
  const [jobRole, setJobRole] = useState('');
  const [showJobRole, setShowJobRole] = useState(false);

  // Service domain and Technology domain custom filters (multiple-select dropdowns) - for company_profile
  const [serviceDomains, setServiceDomains] = useState([]);
  const [showServiceDomains, setShowServiceDomains] = useState(false);
  const [technologyDomains, setTechnologyDomains] = useState([]);
  const [showTechnologyDomains, setShowTechnologyDomains] = useState(false);

  // Service domain and Technology domain custom filters (multiple-select dropdowns) - for rfp_listing
  const [rfpServiceDomain, setRfpServiceDomain] = useState([]);
  const [showRfpServiceDomain, setShowRfpServiceDomain] = useState(false);
  const [rfpTechnologyDomain, setRfpTechnologyDomain] = useState([]);
  const [showRfpTechnologyDomain, setShowRfpTechnologyDomain] = useState(false);

  // Verification status states
  const [isIdentityVerified, setIsIdentityVerified] = useState(false);
  const [isWorkVerified, setIsWorkVerified] = useState(false);
  const [isExperienceVerified, setIsExperienceVerified] = useState(false);
  const [isEducationVerified, setIsEducationVerified] = useState(false);
  const [isBusinessIdentityVerified, setIsBusinessIdentityVerified] = useState(false);
  const [isBeneficialOwnersVerified, setIsBeneficialOwnersVerified] = useState(false);
  const [isAuthorizedPersonVerified, setIsAuthorizedPersonVerified] = useState(false);
  const [isInsuranceVerified, setIsInsuranceVerified] = useState(false);


  const [locationSelected, setLocationSelected] = useState(false);
  const locationFormRef = useRef(null);

  const locationPay = useLocation();
  const searchParamsPay = new URLSearchParams(locationPay.search);

  const paymentSuccess = searchParamsPay.get("paymentSuccess");
  const type = searchParamsPay.get("type");
  const paidRFPListingId = searchParamsPay.get("rfpListingId");
  const sessionId = searchParamsPay.get("session_id");

  useEffect(() => {
    onFetchAllApplyTxns();
  }, [onFetchAllApplyTxns]);

  useEffect(() => {
    getSubscriptionPrices();
  }, [getSubscriptionPrices]);

  const checkSubscriptionStatus = () => {
    if (!subscriptionsOfUser || !Array.isArray(subscriptionsOfUser) || subscriptionsOfUser.length === 0) {
      return { hasSubscription: false, isExpired: false, subscription: null };
    }

    const subscription = subscriptionsOfUser[0]; // Get the first subscription
    if (!subscription) {
      return { hasSubscription: false, isExpired: false, subscription: null };
    }
    const expiryDate = new Date(subscription?.expiry);
    const currentDate = new Date();
    // Check both subscriptionData.status and top-level status
    const status = subscription?.subscriptionData?.status || subscription?.status;
    const isExpired = status === 'canceled' || status === 'incomplete' || status === 'incomplete_expired' || status === 'past_due' || status === 'unpaid';
    const isCanceled = status === 'canceled';

    return {
      hasSubscription: isExpired ? false : true,
      isExpired,
      isCanceled,
      subscription,
      planType: isExpired ? 'n/a'
        : subscription?.type === 'monthly'
          ? 'Verified Plus Member Monthly'
          : subscription?.type === 'yearly'
            ? 'Verified Plus Member Yearly'
            : subscription?.type === 'verified-delivery-vendor-monthly'
              ? 'Verified Delivery Vendor Monthly'
              : subscription?.type === 'verified-delivery-vendor-yearly'
                ? 'Verified Delivery Vendor Yearly'
                : subscription?.type === 'verified-plus-delivery-vendor-monthly'
                  ? 'Verified Plus Delivery Vendor Monthly'
                  : 'Verified Plus Delivery Vendor Yearly'
    };
  };

  const subscriptionStatus = checkSubscriptionStatus();

  const onOpenMobileModal = () => {
    setIsMobileModalOpen(true);
  };

  const onCloseMobileModal = () => {
    setIsMobileModalOpen(false);
  };

  const handleApplyAttemptWithoutAuth = (id, listingType) => {
    setApplyWorkId(encodeURIComponent(id));
    setLoginPopupAgencyOnly(listingType === 'rfp_listing');
    setIsLoginPopupOpen(true);
  };
  useEffect(() => {

    const handleRFPApplication = async () => {
      try {
        let listingId = '';
        if (typeof localStorage !== 'undefined') {
          listingId = localStorage.getItem('rfpId');
          localStorage.removeItem('rfpId');
        }

        let currentListing = await getListingWithPrivateData({ listingId: paidRFPListingId, responseFields: ['publicData'], publicDataFields: ['invitedUsersToPosting'] });
        currentListing = currentListing?.data?.data;
        const isUserInvited = isInvitedToPosting(currentListing, { currentUser, profileListing, mainUserId: currentUserAdminId });

        await onSaveRFPPayment(sessionId);
        await onInitiateApplyTransaction(currentListing?.id?.uuid, isUserInvited, '', '', false, [], [], null, 'pending');
      } catch (error) {
        message.error("Failed to send application. Please try again.");
      }
    };

    const handleRFPApplicationComplete = async (paymentSuccess, type, sessionId) => {
      let selectedPriceId = '';
      if (typeof localStorage !== 'undefined') {
        selectedPriceId = localStorage.getItem('priceId');
        localStorage.removeItem('priceId');
      }
      // await handleSaveSubscription(`${type}`, selectedPriceId);
      await handleRFPApplication();

      const url = new URL(window.location.href);
      url.search = "";
      url.searchParams.set("pub_listingType", "rfp_listing");
      window.location.href = url.toString();
      message.success("Application sent successfully!");
    };

    if (paymentSuccess && type && sessionId) {
      handleRFPApplicationComplete(paymentSuccess, type, sessionId);

      return;
    }
  }, [paymentSuccess, type, sessionId]);

  const resetAll = e => {
    const { history, routeConfiguration, config, location } = props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig } = config?.search || {};

    const urlQueryParams = validUrlQueryParamsFromProps(props);
    const filterQueryParamNames = getQueryParamNames(listingFieldsConfig, defaultFiltersConfig);

    setLocationSelected(false);
    setCandidateSkillFilter([]);
    setRequiredSkillFilter([]);
    setPreferredSkillFilter([]);
    setEducationLevel('');
    setCandidateCertifications('');
    setJobRole('');

    // Clear the location form field if the form reference exists
    if (locationFormRef.current) {
      locationFormRef.current.change("addressDetail", null);
    }

    setCurrentQueryParams({});

    // Reset all date and range filters
    setExpectedStartDate(null);
    setSalaryRange({ min: null, max: null });
    setBillRateRange({ min: null, max: null });
    setBillingRateTargetRange({ min: null, max: null });
    setSubmissionDeadlineRange({ min: null, max: null });
    setAvailableToStartRange({ min: null, max: null });
    setWorkAuthExpiryRange({ min: null, max: null });
    setPostingDateRange({ min: null, max: null });

    // Reset other filter states
    setCurrentEmployer('');
    setCountryOfCitizenship(null);
    setCountryOfResidence(null);
    setCountryOfFormation(null);
    setStateOfIncorporation(null);
    setStateOfIncorpDomestic(null);
    setRegisteredToOperateForeign(null);
    setSelectedExpectedStartDateJob(null);
    setKeyword('');

    // Reset service and technology domain filters
    setServiceDomains([]);
    setTechnologyDomains([]);

    // Reset RFP service and technology domain filters
    setRfpServiceDomain([]);
    setRfpTechnologyDomain([]);

    const queryParams = omit(urlQueryParams, filterQueryParamNames);

    if (queryParams?.address) {
      delete queryParams.address;
    }

    if (queryParams?.bounds) {
      delete queryParams.bounds;
    }

    const currentListingType = searchParams?.pub_listingType || 'candidate';

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, { pub_listingType: currentListingType }));
  };

  const handleDateChange = (date) => {
    setExpectedStartDate(date);
  };

  const handleSalaryChange = (values) => {
    // Convert empty strings to null to prevent filtering issues
    const processedValues = {
      min: values?.min === '' || values?.min === undefined || values?.min === null ? null : values?.min,
      max: values?.max === '' || values?.max === undefined || values?.max === null ? null : values?.max
    };
    setSalaryRange(processedValues);
  };

  const handleBillRateChange = (values) => {
    // Convert empty strings to null to prevent filtering issues
    const processedValues = {
      min: values?.min === '' || values?.min === undefined || values?.min === null ? null : values?.min,
      max: values?.max === '' || values?.max === undefined || values?.max === null ? null : values?.max
    };
    setBillRateRange(processedValues);
  };

  const handleAvailableToStartChange = (values) => {
    setAvailableToStartRange(values);
  };

  const handleWorkAuthExpiryChange = (values) => {
    setWorkAuthExpiryRange(values);
  };

  const handleCurrentEmployerChange = (value) => {
    setCurrentEmployer(value);
  };

  const handleCountryOfCitizenshipChange = (value) => {
    setCountryOfCitizenship(value);
  };

  const handleCountryOfResidenceChange = (value) => {
    setCountryOfResidence(value);
  };

  const handleBillingRateTargetChange = (values) => {
    setBillingRateTargetRange(values);
  };

  const handleSubmissionDeadlineChange = (values) => {
    setSubmissionDeadlineRange(values);
  };

  const handlePostingDateChange = (values) => {
    setPostingDateRange(values);
  };

  const handleCountryOfFormationChange = (value) => {
    setCountryOfFormation(value);
  };

  const handleStateOfIncorporationChange = (value) => {
    setStateOfIncorporation(value);
  };

  const handleStateOfIncorpDomesticChange = (value) => {
    setStateOfIncorpDomestic(value);
  };

  const handleRegisteredToOperateForeignChange = (value) => {
    setRegisteredToOperateForeign(value);
  };

  const handleEducationLevelChange = (value) => {
    setEducationLevel(value);
  };

  const handleCandidateCertificationsChange = (value) => {
    setCandidateCertifications(value);
  };

  const handleJobRoleChange = (value) => {
    setJobRole(value);
  };

  const handleServiceDomainsChange = (value) => {
    setServiceDomains(value || []);
  };

  const handleTechnologyDomainsChange = (value) => {
    setTechnologyDomains(value || []);
  };

  const handleRfpServiceDomainChange = (value) => {
    setRfpServiceDomain(value || []);
  };

  const handleRfpTechnologyDomainChange = (value) => {
    setRfpTechnologyDomain(value || []);
  };

  const getHandleChangedValueFn = useHistoryPush => {
    const { history, routeConfiguration, config, location, params = {} } = props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
    const { listingType: listingTypePathParam } = params;
    const { activeListingTypes } = getActiveListingTypes(config, listingTypePathParam);
    const listingCategories = config.categoryConfiguration.categories;
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
      activeListingTypes,
      listingTypePathParam,
    };

    const urlQueryParams = validUrlQueryParamsFromProps(props);

    return updatedURLParams => {
      const newQueryParams = omitLimitedListingFieldParams(
        {
          ...urlQueryParams,
          ...currentQueryParams,
          ...updatedURLParams,
          ...(isMainSearchTypeKeywords(config) ? { keywords: urlQueryParams.keywords } : {}),
          ...getDatesAndSeatsMaybe({ ...urlQueryParams, ...currentQueryParams }, updatedURLParams),
          address: urlQueryParams.address,
          bounds: urlQueryParams.bounds,
        },
        filterConfigs
      );
      setCurrentQueryParams(newQueryParams);

      if (useHistoryPush) {
        const search = cleanSearchFromConflictingParams(newQueryParams, filterConfigs, sortConfig);
        const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
          routeConfiguration,
          location
        );
        history.push(createResourceLocatorString(routeName, routeConfiguration, pathParams, search));
      }
    };
  };

  const handleSortBy = (urlParam, values) => {
    const urlQueryParams = validUrlQueryParamsFromProps(props);
    const queryParams = values ? { ...urlQueryParams, [urlParam]: values } : omit(urlQueryParams, urlParam);
    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(routeConfiguration, location);
    history.push(createResourceLocatorString(routeName, routeConfiguration, pathParams, queryParams));
  };

  const handleResetAll = e => {
    resetAll(e);
    if (e && e.currentTarget) {
      e.currentTarget.blur();
    }
  };

  // Memoized tag filters - replaces both the tagFilters useEffect and getAllCurrentFilters duplication
  const tagFilters = useMemo(() => {
    const currentFilters = validUrlQueryParamsFromProps(props);
    const { pub_work_site_preference } = parse(props.location.search);
    if (pub_work_site_preference) {
      currentFilters.pub_work_site_preference = pub_work_site_preference;
    }

    const customFilters = {};
    if (keyword && keyword.trim() !== '') customFilters.keyword = keyword;
    if (expectedStartDate) customFilters.expectedStartDate = expectedStartDate;
    if (salaryRange?.min || salaryRange?.max) customFilters.salaryRange = salaryRange;
    if (billRateRange?.min || billRateRange?.max) customFilters.billRateRange = billRateRange;
    if (submissionDeadlineRange?.min || submissionDeadlineRange?.max) customFilters.submissionDeadlineRange = submissionDeadlineRange;
    if (postingDateRange?.min || postingDateRange?.max) customFilters.postingDateRange = postingDateRange;
    if (availableToStartRange?.min || availableToStartRange?.max) customFilters.availableToStartRange = availableToStartRange;
    if (workAuthExpiryRange?.min || workAuthExpiryRange?.max) customFilters.workAuthExpiryRange = workAuthExpiryRange;
    if (currentEmployer && currentEmployer.trim() !== '') customFilters.currentEmployer = currentEmployer;
    if (billingRateTargetRange?.min || billingRateTargetRange?.max) customFilters.billingRateTargetRange = billingRateTargetRange;
    if (countryOfCitizenship) customFilters.countryOfCitizenship = countryOfCitizenship;
    if (countryOfResidence) customFilters.countryOfResidence = countryOfResidence;
    if (countryOfFormation) customFilters.countryOfFormation = countryOfFormation;
    if (stateOfIncorpDomestic) customFilters.stateOfIncorpDomestic = stateOfIncorpDomestic;
    if (registeredToOperateForeign) customFilters.registeredToOperateForeign = registeredToOperateForeign;
    if (candidateSkillFilter.length > 0) customFilters.candidateSkillFilter = candidateSkillFilter;
    if (requiredSkillFilter.length > 0) customFilters.requiredSkillFilter = requiredSkillFilter;
    if (preferredSkillFilter.length > 0) customFilters.preferredSkillFilter = preferredSkillFilter;
    if (educationLevel && educationLevel.trim() !== '') customFilters.educationLevel = educationLevel;
    if (candidateCertifications && candidateCertifications.trim() !== '') customFilters.candidateCertifications = candidateCertifications;
    if (jobRole && jobRole.trim() !== '') customFilters.jobRole = jobRole;
    if (serviceDomains && serviceDomains.length > 0) customFilters.serviceDomains = serviceDomains;
    if (technologyDomains && technologyDomains.length > 0) customFilters.technologyDomains = technologyDomains;
    if (rfpServiceDomain && rfpServiceDomain.length > 0) customFilters.rfpServiceDomain = rfpServiceDomain;
    if (rfpTechnologyDomain && rfpTechnologyDomain.length > 0) customFilters.rfpTechnologyDomain = rfpTechnologyDomain;

    return { ...currentFilters, ...customFilters };
  }, [props.location.search, keyword, expectedStartDate, salaryRange, billRateRange, submissionDeadlineRange, postingDateRange, availableToStartRange, workAuthExpiryRange, currentEmployer, billingRateTargetRange, countryOfCitizenship, countryOfResidence, countryOfFormation, stateOfIncorpDomestic, registeredToOperateForeign, candidateSkillFilter, requiredSkillFilter, preferredSkillFilter, educationLevel, candidateCertifications, jobRole, serviceDomains, technologyDomains, rfpServiceDomain, rfpTechnologyDomain]);

  // Keep currentQueryParams in sync with URL changes
  useEffect(() => {
    const urlQueryParams = validUrlQueryParamsFromProps(props);
    setCurrentQueryParams(urlQueryParams);
  }, [props.location.search]);

  // Backend API fetch effect — replaces progressive loading for all listing types.
  // Fires immediately on mount / URL change; debounces 300ms for state-only filter changes.
  // The backend applies all filters and returns only the filtered+paginated results,
  // so the browser network tab never exposes bulk unfiltered listings.
  useEffect(() => {
    const urlParams = parse(location.search);
    const listingType = urlParams?.pub_listingType;
    if (!listingType) return;

    const locationChanged = location.search !== prevLocationSearchRef.current;
    prevLocationSearchRef.current = location.search;

    const doFetch = () => {
      const page = locationChanged ? (parseInt(urlParams?.page) || 1) : 1;
      const filterParams = {
        // URL-based filter params (parsed directly from current URL)
        work_site_preference: parseFilterParam(urlParams?.pub_work_site_preference),
        work_engagement_type: parseFilterParam(urlParams?.pub_work_engagement_type),
        employment_type: parseFilterParam(urlParams?.pub_employment_type),
        experience_level: parseFilterParam(urlParams?.pub_experience_level),
        work_authorization: parseFilterParam(urlParams?.pub_work_authorization),
        certifications_required: parseFilterParam(urlParams?.pub_certifications_required),
        travel_requirement: parseFilterParam(urlParams?.pub_travel_requirement),
        required_ver_job: parseFilterParam(urlParams?.pub_required_ver_job),
        service_domain: parseFilterParam(urlParams?.pub_service_domain),
        technology_domain: parseFilterParam(urlParams?.pub_technology_domain),
        category_codes: parseFilterParam(urlParams?.pub_category_codes),
        organization_type: parseFilterParam(urlParams?.pub_organization_type),
        delivery_model: parseFilterParam(urlParams?.pub_delivery_model),
        security_certifications: parseFilterParam(urlParams?.pub_security_certifications),
        diversity_certifications: parseFilterParam(urlParams?.pub_diversity_certifications),
        kyb_status_verification: parseFilterParam(urlParams?.pub_kyb_status_verification),
        beneficial_ownership_verification: parseFilterParam(urlParams?.pub_beneficial_ownership_verification),
        data_protection_controls: parseFilterParam(urlParams?.pub_data_protection_controls),
        past_performance_evidence: parseFilterParam(urlParams?.pub_past_performance_evidence),
        required_ver: parseFilterParam(urlParams?.pub_required_ver),
        required_ver_part: parseFilterParam(urlParams?.pub_required_ver_part),
        open_to_work: parseFilterParam(urlParams?.pub_open_to_work),
        work_type_preference_candidate: parseFilterParam(urlParams?.pub_work_type_preference_candidate),
        work_site_preference_candidate: parseFilterParam(urlParams?.pub_work_site_preference_candidate),
        willing_to_relocate: parseFilterParam(urlParams?.pub_willing_to_relocate),
        willing_to_back_check: parseFilterParam(urlParams?.pub_willing_to_back_check),
        work_auth_can: parseFilterParam(urlParams?.pub_work_auth_can),
        visa_type: parseFilterParam(urlParams?.pub_visa_type),
        self_emp: parseFilterParam(urlParams?.pub_self_emp),
        employee_count: parseFilterParam(urlParams?.pub_employee_count),
        annual_revenue: parseFilterParam(urlParams?.pub_annual_revenue),
        business_entity_type: parseFilterParam(urlParams?.pub_business_entity_type),
        company_status: parseFilterParam(urlParams?.pub_company_status),
        delivery_models: parseFilterParam(urlParams?.pub_delivery_models),
        pricing_types: parseFilterParam(urlParams?.pub_pricing_types),
        certifications: parseFilterParam(urlParams?.pub_certifications),
        tech_domains: parseFilterParam(urlParams?.pub_tech_domains),
        service_domains_url: parseFilterParam(urlParams?.pub_service_domains),
        geographies_served: parseFilterParam(urlParams?.pub_geographies_served),
        industries_served: parseFilterParam(urlParams?.pub_industries_served),
        // Meta
        selectedListingType: listingType,
        keywordsFromURL: urlParams?.keywords || '',
        sortByFilter: urlParams?.sort || '',
        technology: urlParams?.technology || '',
        // State-based filters
        keyword,
        expectedStartDate,
        salaryRange,
        billRateRange,
        billingRateTargetRange,
        submissionDeadlineRange,
        postingDateRange,
        availableToStartRange,
        workAuthExpiryRange,
        currentEmployer,
        educationLevel,
        candidateCertifications,
        jobRole,
        countryOfCitizenship,
        countryOfResidence,
        countryOfFormation,
        stateOfIncorpDomestic,
        registeredToOperateForeign,
        candidateSkillFilter,
        requiredSkillFilter,
        preferredSkillFilter,
        serviceDomains,
        technologyDomains,
        rfpServiceDomain,
        rfpTechnologyDomain,
      };

      onSearchWithFilters(
        {
          pub_listingType: listingType,
          keywords: urlParams?.keywords,
          sort: urlParams?.sort,
          page,
          perPage: 24,
          include: ['author', 'images'],
          bounds: urlParams?.bounds,
        },
        filterParams,
        config
      );
    };

    if (backendFetchDebounceRef.current) clearTimeout(backendFetchDebounceRef.current);

    if (locationChanged) {
      // URL changed (user navigated / applied a URL filter): fetch immediately
      doFetch();
    } else {
      // State filter changed: debounce 300ms to avoid firing on every keystroke
      backendFetchDebounceRef.current = setTimeout(doFetch, 300);
    }

    return () => {
      if (backendFetchDebounceRef.current) clearTimeout(backendFetchDebounceRef.current);
    };
  }, [
    location.search, onSearchWithFilters, config,
    // State-based filter dependencies (trigger debounced re-fetch)
    keyword, expectedStartDate, salaryRange, billRateRange, billingRateTargetRange,
    submissionDeadlineRange, postingDateRange, availableToStartRange, workAuthExpiryRange,
    currentEmployer, educationLevel, candidateCertifications, jobRole,
    countryOfCitizenship, countryOfResidence, countryOfFormation,
    stateOfIncorpDomestic, registeredToOperateForeign,
    candidateSkillFilter, requiredSkillFilter, preferredSkillFilter,
    serviceDomains, technologyDomains, rfpServiceDomain, rfpTechnologyDomain,
  ]);

  // getAllCurrentFilters now returns the memoized tagFilters (single source of truth)
  const getAllCurrentFilters = useCallback(() => tagFilters, [tagFilters]);

  const selectedListingType = searchParams?.pub_listingType;


  const sortByFilter = searchParams?.sort;

  // Extract keywords from URL for client-side filtering
  const keywordsFromURL = searchParams?.keywords || "";

  // Technology filter from URL (passed from SectionChoice page)
  const technologyFilter = searchParams?.technology || "";

  // Memoized filter param parsing - replaces 40+ repetitive String() + split(':') blocks
  const parsedFilters = useMemo(() => ({
    // Job listing filters
    work_site_preference: parseFilterParam(searchParams?.pub_work_site_preference),
    work_engagement_type: parseFilterParam(searchParams?.pub_work_engagement_type),
    employment_type: parseFilterParam(searchParams?.pub_employment_type),
    experience_level: parseFilterParam(searchParams?.pub_experience_level),
    work_authorization: parseFilterParam(searchParams?.pub_work_authorization),
    certifications_required: parseFilterParam(searchParams?.pub_certifications_required),
    travel_requirement: parseFilterParam(searchParams?.pub_travel_requirement),
    required_ver_job: parseFilterParam(searchParams?.pub_required_ver_job),
    // RFP listing filters
    service_domain: parseFilterParam(searchParams?.pub_service_domain),
    technology_domain: parseFilterParam(searchParams?.pub_technology_domain),
    category_codes: parseFilterParam(searchParams?.pub_category_codes),
    organization_type: parseFilterParam(searchParams?.pub_organization_type),
    delivery_model: parseFilterParam(searchParams?.pub_delivery_model),
    security_certifications: parseFilterParam(searchParams?.pub_security_certifications),
    diversity_certifications: parseFilterParam(searchParams?.pub_diversity_certifications),
    kyb_status_verification: parseFilterParam(searchParams?.pub_kyb_status_verification),
    beneficial_ownership_verification: parseFilterParam(searchParams?.pub_beneficial_ownership_verification),
    data_protection_controls: parseFilterParam(searchParams?.pub_data_protection_controls),
    past_performance_evidence: parseFilterParam(searchParams?.pub_past_performance_evidence),
    // Candidate filters
    required_ver: parseFilterParam(searchParams?.pub_required_ver),
    required_ver_part: parseFilterParam(searchParams?.pub_required_ver_part),
    open_to_work: parseFilterParam(searchParams?.pub_open_to_work),
    work_type_preference_candidate: parseFilterParam(searchParams?.pub_work_type_preference_candidate),
    work_site_preference_candidate: parseFilterParam(searchParams?.pub_work_site_preference_candidate),
    willing_to_relocate: parseFilterParam(searchParams?.pub_willing_to_relocate),
    willing_to_back_check: parseFilterParam(searchParams?.pub_willing_to_back_check),
    work_auth_can: parseFilterParam(searchParams?.pub_work_auth_can),
    visa_type: parseFilterParam(searchParams?.pub_visa_type),
    self_emp: parseFilterParam(searchParams?.pub_self_emp),
    // Agency/Company filters
    employee_count: parseFilterParam(searchParams?.pub_employee_count),
    annual_revenue: parseFilterParam(searchParams?.pub_annual_revenue),
    business_entity_type: parseFilterParam(searchParams?.pub_business_entity_type),
    company_status: parseFilterParam(searchParams?.pub_company_status),
    delivery_models: parseFilterParam(searchParams?.pub_delivery_models),
    pricing_types: parseFilterParam(searchParams?.pub_pricing_types),
    certifications: parseFilterParam(searchParams?.pub_certifications),
    tech_domains: parseFilterParam(searchParams?.pub_tech_domains),
    service_domains_url: parseFilterParam(searchParams?.pub_service_domains),
    geographies_served: parseFilterParam(searchParams?.pub_geographies_served),
    industries_served: parseFilterParam(searchParams?.pub_industries_served),
  }), [searchParams]);

  const {
    work_site_preference, work_engagement_type, employment_type, experience_level,
    work_authorization, certifications_required, travel_requirement, required_ver_job,
    service_domain, technology_domain, category_codes, organization_type,
    delivery_model, security_certifications, diversity_certifications,
    kyb_status_verification, beneficial_ownership_verification,
    data_protection_controls, past_performance_evidence,
    required_ver, required_ver_part,
    open_to_work, work_type_preference_candidate, work_site_preference_candidate,
    willing_to_relocate, willing_to_back_check, work_auth_can, visa_type, self_emp,
    employee_count, annual_revenue, business_entity_type, company_status,
    delivery_models, pricing_types, certifications, tech_domains,
    service_domains_url: service_domains, geographies_served, industries_served,
  } = parsedFilters;

  // If the search page variant is of type /s/:listingType, this defines the :listingType
  // path parameter used to filter the whole page.
  //
  // On a default search page (/s), this constant does not have a value, even when a
  // query parameter ?pub_listingType=[queryParamListingType] is used.
  const { listingType: listingTypePathParam } = params;

  const { listingFields } = config?.listing || {};
  const { defaultFilters: defaultFiltersRaw, sortConfig } = config?.search || {};

  const { activeListingTypes } = getActiveListingTypes(config, listingTypePathParam);

  const defaultFiltersConfig = listingTypePathParam
    ? defaultFiltersRaw.filter(f => f.key !== 'listingType')
    : defaultFiltersRaw;
  const marketplaceCurrency = config.currency;
  const categoryConfiguration = config.categoryConfiguration;
  const listingCategories = categoryConfiguration.categories;
  const listingFieldsConfig = pickListingFieldFilters({
    listingFields,
    locationSearch: location.search,
    categoryConfiguration,
    activeListingTypes,
    listingTypeParam: listingTypePathParam,
  });
  let filterConfigs = {
    listingFieldsConfig,
    defaultFiltersConfig,
    listingCategories,
    activeListingTypes,
  };

  // Page transition might initially use values from previous search
  // urlQueryParams doesn't contain page specific url params
  // like mapSearch, page or origin (origin depends on config.maps.search.sortSearchByDistance)
  const { searchParamsAreInSync, urlQueryParams, searchParamsInURL } = searchParamsPicker(
    location.search,
    searchParams,
    filterConfigs,
    sortConfig,
    isOriginInUse(config)
  );
  const validQueryParams = urlQueryParams;
  selectedFilters = validQueryParams;

  const isKeywordSearch = isMainSearchTypeKeywords(config);
  const builtInPrimaryFilters = defaultFiltersConfig.filter(f =>
    ['categoryLevel', 'listingType'].includes(f.key)
  );
  const builtInFilters = isKeywordSearch
    ? defaultFiltersConfig.filter(
      f => !['keywords', 'categoryLevel', 'listingType'].includes(f.key)
    )
    : defaultFiltersConfig.filter(f => !['categoryLevel', 'listingType'].includes(f.key));
  const [customPrimaryFilters, customSecondaryFilters] = groupListingFieldConfigs(
    listingFieldsConfig,
    activeListingTypes
  );
  let availableFilters = [
    ...builtInPrimaryFilters,
    ...customPrimaryFilters,
    ...builtInFilters,
    ...customSecondaryFilters,
  ];

  // Selected aka active filters
  const isValidDatesFilter =
    searchParamsInURL.dates == null ||
    (searchParamsInURL.dates != null && searchParamsInURL.dates === selectedFilters.dates);
  const keysOfSelectedFilters = Object.keys(selectedFilters);
  const selectedFiltersCountForMobile = isKeywordSearch
    ? keysOfSelectedFilters.filter(f => f !== 'keywords').length
    : keysOfSelectedFilters.length;

  const hasPaginationInfo = !!pagination && pagination.totalItems != null;
  let totalItems =
    searchParamsAreInSync && hasPaginationInfo
      ? pagination.totalItems
      : pagination?.paginationUnsupported
        ? listings.length
        : 0;
  const listingsAreLoaded =
    !searchInProgress &&
    searchParamsAreInSync &&
    !!(hasPaginationInfo || pagination?.paginationUnsupported);

  // Use actual listings data if available, even if sync/pagination info isn't ready yet
  // This prevents broken UI on refresh/duplicate tab
  const shouldShowListings = listingsAreLoaded || (listings && listings.length > 0);

  const conflictingFilterActive = isAnyFilterActive(
    sortConfig.conflictingFilters,
    validQueryParams,
    filterConfigs
  );
  const sortBy = mode => {
    return sortConfig.active ? (
      <SortBy
        sort={validQueryParams[sortConfig.queryParamName]}
        isConflictingFilterActive={!!conflictingFilterActive}
        hasConflictingFilters={!!(sortConfig.conflictingFilters?.length > 0)}
        selectedFilters={selectedFilters}
        onSelect={handleSortBy}
        showAsPopup
        mode={mode}
        contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
      />
    ) : null;
  };
  const noResultsInfo = (
    <NoSearchResultsMaybe
      listingsAreLoaded={shouldShowListings}
      totalItems={totalItems}
      location={location}
      resetAll={resetAll}
    />
  );

  const { title, description, schema } = createSearchResultSchema(
    listings,
    searchParamsInURL || {},
    props.intl,
    routeConfiguration,
    config
  );

  // Set topbar class based on if a modal is open in
  // a child component
  const topbarClasses = isMobileModalOpen
    ? classNames(css.topbarBehindModal, css.topbar) // isMobileModalOpen is not defined
    : css.topbar;

  const currentUserType = userTypeOf(currentUser);
  const agencyType = currentUser?.attributes?.profile?.publicData?.agencyProfile?.agencyType || profileListing?.attributes?.publicData?.agencyProfile?.agencyType || null;

  availableFilters = availableFilters?.map(filter => {
    if (filter?.key === 'listingType') {
      let updatedOptions = filter.options
        .filter(opt => opt.option !== 'free-listing')
        .map(opt => {
          if (opt.option === 'job_listing') {
            return { ...opt, label: 'Work' };
          }
          if (opt.option === 'rfp_listing') {
            return { ...opt, label: 'RFPs' };
          }
          return opt;
        });

      // Add custom types to the filter list
      updatedOptions = [
        { option: 'candidate', label: 'Talent' },
        { option: 'agency', label: 'Suppliers' },
        // { option: 'customer', label: 'Customers' },
        ...updatedOptions,
      ];

      if (currentUserType) {
        updatedOptions = updatedOptions.filter(opt => {
          const type = opt.option;

          if (type === 'job_listing') {
            return currentUserType === 'agency' ? ((agencyType === 'Staffing Services' || agencyType === 'Staffing and Implementation Services') && (teamRoleOf(currentUser) === 'Admin' || teamRoleOf(currentUser) === 'Staffing Sales')) : true;
          }

          if (type === 'rfp_listing') {
            return currentUserType === 'agency' ? ((agencyType === 'Implementation Service' || agencyType === 'Staffing and Implementation Services') && (teamRoleOf(currentUser) === 'Admin' || teamRoleOf(currentUser) === 'Tech Sales')) : (currentUserType !== 'candidate');
          }

          if (type === 'candidate') {
            return ['agency', 'customer'].includes(currentUserType);
          }

          if (type === 'agency') {
            return ['agency', 'customer'].includes(currentUserType);
          }

          return true;
        });
      }

      return {
        ...filter,
        options: updatedOptions,
      };
    }

    return filter;
  });


  const clearSearchLocation = (form) => {

    setLocationSelected(false);

    const urlQueryParams = validUrlQueryParamsFromProps(props);

    if (urlQueryParams?.address) {
      delete urlQueryParams.address;
    }

    if (urlQueryParams?.bounds) {
      delete urlQueryParams.bounds;
    }

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    form.change("addressDetail", null);

    history.push(
      createResourceLocatorString(routeName, routeConfiguration, pathParams, urlQueryParams)
    );
  };

  // Service Domain filter - dynamically generated from fieldOptions
  const serviceDomainOptions = fieldOptions.serviceDomainsOptions
    .filter(opt => opt.key !== 'add_new_option')
    .map(opt => ({ label: opt.label, option: opt.key }));

  // Technology Domain filter - dynamically generated from serviceDomainTechnologyMapping
  const technologyOptions = [];
  const technologyMapping = fieldOptions.serviceDomainTechnologyMapping;
  const addedTechnologies = new Set();

  // Collect all unique technologies from the mapping
  Object.values(technologyMapping).forEach(technologies => {
    technologies.forEach(tech => {
      if (!addedTechnologies.has(tech)) {
        addedTechnologies.add(tech);
        technologyOptions.push({ label: tech, option: tech.toLowerCase().replace(/[^a-z0-9]+/g, '_') });
      }
    });
  });

  // Sort technologies alphabetically by label
  technologyOptions.sort((a, b) => a.label.localeCompare(b.label));

  availableFilters.push({
    key: 'required_ver_job',
    schemaType: "enum",
    scope: "public",
    categoryConfig: {
      limitToCategoryIds: false,
    },
    enumOptions: [
      { label: "Identity", option: "identity" },
      { label: "Work Authorization", option: "work" },
      { label: "None", option: "none" }
    ],
    filterConfig: {
      filterType: "SelectMultipleFilter",
      group: "primary",
      indexForSearch: true,
      label: "Integrity checks required",
      searchMode: 'has_any',
    },
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['job_listing', 'work'],
    },
    saveConfig: {
      isRequired: false,
      label: "Integrity checks required",
      placeholderMessage: undefined,
      requiredMessage: undefined,
    },
    showConfig: {
      isDetail: true,
      label: "Integrity checks required",
      unselectedOptions: true,
    },
  });

  // Candidate required verifications sort (prioritizes listings with matching verifications)
  availableFilters.push({
    key: 'required_ver',
    schemaType: "enum",
    scope: "public",
    categoryConfig: {
      limitToCategoryIds: false,
    },
    enumOptions: [
      { label: "Identity", option: "identity" },
      { label: "Work Authorization", option: "work" },
      { label: "None", option: "none" }
    ],
    filterConfig: {
      filterType: "SelectMultipleFilter",
      group: "primary",
      indexForSearch: true,
      label: "Sort by verifications",
      searchMode: 'has_any',
    },
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['candidate', 'agency'],
    },
    saveConfig: {
      isRequired: false,
      label: "Verifications completed",
      placeholderMessage: undefined,
      requiredMessage: undefined,
    },
    showConfig: {
      isDetail: true,
      label: "Verifications completed",
      unselectedOptions: true,
    },
  });

  // Partner required verifications filter
  availableFilters.push({
    key: 'required_ver_part',
    schemaType: "enum",
    scope: "public",
    categoryConfig: {
      limitToCategoryIds: false,
    },
    enumOptions: [
      { label: "KYB", option: "kyb" },
    ],
    filterConfig: {
      filterType: "SelectMultipleFilter",
      group: "primary",
      indexForSearch: true,
      label: "Verifications needed",
      searchMode: 'has_any',
    },
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['agency'],
    },
    saveConfig: {
      isRequired: false,
      label: "Verifications needed",
      placeholderMessage: undefined,
      requiredMessage: undefined,
    },
    showConfig: {
      isDetail: true,
      label: "Verifications needed",
      unselectedOptions: true,
    },
  });

  // 1. Work Site Preference
  availableFilters.push({
    key: 'work_site_preference',
    schemaType: "enum",
    scope: "public",
    categoryConfig: {
      limitToCategoryIds: false,
    },
    enumOptions: [
      { label: "Remote", option: "remote" },
      { label: "On-site", option: "on_site" },
      { label: "Hybrid", option: "hybrid" },
    ],
    filterConfig: {
      filterType: "SelectMultipleFilter",
      group: "primary",
      indexForSearch: true,
      label: "Work Site Preference",
      searchMode: 'has_any',
    },
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['job_listing'],
    },
    saveConfig: {
      isRequired: false,
      label: "Work Site Preference",
      placeholderMessage: undefined,
      requiredMessage: undefined,
    },
    showConfig: {
      isDetail: true,
      label: "Work Site Preference",
      unselectedOptions: true,
    },
  });

  // 2. Work Engagement Type
  availableFilters.push({
    key: 'work_engagement_type',
    schemaType: "enum",
    scope: "public",
    categoryConfig: {
      limitToCategoryIds: false,
    },
    enumOptions: [
      { label: "W2 Full Time", option: "w2c_full_time" },
      { label: "W2 Contract", option: "w2_contract" },
      { label: "Corp-to-Corp (C2C)", option: "c2c" },
      { label: "C2H", option: "c2h" },
      { label: "1099", option: "1099" },
      { label: "Independent Contractor", option: "independent_contractor" },
      { label: "Intern", option: "intern" },
    ],
    filterConfig: {
      filterType: "SelectMultipleFilter",
      group: "primary",
      indexForSearch: true,
      label: "Work Engagement Type",
      searchMode: 'has_any',
    },
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['job_listing'],
    },
    saveConfig: {
      isRequired: false,
      label: "Work Engagement Type",
      placeholderMessage: undefined,
      requiredMessage: undefined,
    },
    showConfig: {
      isDetail: true,
      label: "Work Engagement Type",
      unselectedOptions: true,
    },
  });

  // 3. Employment Type
  availableFilters.push({
    key: 'employment_type',
    schemaType: "enum",
    scope: "public",
    categoryConfig: {
      limitToCategoryIds: false,
    },
    enumOptions: [
      { label: "Full Time", option: "full time" },
      { label: "Part Time", option: "part time" },
      { label: "Contract", option: "contract" },
      { label: "Temporary", option: "temporary" },
      { label: "Internship", option: "internship" },
      { label: "Freelance", option: "freelance" },
    ],
    filterConfig: {
      filterType: "SelectMultipleFilter",
      group: "primary",
      indexForSearch: true,
      label: "Employment Type",
      searchMode: 'has_any',
    },
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['job_listing'],
    },
    saveConfig: {
      isRequired: false,
      label: "Employment Type",
      placeholderMessage: undefined,
      requiredMessage: undefined,
    },
    showConfig: {
      isDetail: true,
      label: "Employment Type",
      unselectedOptions: true,
    },
  });

  // 4. Experience Level
  availableFilters.push({
    key: 'experience_level',
    schemaType: "enum",
    scope: "public",
    categoryConfig: {
      limitToCategoryIds: false,
    },
    enumOptions: [
      { label: "Entry level", option: "entry_level" },
      { label: "Mid level", option: "mid_level" },
      { label: "Senior level", option: "senior_level" },
      { label: "Lead/Principal", option: "lead_principal" },
      { label: "Executive", option: "executive" },
    ],
    filterConfig: {
      filterType: "SelectMultipleFilter",
      group: "primary",
      indexForSearch: true,
      label: "Experience Level",
      searchMode: 'has_any',
    },
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['job_listing'],
    },
    saveConfig: {
      isRequired: false,
      label: "Experience Level",
      placeholderMessage: undefined,
      requiredMessage: undefined,
    },
    showConfig: {
      isDetail: true,
      label: "Experience Level",
      unselectedOptions: true,
    },
  });

  // 5. Work Authorization Requirements
  availableFilters.push({ ...WORK_AUTHORIZATION_FILTER });

  // 6. Certifications Required
  availableFilters.push({
    key: 'certifications_required',
    schemaType: "enum",
    scope: "public",
    categoryConfig: {
      limitToCategoryIds: false,
    },
    enumOptions: [
      { label: "Yes", option: "yes" },
      { label: "No", option: "no" },
    ],
    filterConfig: {
      filterType: "SelectMultipleFilter",
      group: "primary",
      indexForSearch: true,
      label: "Certifications Required",
      searchMode: 'has_any',
    },
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['job_listing'],
    },
    saveConfig: {
      isRequired: false,
      label: "Certifications Required",
      placeholderMessage: undefined,
      requiredMessage: undefined,
    },
    showConfig: {
      isDetail: true,
      label: "Certifications Required",
      unselectedOptions: true,
    },
  });

  // 7. Travel requried
  availableFilters.push({
    key: 'travel_requirement',
    schemaType: "enum",
    scope: "public",
    categoryConfig: {
      limitToCategoryIds: false,
    },
    enumOptions: [
      { label: "Yes", option: "yes" },
      { label: "No", option: "no" },
    ],
    filterConfig: {
      filterType: "SelectMultipleFilter",
      group: "primary",
      indexForSearch: true,
      label: "Travel Required",
      searchMode: 'has_any',
    },
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['job_listing'],
    },
    saveConfig: {
      isRequired: false,
      label: "Travel Required",
      placeholderMessage: undefined,
      requiredMessage: undefined,
    },
    showConfig: {
      isDetail: true,
      label: "Travel Required",
      unselectedOptions: true,
    },
  });

  // 1. Service Domain - Now rendered as custom multi-select dropdown filter for RFP (see below in JSX)
  // (Removed from availableFilters to render as custom dropdown like Country of residence)

  // 2. Technology Domain - Now rendered as custom multi-select dropdown filter for RFP (see below in JSX)
  // (Removed from availableFilters to render as custom dropdown like Country of residence)

  // 3. Category Codes
  availableFilters.push({
    key: 'category_codes',
    schemaType: "enum",
    scope: "public",
    categoryConfig: {
      limitToCategoryIds: false,
    },
    enumOptions: [
      { label: "NAICS", option: "NAICS" },
      { label: "PSC / FSC", option: "PSC/FSC" },
      { label: "NIGP", option: "NIGP" },
      { label: "UNSPSC", option: "UNSPSC" },
    ],
    filterConfig: {
      filterType: "SelectMultipleFilter",
      group: "primary",
      indexForSearch: true,
      label: "Category Codes",
      searchMode: 'has_any',
    },
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['rfp_listing'],
    },
    saveConfig: {
      isRequired: false,
      label: "Category Codes",
      placeholderMessage: undefined,
      requiredMessage: undefined,
    },
    showConfig: {
      isDetail: true,
      label: "Category Codes",
      unselectedOptions: true,
    },
  });

  // 4. Posting Organization Type
  availableFilters.push({
    key: 'organization_type',
    schemaType: "enum",
    scope: "public",
    categoryConfig: {
      limitToCategoryIds: false,
    },
    enumOptions: [
      { label: "State and Local (SLED)", option: "sled" },
      { label: "Federal", option: "federal" },
      { label: "Non-profit", option: "non_profit" },
      { label: "Publicly listed", option: "publicly_listed" },
      { label: "Private", option: "private" },
    ],
    filterConfig: {
      filterType: "SelectMultipleFilter",
      group: "primary",
      indexForSearch: true,
      label: "Posting Organization Type",
      searchMode: 'has_any',
    },
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['job_listing'],
    },
    saveConfig: {
      isRequired: false,
      label: "Posting Organization Type",
      placeholderMessage: undefined,
      requiredMessage: undefined,
    },
    showConfig: {
      isDetail: true,
      label: "Posting Organization Type",
      unselectedOptions: true,
    },
  });

  // 5. Delivery Model
  availableFilters.push({
    key: 'delivery_model',
    schemaType: "enum",
    scope: "public",
    categoryConfig: {
      limitToCategoryIds: false,
    },
    enumOptions: [
      { label: "Onsite", option: "onsite" },
      { label: "Offsite", option: "offsite" },
      { label: "Offshore", option: "offshore" },
      { label: "Hybrid", option: "hybrid" },
    ],
    filterConfig: {
      filterType: "SelectMultipleFilter",
      group: "primary",
      indexForSearch: true,
      label: "Delivery Model",
      searchMode: 'has_any',
    },
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['job_listing'],
    },
    saveConfig: {
      isRequired: false,
      label: "Delivery Model",
      placeholderMessage: undefined,
      requiredMessage: undefined,
    },
    showConfig: {
      isDetail: true,
      label: "Delivery Model",
      unselectedOptions: true,
    },
  });

  // 6. Security Certifications
  availableFilters.push({
    key: 'security_certifications',
    schemaType: "enum",
    scope: "public",
    categoryConfig: {
      limitToCategoryIds: false,
    },
    enumOptions: [
      { label: "SOC2", option: "soc2" },
      { label: "ISO27001", option: "iso27001" },
      { label: "FedRAMP", option: "fedramp" },
      { label: "PCI", option: "pci" },
      { label: "ISO 9001", option: "iso9001" },
      { label: "CMMI Level 3+", option: "cmmi_level3" },
    ],
    filterConfig: {
      filterType: "SelectMultipleFilter",
      group: "primary",
      indexForSearch: true,
      label: "Security Certifications",
      searchMode: 'has_any',
    },
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['job_listing'],
    },
    saveConfig: {
      isRequired: false,
      label: "Security Certifications",
      placeholderMessage: undefined,
      requiredMessage: undefined,
    },
    showConfig: {
      isDetail: true,
      label: "Security Certifications",
      unselectedOptions: true,
    },
  });

  // 7. Diversity Certifications
  if (currentUser) {
    availableFilters.push({
      key: 'diversity_certifications',
      schemaType: "enum",
      scope: "public",
      categoryConfig: {
        limitToCategoryIds: false,
      },
      enumOptions: [
        { label: "Minority-Owned", option: "minority_owned" },
        { label: "Woman-Owned", option: "woman_owned" },
        { label: "Veteran-Owned", option: "veteran_owned" },
        { label: "SBA 8(a)", option: "sba_8(a)" },
        { label: "NMSDC", option: "nmsdc" },
      ],
      filterConfig: {
        filterType: "SelectMultipleFilter",
        group: "primary",
        indexForSearch: true,
        label: "Diversity Certifications",
        searchMode: 'has_any',
      },
      listingTypeConfig: {
        limitToListingTypeIds: true,
        listingTypeIds: ['job_listing'],
      },
      saveConfig: {
        isRequired: false,
        label: "Diversity Certifications",
        placeholderMessage: undefined,
        requiredMessage: undefined,
      },
      showConfig: {
        isDetail: true,
        label: "Diversity Certifications",
        unselectedOptions: true,
      },
    });
  }

  // 8. KYB status verification needed
  availableFilters.push({
    key: 'kyb_status_verification',
    schemaType: "enum",
    scope: "public",
    categoryConfig: {
      limitToCategoryIds: false,
    },
    enumOptions: [
      { label: "Yes", option: "yes" },
      { label: "No", option: "no" },
    ],
    filterConfig: {
      filterType: "SelectMultipleFilter",
      group: "primary",
      indexForSearch: true,
      label: "KYB Status Verification Needed",
      searchMode: 'has_any',
    },
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['agency'],
    },
    saveConfig: {
      isRequired: false,
      label: "KYB Status Verification Needed",
      placeholderMessage: undefined,
      requiredMessage: undefined,
    },
    showConfig: {
      isDetail: true,
      label: "KYB Status Verification Needed",
      unselectedOptions: true,
    },
  });

  // 9. Beneficial ownership verification needed
  availableFilters.push({
    key: 'beneficial_ownership_verification',
    schemaType: "enum",
    scope: "public",
    categoryConfig: {
      limitToCategoryIds: false,
    },
    enumOptions: [
      { label: "Yes", option: "yes" },
      { label: "No", option: "no" },
    ],
    filterConfig: {
      filterType: "SelectMultipleFilter",
      group: "primary",
      indexForSearch: true,
      label: "Beneficial Ownership Verification Needed",
      searchMode: 'has_any',
    },
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['agency'],
    },
    saveConfig: {
      isRequired: false,
      label: "Beneficial Ownership Verification Needed",
      placeholderMessage: undefined,
      requiredMessage: undefined,
    },
    showConfig: {
      isDetail: true,
      label: "Beneficial Ownership Verification Needed",
      unselectedOptions: true,
    },
  });

  // 11. Data protection controls
  availableFilters.push({
    key: 'data_protection_controls',
    schemaType: "enum",
    scope: "public",
    categoryConfig: {
      limitToCategoryIds: false,
    },
    enumOptions: [
      { label: 'DPA', option: 'DPA' },
      { label: 'SCCs', option: 'SCCs' },
      { label: 'Data Residency', option: 'Data Residency' },
    ],
    filterConfig: {
      filterType: "SelectMultipleFilter",
      group: "primary",
      indexForSearch: true,
      label: "Data Protection Controls",
      searchMode: 'has_any',
    },
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['agency'],
    },
    saveConfig: {
      isRequired: false,
      label: "Data Protection Controls",
      placeholderMessage: undefined,
      requiredMessage: undefined,
    },
    showConfig: {
      isDetail: true,
      label: "Data Protection Controls",
      unselectedOptions: true,
    },
  });

  // 12. Past performance evidence required
  availableFilters.push({
    key: 'past_performance_evidence',
    schemaType: "enum",
    scope: "public",
    categoryConfig: {
      limitToCategoryIds: false,
    },
    enumOptions: [
      { label: "Yes", option: "yes" },
      { label: "No", option: "no" },
    ],
    filterConfig: {
      filterType: "SelectMultipleFilter",
      group: "primary",
      indexForSearch: true,
      label: "Past Performance Evidence Required",
      searchMode: 'has_any',
    },
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['agency'],
    },
    saveConfig: {
      isRequired: false,
      label: "Past Performance Evidence Required",
      placeholderMessage: undefined,
      requiredMessage: undefined,
    },
    showConfig: {
      isDetail: true,
      label: "Past Performance Evidence Required",
      unselectedOptions: true,
    },
  });

  // 1. Open to Work
  availableFilters.push({
    key: 'open_to_work',
    schemaType: "enum",
    scope: "public",
    categoryConfig: {
      limitToCategoryIds: false,
    },
    enumOptions: [
      { label: "Yes", option: "yes" },
      { label: "No", option: "no" },
    ],
    filterConfig: {
      filterType: "SelectMultipleFilter",
      group: "primary",
      indexForSearch: true,
      label: "Open to Work",
      searchMode: 'has_any',
    },
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['candidate'], // Change this to your candidate listing type
    },
    saveConfig: {
      isRequired: false,
      label: "Open to Work",
      placeholderMessage: undefined,
      requiredMessage: undefined,
    },
    showConfig: {
      isDetail: true,
      label: "Open to Work",
      unselectedOptions: true,
    },
  });

  // 2. Work Type Preference
  availableFilters.push({
    key: 'work_type_preference_candidate',
    schemaType: "enum",
    scope: "public",
    categoryConfig: {
      limitToCategoryIds: false,
    },
    enumOptions: [
      { label: "W2 Full Time", option: "w2c_full_time" },
      { label: "W2 Contract", option: "w2_contract" },
      { label: "Corp-to-Corp (C2C)", option: "c2c" },
      { label: "C2H", option: "c2h" },
      { label: "1099", option: "1099" },
      { label: "Independent Contractor", option: "independent_contractor" },
      { label: "Intern", option: "intern" },
    ],
    filterConfig: {
      filterType: "SelectMultipleFilter",
      group: "primary",
      indexForSearch: true,
      label: "Work Type Preference",
      searchMode: 'has_any',
    },
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['candidate'],
    },
    saveConfig: {
      isRequired: false,
      label: "Work Type Preference",
      placeholderMessage: undefined,
      requiredMessage: undefined,
    },
    showConfig: {
      isDetail: true,
      label: "Work Type Preference",
      unselectedOptions: true,
    },
  });

  // 3. Work Site Preference
  availableFilters.push({
    key: 'work_site_preference_candidate',
    schemaType: "enum",
    scope: "public",
    categoryConfig: {
      limitToCategoryIds: false,
    },
    enumOptions: [
      { label: "On-site", option: "on-site" },
      { label: "Remote", option: "remote" },
      { label: "Hybrid", option: "hybrid" },
    ],
    filterConfig: {
      filterType: "SelectMultipleFilter",
      group: "primary",
      indexForSearch: true,
      label: "Work Site Preference",
      searchMode: 'has_any',
    },
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['candidate'],
    },
    saveConfig: {
      isRequired: false,
      label: "Work Site Preference",
      placeholderMessage: undefined,
      requiredMessage: undefined,
    },
    showConfig: {
      isDetail: true,
      label: "Work Site Preference",
      unselectedOptions: true,
    },
  });

  // 4. Willing To Relocate
  availableFilters.push({
    key: 'willing_to_relocate',
    schemaType: "enum",
    scope: "public",
    categoryConfig: {
      limitToCategoryIds: false,
    },
    enumOptions: [
      { label: "Yes", option: "yes" },
      { label: "No", option: "no" },
    ],
    filterConfig: {
      filterType: "SelectMultipleFilter",
      group: "primary",
      indexForSearch: true,
      label: "Willing To Relocate",
      searchMode: 'has_any',
    },
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['candidate'],
    },
    saveConfig: {
      isRequired: false,
      label: "Willing To Relocate",
      placeholderMessage: undefined,
      requiredMessage: undefined,
    },
    showConfig: {
      isDetail: true,
      label: "Willing To Relocate",
      unselectedOptions: true,
    },
  });

  // 5. Willing to undergo background check
  availableFilters.push({
    key: 'willing_to_back_check',
    schemaType: "enum",
    scope: "public",
    categoryConfig: {
      limitToCategoryIds: false,
    },
    enumOptions: [
      { label: "Yes", option: "yes" },
      { label: "No", option: "no" },
    ],
    filterConfig: {
      filterType: "SelectMultipleFilter",
      group: "primary",
      indexForSearch: true,
      label: "Willing to undergo background check",
      searchMode: 'has_any',
    },
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['candidate'],
    },
    saveConfig: {
      isRequired: false,
      label: "Willing to undergo background check",
      placeholderMessage: undefined,
      requiredMessage: undefined,
    },
    showConfig: {
      isDetail: true,
      label: "Willing to undergo background check",
      unselectedOptions: true,
    },
  });

  // 6. Authorized to work in the US
  // availableFilters.push({
  //   key: 'authorized_to_work_us',
  //   schemaType: "enum",
  //   scope: "public",
  //   categoryConfig: {
  //     limitToCategoryIds: false,
  //   },
  //   enumOptions: [
  //     { label: "Yes", option: "yes" },
  //     { label: "No", option: "no" },
  //   ],
  //   filterConfig: {
  //     filterType: "SelectMultipleFilter",
  //     group: "primary",
  //     indexForSearch: true,
  //     label: "Authorized to work in the US",
  //     searchMode: 'has_any',
  //   },
  //   listingTypeConfig: {
  //     limitToListingTypeIds: true,
  //     listingTypeIds: ['candidate'],
  //   },
  //   saveConfig: {
  //     isRequired: false,
  //     label: "Authorized to work in the US",
  //     placeholderMessage: undefined,
  //     requiredMessage: undefined,
  //   },
  //   showConfig: {
  //     isDetail: true,
  //     label: "Authorized to work in the US",
  //     unselectedOptions: true,
  //   },
  // });

  // 6. Sort by Work auth can
  availableFilters.push({
    key: 'work_auth_can',
    schemaType: "enum",
    scope: "public",
    categoryConfig: {
      limitToCategoryIds: false,
    },
    enumOptions: [
      { option: "Auth_without_spons", label: "Authorized to work in the U.S. without employer sponsorship" },
      { option: "Auth_with_spons", label: "Authorized to work in the U.S. and requires current/future employer sponsorship" },
      { option: 'Auth_limited_duration', label: 'Authorized to work in the U.S. for a limited duration (with valid work authorization/EAD)' },
      // { option: 'Legally_auth', label: 'Legally authorized to work in the U.S.' },
      // { option: 'US_Citizen', label: 'U.S. Citizen' },
      // { option: 'Green_Card_holder', label: 'Lawful Permanent Resident (Green Card holder)' },
    ],
    filterConfig: {
      filterType: "SelectMultipleFilter",
      group: "primary",
      indexForSearch: true,
      label: "Sort by Authorized to work in the US",
      searchMode: 'has_any',
    },
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['candidate'],
    },
    saveConfig: {
      isRequired: false,
      label: "Authorized to work in the US",
      placeholderMessage: undefined,
      requiredMessage: undefined,
    },
    showConfig: {
      isDetail: true,
      label: "Authorized to work in the US",
      unselectedOptions: true,
    },
  });

  // 7. Visa type
  if (currentUser) {
    // availableFilters.push({
    //   key: 'visa_type',
    //   schemaType: "enum",
    //   scope: "public",
    //   categoryConfig: {
    //     limitToCategoryIds: false,
    //   },
    //   enumOptions: [
    //     { option: 'cpt', label: 'CPT' },
    //     { option: 'gcead', label: 'GC EAD' },
    //     { option: 'greencard', label: 'Green Card' },
    //     { option: 'h1b', label: 'H-1B' },
    //     { option: 'h4ead', label: 'H-4 EAD' },
    //     { option: 'l1al1b', label: 'L1A/L1B' },
    //     { option: 'l2al2b', label: 'L2A/L2B' },
    //     { option: 'opt', label: 'OPT' },
    //     { option: 'tn', label: 'TN' },
    //     { option: 'uscitizen', label: 'US Citizen' },
    //   ],
    //   filterConfig: {
    //     filterType: "SelectMultipleFilter",
    //     group: "primary",
    //     indexForSearch: true,
    //     label: "Visa Type",
    //     searchMode: 'has_any',
    //   },
    //   listingTypeConfig: {
    //     limitToListingTypeIds: true,
    //     listingTypeIds: ['candidate'],
    //   },
    //   saveConfig: {
    //     isRequired: false,
    //     label: "Visa Type",
    //     placeholderMessage: undefined,
    //     requiredMessage: undefined,
    //   },
    //   showConfig: {
    //     isDetail: true,
    //     label: "Visa Type",
    //     unselectedOptions: true,
    //   },
    // });
  }

  // 8. Self employed
  availableFilters.push({
    key: 'self_emp',
    schemaType: "enum",
    scope: "public",
    categoryConfig: {
      limitToCategoryIds: false,
    },
    enumOptions: [
      { option: 'yes', label: 'Yes' },
      { option: 'no', label: 'No' },
    ],
    filterConfig: {
      filterType: "SelectMultipleFilter",
      group: "primary",
      indexForSearch: true,
      label: "Self employed",
      searchMode: 'has_any',
    },
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['candidate'],
    },
    saveConfig: {
      isRequired: false,
      label: "Self employed",
      placeholderMessage: undefined,
      requiredMessage: undefined,
    },
    showConfig: {
      isDetail: true,
      label: "Self employed",
      unselectedOptions: true,
    },
  });

  // 1. Employee Count
  availableFilters.push({
    key: 'employee_count',
    schemaType: "enum",
    scope: "public",
    categoryConfig: {
      limitToCategoryIds: false,
    },
    enumOptions: [
      { label: "1-10", option: "1-10" },
      { label: "11-50", option: "11-50" },
      { label: "51-200", option: "51-200" },
      { label: "201-500", option: "201-500" },
      { label: "500+", option: "500+" },
    ],
    filterConfig: {
      filterType: "SelectMultipleFilter",
      group: "primary",
      indexForSearch: true,
      label: "Employee Count",
      searchMode: 'has_any',
    },
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['company_profile'], // Change to your company listing type
    },
    saveConfig: {
      isRequired: false,
      label: "Employee Count",
      placeholderMessage: undefined,
      requiredMessage: undefined,
    },
    showConfig: {
      isDetail: true,
      label: "Employee Count",
      unselectedOptions: true,
    },
  });

  // 2. Annual Revenue
  availableFilters.push({
    key: 'annual_revenue',
    schemaType: "enum",
    scope: "public",
    categoryConfig: {
      limitToCategoryIds: false,
    },
    enumOptions: [
      { label: "Under $1M", option: "Under $1M" },
      { label: "$1M-$5M", option: "$1M-$5M" },
      { label: "$5M-$25M", option: "$5M-$25M" },
      { label: "$25M-$100M", option: "$25M-$100M" },
      { label: "$100M+", option: "$100M+" },
    ],
    filterConfig: {
      filterType: "SelectMultipleFilter",
      group: "primary",
      indexForSearch: true,
      label: "Annual Revenue",
      searchMode: 'has_any',
    },
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['company_profile'],
    },
    saveConfig: {
      isRequired: false,
      label: "Annual Revenue",
      placeholderMessage: undefined,
      requiredMessage: undefined,
    },
    showConfig: {
      isDetail: true,
      label: "Annual Revenue",
      unselectedOptions: true,
    },
  });

  // 3. Business Entity Type
  availableFilters.push({
    key: 'business_entity_type',
    schemaType: "enum",
    scope: "public",
    categoryConfig: {
      limitToCategoryIds: false,
    },
    enumOptions: [
      { label: "LLC (Limited Liability Company)", option: "llc" },
      { label: "Sole Proprietorship", option: "sole_proprietorship" },
      { label: "Partnership", option: "partnership" },
      { label: "Corporation (C-Corp)", option: "corporation_c" },
      { label: "S Corporation (S-Corp)", option: "s_corporation" },
      { label: "Nonprofit", option: "nonprofit" },
      { label: "Cooperative", option: "cooperative" },
    ],
    filterConfig: {
      filterType: "SelectMultipleFilter",
      group: "primary",
      indexForSearch: true,
      label: "Business Entity Type",
      searchMode: 'has_any',
    },
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['company_profile'],
    },
    saveConfig: {
      isRequired: false,
      label: "Business Entity Type",
      placeholderMessage: undefined,
      requiredMessage: undefined,
    },
    showConfig: {
      isDetail: true,
      label: "Business Entity Type",
      unselectedOptions: true,
    },
  });

  // 4. Public or Private Company Status
  availableFilters.push({
    key: 'company_status',
    schemaType: "enum",
    scope: "public",
    categoryConfig: {
      limitToCategoryIds: false,
    },
    enumOptions: [
      { label: "Private", option: "Private" },
      { label: "Public", option: "Public" },
    ],
    filterConfig: {
      filterType: "SelectMultipleFilter",
      group: "primary",
      indexForSearch: true,
      label: "Public or Private Company Status",
      searchMode: 'has_any',
    },
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['company_profile'],
    },
    saveConfig: {
      isRequired: false,
      label: "Public or Private Company Status",
      placeholderMessage: undefined,
      requiredMessage: undefined,
    },
    showConfig: {
      isDetail: true,
      label: "Public or Private Company Status",
      unselectedOptions: true,
    },
  });

  // 5. Delivery Models
  availableFilters.push({
    key: 'delivery_models',
    schemaType: "enum",
    scope: "public",
    categoryConfig: {
      limitToCategoryIds: false,
    },
    enumOptions: [
      { label: "Onsite", option: "Onsite" },
      { label: "Offsite", option: "Offsite" },
      { label: "Offshore", option: "Offshore" },
      { label: "Hybrid", option: "Hybrid" },
    ],
    filterConfig: {
      filterType: "SelectMultipleFilter",
      group: "primary",
      indexForSearch: true,
      label: "Delivery Models",
      searchMode: 'has_any',
    },
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['company_profile'],
    },
    saveConfig: {
      isRequired: false,
      label: "Delivery Models",
      placeholderMessage: undefined,
      requiredMessage: undefined,
    },
    showConfig: {
      isDetail: true,
      label: "Delivery Models",
      unselectedOptions: true,
    },
  });

  // 6. Project Pricing Types
  availableFilters.push({
    key: 'pricing_types',
    schemaType: "enum",
    scope: "public",
    categoryConfig: {
      limitToCategoryIds: false,
    },
    enumOptions: [
      { label: "Fixed Fee", option: "fixedFee" },
      { label: "Time & Materials", option: "timeAndMaterials" },
      { label: "Milestone-Based", option: "milestoneBased" },
    ],
    filterConfig: {
      filterType: "SelectMultipleFilter",
      group: "primary",
      indexForSearch: true,
      label: "Project Pricing Types",
      searchMode: 'has_any',
    },
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['company_profile'],
    },
    saveConfig: {
      isRequired: false,
      label: "Project Pricing Types",
      placeholderMessage: undefined,
      requiredMessage: undefined,
    },
    showConfig: {
      isDetail: true,
      label: "Project Pricing Types",
      unselectedOptions: true,
    },
  });

  // 7. Certifications
  availableFilters.push({
    key: 'certifications',
    schemaType: "enum",
    scope: "public",
    categoryConfig: {
      limitToCategoryIds: false,
    },
    enumOptions: [
      { label: "ISO 9001", option: "ISO 9001" },
      { label: "CMMI L3+", option: "CMMI L3+" },
      { label: "SOC 2", option: "SOC 2" },
      { label: "ITIL", option: "ITIL" },
      { label: "HIPAA", option: "HIPAA" },
      { label: "FedRAMP", option: "FedRAMP" },
    ],
    filterConfig: {
      filterType: "SelectMultipleFilter",
      group: "primary",
      indexForSearch: true,
      label: "Certifications",
      searchMode: 'has_any',
    },
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['company_profile'],
    },
    saveConfig: {
      isRequired: false,
      label: "Certifications",
      placeholderMessage: undefined,
      requiredMessage: undefined,
    },
    showConfig: {
      isDetail: true,
      label: "Certifications",
      unselectedOptions: true,
    },
  });

  // 8. Technology domains - Now rendered as custom multi-select dropdown filter (see below in JSX)
  // (Removed from availableFilters to render as custom dropdown like Country of residence)

  // 9. Implementation Service Domains - Now rendered as custom multi-select dropdown filter (see below in JSX)
  // (Removed from availableFilters to render as custom dropdown like Country of residence)

  // 10. Geographies served
  availableFilters.push({
    key: 'geographies_served',
    schemaType: "enum",
    scope: "public",
    categoryConfig: {
      limitToCategoryIds: false,
    },
    enumOptions: [
      { option: 'Local', label: 'Local' },
      { option: 'Multi-State', label: 'Multi-State' },
      { option: 'National', label: 'National' },
      { option: 'Global', label: 'Global' },
    ],
    filterConfig: {
      filterType: "SelectMultipleFilter",
      group: "primary",
      indexForSearch: true,
      label: "Geographies served",
      searchMode: 'has_any',
    },
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['company_profile'],
    },
    saveConfig: {
      isRequired: false,
      label: "Geographies served",
      placeholderMessage: undefined,
      requiredMessage: undefined,
    },
    showConfig: {
      isDetail: true,
      label: "Geographies served",
      unselectedOptions: true,
    },
  });

  // 11. Industries served
  availableFilters.push({
    key: 'industries_served',
    schemaType: "enum",
    scope: "public",
    categoryConfig: {
      limitToCategoryIds: false,
    },
    enumOptions: [
      { option: 'Healthcare', label: 'Healthcare' },
      { option: 'Finance', label: 'Finance' },
      { option: 'Public Sector', label: 'Public Sector' },
      { option: 'Manufacturing', label: 'Manufacturing' },
      { option: 'Retail', label: 'Retail' },
      { option: 'Technology', label: 'Technology' },
    ],
    filterConfig: {
      filterType: "SelectMultipleFilter",
      group: "primary",
      indexForSearch: true,
      label: "Industries served",
      searchMode: 'has_any',
    },
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['company_profile'],
    },
    saveConfig: {
      isRequired: false,
      label: "Industries served",
      placeholderMessage: undefined,
      requiredMessage: undefined,
    },
    showConfig: {
      isDetail: true,
      label: "Industries served",
      unselectedOptions: true,
    },
  });

  // Move handleDateChange side effect before memoized computation
  useEffect(() => {
    if (expectedStartDate) {
      handleDateChange(expectedStartDate);
    }
  }, [expectedStartDate]);

  // Listings are now filtered server-side by the backend API (searchFilteredListings endpoint).
  // processedListings is a simple pass-through — filtering, sorting, and pagination
  // all happen in the backend before the response reaches the browser.
  const processedListings = useMemo(() => {
    return listings || [];
  }, [listings]);


  const customFilterKeys = [
    ...customPrimaryFilters.map(f => constructQueryParamName(f.key, f.scope)),
    ...customSecondaryFilters.map(f => constructQueryParamName(f.key, f.scope)),
  ];

  const customFilters = [
    expectedStartDate,
    salaryRange?.min, salaryRange?.max,
    billRateRange?.min, billRateRange?.max,
    submissionDeadlineRange?.min, submissionDeadlineRange?.max,
    postingDateRange?.min, postingDateRange?.max,
    availableToStartRange?.min, availableToStartRange?.max,
    workAuthExpiryRange?.min, workAuthExpiryRange?.max,
    currentEmployer,
    billingRateTargetRange?.min, billingRateTargetRange?.max,
    countryOfCitizenship,
    countryOfResidence,
    countryOfFormation,
    stateOfIncorpDomestic,
    registeredToOperateForeign,
    ...candidateSkillFilter,
    ...requiredSkillFilter,
    ...preferredSkillFilter,
    educationLevel,
    candidateCertifications,
    jobRole,
    ...(serviceDomains || []),
    ...(technologyDomains || []),
    ...(rfpServiceDomain || []),
    ...(rfpTechnologyDomain || [])
  ].filter(Boolean);

  const activeCustomFiltersCount = Object.keys(validQueryParams).filter(key =>
    customFilterKeys.includes(key)
  ).length + customFilters.length;

  const activeFilterIndicator = (count) => {
    if (count > 0) {
      return (
        <span style={{
          color: '#24a148', marginLeft: '5px'
        }}>• {count}</span>
      );
    }
    return null;
  };

  // After all availableFilters.push calls
  // Collect all enumOptions from availableFilters into allEnumsOptions
  const allEnumsOptions = availableFilters.reduce((acc, f) => {
    if (f.enumOptions) {
      acc[f.key] = f.enumOptions;
    }
    return acc;
  }, {});

  // ========== FILTER VISIBILITY BASED ON LISTING TYPE ==========

  // const selectedListingType = searchParams?.pub_listingType;
  availableFilters = availableFilters?.filter(filter => {
    const jobOnlyFilters = ['work_engagement_type', 'employment_type', 'experience_level', 'work_authorization', 'certifications_required', 'travel_requirement', 'required_ver_job'];
    const rfpOnlyFilters = ['service_domain', 'technology_domain', 'category_codes', 'organization_type', 'delivery_model', 'security_certifications', 'diversity_certifications', 'kyb_status_verification', 'beneficial_ownership_verification', 'data_protection_controls', 'past_performance_evidence'];
    const candidateOnlyFilters = ['open_to_work', 'work_type_preference_candidate', 'work_site_preference_candidate', 'willing_to_relocate', 'willing_to_back_check', 'authorized_to_work_us', 'work_auth_can', 'visa_type', 'self_emp', 'required_ver'];
    const companyOnlyFilters = ['employee_count', 'annual_revenue', 'business_entity_type', 'company_status', 'delivery_models', 'pricing_types', 'certifications', 'tech_domains', 'service_domains', 'geographies_served', 'industries_served', 'required_ver', 'required_ver_part'];

    if (filter?.key === 'work_site_preference') {
      return ['job_listing'].includes(selectedListingType);
    }
    if (jobOnlyFilters.includes(filter?.key)) {
      return selectedListingType === 'job_listing';
    }
    if (rfpOnlyFilters.includes(filter?.key)) {
      return selectedListingType === 'rfp_listing';
    }
    if (candidateOnlyFilters.includes(filter?.key)) {
      // Hide "Verifications completed" filter for non-logged-in users on talent search
      if (filter?.key === 'required_ver' && !currentUser) {
        return false;
      }
      if (filter?.key === 'work_auth_can' && !currentUser) {
        return false;
      }
      return selectedListingType === 'candidate';
    }
    if (companyOnlyFilters.includes(filter?.key)) {
      return selectedListingType === 'agency';
    }
    return true;
  });

  useEffect(() => {
    // All listing types now use server-side pagination via the backend API.
    // The backend always returns proper meta: { totalItems, totalPages, page, perPage }.
    if (pagination && pagination.totalItems != null) {
      const { totalItems, totalPages, page: apiPage, perPage: apiPerPage } = pagination;
      const calculatedPagination = totalItems > 0 && totalPages > 1
        ? { page: apiPage, perPage: apiPerPage, totalItems, totalPages }
        : null;
      setManualPagination(calculatedPagination);
    }
  }, [pagination?.totalItems, pagination?.totalPages, pagination?.page, pagination?.perPage]);

  return (

    <Page
      scrollingDisabled={scrollingDisabled}
      description={description}
      title={title}
      schema={schema}
    >
      <TopbarContainer rootClassName={isMobileModalOpen ? classNames(css.topbarBehindModal, css.topbar) : css.topbar} currentSearchParams={validQueryParams} />
      <div className={css.layoutWrapperContainer}>
        <aside className={css.layoutWrapperFilterColumn} data-testid="filterColumnAside">
          <div className={css.filterColumnContent}>
            {/* Tag bar for selected filters */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {Object.entries(tagFilters).filter(([key, value]) => value && value !== '').map(([key, value]) => {
                // Find filter config for label
                const filterConfig = availableFilters.find(f => f.key === key.replace(/^pub_/, '')) || availableFilters.find(f => f.key === key);
                if (filterConfig?.key === 'listingType') {
                  return null;
                }

                let label = filterConfig?.filterConfig?.label || filterConfig?.label || key;

                if (label === 'address')
                  return null;

                if (key === 'bounds') {
                  label = 'Location';
                  value = tagFilters.address || 'Current map area';
                }

                if (key === 'keyword') {
                  label = 'Keyword';
                  value = keyword;
                }

                if (key === 'sort') {
                  // label = value === 'createdAt' ? 'Newest' : 'Oldest';
                  value = value === 'createdAt' ? 'Newest' : 'Oldest';
                }

                if (key === 'expectedStartDate') {
                  label = 'Expected start date';
                  value = expectedStartDate?.date ? moment(expectedStartDate.date).format('MMM D, YYYY') : '';
                }

                if (key === 'salaryRange') {
                  label = 'Salary / Pay range';
                  value = `$${salaryRange?.min || '0'} - $${salaryRange?.max || 'Any'}`;
                }

                if (key === 'billRateRange') {
                  label = 'Bill rate target';
                  value = `$${billRateRange?.min || '0'} - $${billRateRange?.max || 'Any'}`;
                }

                if (key === 'submissionDeadlineRange') {
                  label = 'Submission deadline';
                  const min = submissionDeadlineRange?.min?.date ? moment(submissionDeadlineRange?.min.date).format('MMM D') : 'Any';
                  const max = submissionDeadlineRange?.max?.date ? moment(submissionDeadlineRange?.max.date).format('MMM D, YYYY') : 'Any';
                  value = `${min} - ${max}`;
                }

                if (key === 'postingDateRange') {
                  label = 'Posting date';
                  const min = postingDateRange?.min?.date ? moment(postingDateRange?.min.date).format('MMM D') : 'Any';
                  const max = postingDateRange?.max?.date ? moment(postingDateRange?.max.date).format('MMM D, YYYY') : 'Any';
                  value = `${min} - ${max}`;
                }

                if (key === 'availableToStartRange') {
                  label = 'Available to start';
                  const min = availableToStartRange?.min?.date ? moment(availableToStartRange?.min.date).format('MMM D') : 'Any';
                  const max = availableToStartRange?.max?.date ? moment(availableToStartRange?.max.date).format('MMM D, YYYY') : 'Any';
                  value = `${min} - ${max}`;
                }

                if (key === 'workAuthExpiryRange') {
                  label = 'Work auth expiry';
                  const min = workAuthExpiryRange?.min?.date ? moment(workAuthExpiryRange?.min.date).format('MMM D') : 'Any';
                  const max = workAuthExpiryRange?.max?.date ? moment(workAuthExpiryRange?.max.date).format('MMM D, YYYY') : 'Any';
                  value = `${min} - ${max}`;
                }

                if (key === 'currentEmployer') {
                  label = 'Current Employer';
                  value = currentEmployer;
                }

                if (key === 'technology') {
                  label = 'Technology';
                  value = technologyFilter;
                }

                if (key === 'billingRateTargetRange') {
                  label = 'Billing rate target';
                  value = `$${billingRateTargetRange?.min || '0'} - $${billingRateTargetRange?.max || 'Any'}`;
                }

                if (key === 'countryOfCitizenship') {
                  label = 'Country of citizenship';
                  value = countryOfCitizenship;
                }

                if (key === 'countryOfResidence') {
                  label = 'Country of residence';
                  value = countryOfResidence;
                }

                if (key === 'countryOfFormation') {
                  label = 'Country of Formation';
                  value = countryOfFormation;
                }

                if (key === 'stateOfIncorpDomestic') {
                  label = 'State of incorporation (Domestic)';
                  value = stateOfIncorpDomestic;
                }

                if (key === 'registeredToOperateForeign') {
                  label = 'Registered to operate as foreign entity';
                  value = registeredToOperateForeign;
                }

                if (key === 'candidateSkillFilter') {
                  label = 'Skills';
                  value = candidateSkillFilter.map(s => `${s.name} (${s.exp}+ yrs)`).join(', ');
                }

                if (key === 'requiredSkillFilter') {
                  label = 'Required Skills';
                  value = requiredSkillFilter.map(s => `${s.name} (${s.exp}+ yrs)`).join(', ');
                }

                if (key === 'preferredSkillFilter') {
                  label = 'Preferred Skills';
                  value = preferredSkillFilter.map(s => `${s.name} (${s.exp}+ yrs)`).join(', ');
                }

                if (key === 'educationLevel') {
                  label = 'Education Level';
                  value = educationLevel;
                }

                if (key === 'candidateCertifications') {
                  label = 'Certifications';
                  value = candidateCertifications;
                }

                if (key === 'jobRole') {
                  label = 'Job Role';
                  value = jobRole;
                }

                if (key === 'serviceDomains') {
                  label = 'Service Domains';
                  // Convert option keys to labels for display
                  value = serviceDomains
                    .map(key => {
                      const opt = serviceDomainOptions.find(o => o.option === key);
                      return opt ? opt.label : key;
                    })
                    .join(', ');
                }

                if (key === 'technologyDomains') {
                  label = 'Technology Domains';
                  // Convert option keys to labels for display
                  value = technologyDomains
                    .map(key => {
                      const opt = technologyOptions.find(o => o.option === key);
                      return opt ? opt.label : key;
                    })
                    .join(', ');
                }

                if (key === 'rfpServiceDomain') {
                  label = 'Service Domain';
                  // Convert option keys to labels for display
                  value = rfpServiceDomain
                    .map(key => {
                      const opt = serviceDomainOptions.find(o => o.option === key);
                      return opt ? opt.label : key;
                    })
                    .join(', ');
                }

                if (key === 'rfpTechnologyDomain') {
                  label = 'Technology Domain';
                  // Convert option keys to labels for display
                  value = rfpTechnologyDomain
                    .map(key => {
                      const opt = technologyOptions.find(o => o.option === key);
                      return opt ? opt.label : key;
                    })
                    .join(', ');
                }

                const handleRemoveCustomTag = (tagKey) => {
                  switch (tagKey) {
                    case 'keyword':
                      setKeyword('');
                      break;
                    case 'expectedStartDate':
                      setExpectedStartDate(null);
                      break;
                    case 'salaryRange':
                      setSalaryRange({ min: null, max: null });
                      break;
                    case 'billRateRange':
                      setBillRateRange({ min: null, max: null });
                      break;
                    case 'submissionDeadlineRange':
                      setSubmissionDeadlineRange({ min: null, max: null });
                      break;
                    case 'postingDateRange':
                      setPostingDateRange({ min: null, max: null });
                      break;
                    case 'availableToStartRange':
                      setAvailableToStartRange({ min: null, max: null });
                      break;
                    case 'workAuthExpiryRange':
                      setWorkAuthExpiryRange({ min: null, max: null });
                      break;
                    case 'currentEmployer':
                      setCurrentEmployer('');
                      break;
                    case 'billingRateTargetRange':
                      setBillingRateTargetRange({ min: null, max: null });
                      break;
                    case 'countryOfCitizenship':
                      setCountryOfCitizenship(null);
                      break;
                    case 'countryOfResidence':
                      setCountryOfResidence(null);
                      break;
                    case 'countryOfFormation':
                      setCountryOfFormation(null);
                      break;
                    case 'stateOfIncorpDomestic':
                      setStateOfIncorpDomestic(null);
                      break;
                    case 'registeredToOperateForeign':
                      setRegisteredToOperateForeign(null);
                      break;
                    case 'candidateSkillFilter':
                      setCandidateSkillFilter([]);
                      break;
                    case 'requiredSkillFilter':
                      setRequiredSkillFilter([]);
                      break;
                    case 'preferredSkillFilter':
                      setPreferredSkillFilter([]);
                      break;
                    case 'educationLevel':
                      setEducationLevel('');
                      break;
                    case 'candidateCertifications':
                      setCandidateCertifications('');
                      break;
                    case 'jobRole':
                      setJobRole('');
                      break;
                    case 'serviceDomains':
                      setServiceDomains([]);
                      break;
                    case 'technologyDomains':
                      setTechnologyDomains([]);
                      break;
                    case 'rfpServiceDomain':
                      setRfpServiceDomain([]);
                      break;
                    case 'rfpTechnologyDomain':
                      setRfpTechnologyDomain([]);
                      break;
                    case 'technology':
                      // Handle technology filter removal through URL parameter removal
                      // This will be handled by URL parameter removal in the main logic
                      break;
                    case 'bounds':
                      // Handle location filter removal through tag removal
                      setLocationSelected(false);
                      break;
                    case 'work_site_preference':
                    case 'pub_work_site_preference':
                      // Handle work site preference removal for job listings
                      // This will be handled by URL parameter removal in the main logic
                      break;
                    case 'work_site_preference_candidate':
                    case 'pub_work_site_preference_candidate':
                      // Handle work site preference removal for candidate listings
                      // This will be handled by URL parameter removal in the main logic
                      break;
                    default:
                      break;
                  }
                };

                // Use allEnumsOptions for value-to-label mapping
                let displayValue = value;
                if (filterConfig && allEnumsOptions[filterConfig.key]) {
                  const options = allEnumsOptions[filterConfig.key];
                  const values = typeof value === 'string' ? value.split(',') : [value];
                  displayValue = values
                    .map(v => {
                      const opt = options.find(o => o.option === v);
                      return opt ? opt.label : v;
                    })
                    .join(', ');
                }
                // Remove tag handler
                const handleRemoveTag = () => {
                  const { history, routeConfiguration, location } = props;
                  const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(routeConfiguration, location);

                  // Check if this is a custom filter first
                  const customFilterKeys = [
                    'keyword', 'expectedStartDate', 'salaryRange', 'billRateRange',
                    'submissionDeadlineRange', 'postingDateRange', 'availableToStartRange',
                    'workAuthExpiryRange', 'currentEmployer', 'billingRateTargetRange',
                    'countryOfCitizenship', 'countryOfResidence', 'countryOfFormation',
                    'stateOfIncorpDomestic', 'registeredToOperateForeign',
                    'candidateSkillFilter', 'requiredSkillFilter', 'preferredSkillFilter',
                    'educationLevel', 'candidateCertifications', 'jobRole',
                    'serviceDomains', 'technologyDomains',
                    'rfpServiceDomain', 'rfpTechnologyDomain'
                  ];

                  if (key === 'bounds') {
                    // Special handling for location tag - remove both address and bounds
                    // Get only URL params, not state-based custom filters
                    const urlParams = validUrlQueryParamsFromProps(props);
                    const newParams = { ...urlParams };
                    delete newParams.address;
                    delete newParams.bounds;
                    locationFormRef?.current?.change("addressDetail", null);
                    setLocationSelected(false);
                    setCurrentQueryParams(validUrlQueryParamsFromProps(props));
                    setCurrentQueryParams(newParams);
                    history.push(createResourceLocatorString(routeName, routeConfiguration, pathParams, newParams));
                  } else if (key === 'pub_work_site_preference' || key === 'pub_work_site_preference_candidate') {
                    // Special handling for work site preference - remove from URL directly
                    // const url = new URL(props.location.search, window.location.origin);
                    const url = new URL(location.search, window.location.origin);
                    url.searchParams.delete(key);
                    const searchWithoutWorkSite = url.search.substring(1); // Remove the leading '?'
                    setCurrentQueryParams(validUrlQueryParamsFromProps(props));
                    const newQueryParams = searchWithoutWorkSite ? parse(searchWithoutWorkSite) : {};
                    setCurrentQueryParams(newQueryParams);
                    history.push(createResourceLocatorString(routeName, routeConfiguration, pathParams, searchWithoutWorkSite ? parse(searchWithoutWorkSite) : {}));
                  } else if (customFilterKeys.includes(key)) {
                    // Handle custom filters through state management
                    handleRemoveCustomTag(key);
                    // For custom filters, the state change will trigger a re-render and filter update.
                    // We don't need to manipulate URL params for them here.
                  } else {
                    // Handle standard URL-based filters
                    // Get only URL params, not state-based custom filters
                    const urlParams = validUrlQueryParamsFromProps(props);
                    const newParams = { ...urlParams };
                    if (newParams.hasOwnProperty(key)) {
                      delete newParams[key];
                      setCurrentQueryParams(validUrlQueryParamsFromProps(props));
                      setCurrentQueryParams(newParams);
                      history.push(createResourceLocatorString(routeName, routeConfiguration, pathParams, newParams));
                    }
                  }
                };
                return (
                  <span key={key} style={{ background: '#e6f3ff', borderRadius: '4px', padding: '4px 12px', display: 'flex', alignItems: 'center', fontSize: '14px', width: "100%" }}>
                    <span style={{ flex: "1" }}>
                      <span style={{ marginRight: '8px', fontWeight: 500, color: "#333", display: "block" }}>{label}:</span>
                      <span style={{ marginRight: '8px' }}>{displayValue}</span>
                    </span>
                    <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#0078d4', fontWeight: 'bold', fontSize: '16px', lineHeight: 1 }} aria-label={`Remove ${label}`} onClick={handleRemoveTag}>&times;</button>
                  </span>
                );
              })}
            </div>

            {/* Keyword Search Field for RFP and Agency listings */}
            {(searchParams?.pub_listingType === 'rfp_listing' || searchParams?.pub_listingType === 'agency') && (
              <div className={css.filter}>
                <div className={css.filterHeader}>
                  <span className={css.label}>Keyword Search</span>
                </div>
                <div className={css.plain}>
                  <input
                    id="keywordSearch"
                    name="keywordSearch"
                    type="text"
                    placeholder={searchParams?.pub_listingType === 'rfp_listing' ? 'Search RFP details...' : 'Search supplier tags...'}
                    value={keyword}
                    onChange={e => setKeyword(e.target.value)}
                    className={css.location}
                  />
                  {keyword && (
                    <button className={css.clearButton} type="button" onClick={() => setKeyword('')}>
                      <FormattedMessage id={'FilterPlain.clear'} />
                    </button>
                  )}
                </div>
              </div>
            )}

            {availableFilters.map(filterConfig => {
              const key = `SearchFiltersDesktop.${filterConfig.scope || 'built-in'}.${filterConfig.key
                }`;
              return (
                <FilterComponent
                  key={key}
                  idPrefix="SearchFiltersDesktop"
                  className={css.filter}
                  config={filterConfig}
                  listingCategories={listingCategories}
                  marketplaceCurrency={marketplaceCurrency}
                  urlQueryParams={validQueryParams}
                  initialValues={initialValues(props, currentQueryParams)}
                  getHandleChangedValueFn={getHandleChangedValueFn}
                  intl={intl}
                  liveEdit
                  showAsPopup={false}
                  searchParams={searchParams}
                  isDesktop
                />
              );
            })}

            {searchParams?.pub_listingType === 'job_listing' ? (
              <>
                <div className={css.filter}>
                  <div className={css.filterHeader}>
                    <button className={css.labelButton} onClick={() => setShowExpectedStartDate(!showExpectedStartDate)}>
                      <span className={css.labelButtonContent}>
                        <span className={css.label}>
                          Expected start date
                          {activeFilterIndicator(expectedStartDate ? 1 : 0)}
                        </span>
                        <span className={css.openSign}>
                          <IconPlus isOpen={showExpectedStartDate} />
                        </span>
                      </span>
                    </button>
                  </div>
                  {showExpectedStartDate && (
                    <FinalForm
                      onSubmit={() => { }}
                      initialValues={{ expectedStartDateJob: expectedStartDate }}
                      render={({ handleSubmit, form }) => (
                        <form onSubmit={handleSubmit}>
                          <FormSpy
                            subscription={{ values: true }}
                            onChange={formState => {
                              if (formState.values.expectedStartDateJob !== expectedStartDate) {
                                handleDateChange(formState.values.expectedStartDateJob);
                              }
                            }}
                          />
                          <div className={css.plain}>
                            <FieldSingleDatePicker
                              id="expectedStartDateJob"
                              name="expectedStartDateJob"
                              placeholderText="Select a date"
                              onChange={date => {
                                setExpectedStartDate(date);
                              }}
                              isOutsideRange={() => false}
                              useMobileMargins
                            />
                            <button
                              className={css.clearButton}
                              type="button"
                              onClick={() => {
                                form.change('expectedStartDateJob', null);
                                setExpectedStartDate(null);
                              }}
                            >
                              <FormattedMessage id={'FilterPlain.clear'} />
                            </button>
                          </div>
                        </form>
                      )}
                    />
                  )}
                </div>

                <div className={css.filter}>
                  <div className={css.filterHeader}>
                    <button className={css.labelButton} onClick={() => setShowSalaryRange(!showSalaryRange)}>
                      <span className={css.labelButtonContent}>
                        <span className={css.label}>
                          Salary / Pay range (per month)
                          {activeFilterIndicator(salaryRange?.min || salaryRange?.max ? 1 : 0)}
                        </span>
                        <span className={css.openSign}>
                          <IconPlus isOpen={showSalaryRange} />
                        </span>
                      </span>
                    </button>
                  </div>
                  {showSalaryRange && (
                    <FinalForm
                      onSubmit={() => { }}
                      initialValues={{ salary: salaryRange }}
                      render={({ handleSubmit, form }) => (
                        <form onSubmit={handleSubmit}>
                          <FormSpy
                            subscription={{ values: true }}
                            onChange={formState => {
                              if (formState.values.salary !== salaryRange) {
                                handleSalaryChange(formState.values.salary);
                              }
                            }}
                          />
                          <div className={css.plain}>
                            <div className={css.inputsWrapper}>
                              <FieldTextInput
                                id="salary.min"
                                name="salary.min"
                                type="number"
                                placeholder="Min"
                                className={css.rangeInput}
                              />
                              <span className={css.separator}>&nbsp;</span>
                              <FieldTextInput
                                id="salary.max"
                                name="salary.max"
                                type="number"
                                placeholder="Max"
                                className={css.rangeInput}
                              />
                            </div>
                            <button
                              className={css.clearButton}
                              type="button"
                              onClick={() => {
                                form.change('salary', { min: null, max: null });
                                handleSalaryChange({ min: null, max: null });
                              }}
                            >
                              <FormattedMessage id={'FilterPlain.clear'} />
                            </button>
                          </div>
                        </form>
                      )}
                    />
                  )}
                </div>

                <div className={css.filter}>
                  <div className={css.filterHeader}>
                    <button className={css.labelButton} onClick={() => setShowBillRateRange(!showBillRateRange)}>
                      <span className={css.labelButtonContent}>
                        <span className={css.label}>
                          Bill rate target (per hour)
                          {activeFilterIndicator(billRateRange?.min || billRateRange?.max ? 1 : 0)}
                        </span>
                        <span className={css.openSign}>
                          <IconPlus isOpen={showBillRateRange} />
                        </span>
                      </span>
                    </button>
                  </div>
                  {showBillRateRange && (
                    <FinalForm
                      onSubmit={() => { }}
                      initialValues={{ billRate: billRateRange }}
                      render={({ handleSubmit, form }) => (
                        <form onSubmit={handleSubmit}>
                          <FormSpy
                            subscription={{ values: true }}
                            onChange={formState => {
                              if (formState.values.billRate !== billRateRange) {
                                handleBillRateChange(formState.values.billRate);
                              }
                            }}
                          />
                          <div className={css.plain}>
                            <div className={css.inputsWrapper}>
                              <FieldTextInput id="billRate.min" name="billRate.min" type="number" placeholder="Min" className={css.rangeInput} />
                              <span className={css.separator}>&nbsp;</span>
                              <FieldTextInput id="billRate.max" name="billRate.max" type="number" placeholder="Max" className={css.rangeInput} />
                            </div>
                            <button className={css.clearButton} type="button" onClick={() => { form.change('billRate', { min: null, max: null }); handleBillRateChange({ min: null, max: null }); }}>
                              <FormattedMessage id={'FilterPlain.clear'} />
                            </button>
                          </div>
                        </form>
                      )}
                    />
                  )}
                </div>

              </>
            ) : null}

            {searchParams?.pub_listingType === 'rfp_listing' ? (
              <>
                <div className={css.filter}>
                  <div className={css.filterHeader}>
                    <button className={css.labelButton} onClick={() => setShowSubmissionDeadlineRange(!showSubmissionDeadlineRange)}>
                      <span className={css.labelButtonContent}>
                        <span className={css.label}>
                          Submission deadline range
                          {activeFilterIndicator(submissionDeadlineRange?.min || submissionDeadlineRange?.max ? 1 : 0)}
                        </span>
                        <span className={css.openSign}>
                          <IconPlus isOpen={showSubmissionDeadlineRange} />
                        </span>
                      </span>
                    </button>
                  </div>
                  {showSubmissionDeadlineRange && (
                    <FinalForm
                      onSubmit={() => { }}
                      initialValues={{ submissionDeadline: submissionDeadlineRange }}
                      render={({ handleSubmit, form }) => (
                        <form onSubmit={handleSubmit}>
                          <FormSpy
                            subscription={{ values: true }}
                            onChange={formState => {
                              if (formState.values.submissionDeadline !== submissionDeadlineRange) {
                                handleSubmissionDeadlineChange(formState.values.submissionDeadline);
                              }
                            }}
                          />
                          <div className={css.plain}>
                            <div className={css.inputsWrapper}>
                              <FieldSingleDatePicker id="submissionDeadline.min" name="submissionDeadline.min" placeholderText="Min date" isOutsideRange={() => false} useMobileMargins />
                              <span className={css.separator}>&nbsp;</span>
                              <FieldSingleDatePicker id="submissionDeadline.max" name="submissionDeadline.max" placeholderText="Max date" isOutsideRange={() => false} useMobileMargins />
                            </div>
                            <button
                              className={css.clearButton}
                              type="button"
                              onClick={() => {
                                form.change('submissionDeadline', { min: null, max: null });
                                handleSubmissionDeadlineChange({ min: null, max: null });
                              }}
                            >
                              <FormattedMessage id={'FilterPlain.clear'} />
                            </button>
                          </div>
                        </form>
                      )}
                    />
                  )}
                </div>

                <div className={css.filter}>
                  <div className={css.filterHeader}>
                    <button className={css.labelButton} onClick={() => setShowPostingDateRange(!showPostingDateRange)}>
                      <span className={css.labelButtonContent}>
                        <span className={css.label}>
                          Posting date range
                          {activeFilterIndicator(postingDateRange?.min || postingDateRange?.max ? 1 : 0)}
                        </span>
                        <span className={css.openSign}>
                          <IconPlus isOpen={showPostingDateRange} />
                        </span>
                      </span>
                    </button>
                  </div>
                  {showPostingDateRange && (
                    <FinalForm
                      onSubmit={() => { }}
                      initialValues={{ postingDate: postingDateRange }}
                      render={({ handleSubmit, form }) => (
                        <form onSubmit={handleSubmit}>
                          <FormSpy
                            subscription={{ values: true }}
                            onChange={formState => {
                              if (formState.values.postingDate !== postingDateRange) {
                                handlePostingDateChange(formState.values.postingDate);
                              }
                            }}
                          />
                          <div className={css.plain}>
                            <div className={css.inputsWrapper}>
                              <FieldSingleDatePicker
                                id="postingDate.min"
                                name="postingDate.min"
                                placeholderText="Min date"
                                useMobileMargins
                                isOutsideRange={() => false}
                              />
                              <span className={css.separator}>&nbsp;</span>
                              <FieldSingleDatePicker
                                id="postingDate.max"
                                name="postingDate.max"
                                placeholderText="Max date"
                                useMobileMargins
                                isOutsideRange={() => false}
                              />
                            </div>
                            <button
                              className={css.clearButton}
                              type="button"
                              onClick={() => {
                                form.change('postingDate', { min: null, max: null });
                                handlePostingDateChange({ min: null, max: null });
                              }}
                            >
                              <FormattedMessage id={'FilterPlain.clear'} />
                            </button>
                          </div>
                        </form>
                      )}
                    />
                  )}
                </div>

                {/* RFP Service Domain - Multiple select dropdown filter */}
                <div className={css.filter}>
                  <div className={css.filterHeader}>
                    <button className={css.labelButton} onClick={() => setShowRfpServiceDomain(!showRfpServiceDomain)}>
                      <span className={css.labelButtonContent}>
                        <span className={css.label}>
                          Service Domain
                          {activeFilterIndicator(rfpServiceDomain?.length || 0)}
                        </span>
                        <span className={css.openSign}>
                          <IconPlus isOpen={showRfpServiceDomain} />
                        </span>
                      </span>
                    </button>
                  </div>
                  {showRfpServiceDomain && (
                    <div className={css.plain}>
                      <Select
                        mode="multiple"
                        allowClear
                        style={{ width: '100%' }}
                        placeholder="Select service domains"
                        value={rfpServiceDomain}
                        onChange={handleRfpServiceDomainChange}
                        optionFilterProp="label"
                        showSearch
                      >
                        {serviceDomainOptions.map(opt => (
                          <Select.Option key={opt.option} value={opt.option} label={opt.label}>
                            {opt.label}
                          </Select.Option>
                        ))}
                      </Select>
                      <button className={css.clearButton} type="button" onClick={() => handleRfpServiceDomainChange([])}>
                        <FormattedMessage id={'FilterPlain.clear'} />
                      </button>
                    </div>
                  )}
                </div>

                {/* RFP Technology Domain - Multiple select dropdown filter */}
                <div className={css.filter}>
                  <div className={css.filterHeader}>
                    <button className={css.labelButton} onClick={() => setShowRfpTechnologyDomain(!showRfpTechnologyDomain)}>
                      <span className={css.labelButtonContent}>
                        <span className={css.label}>
                          Technology Domain
                          {activeFilterIndicator(rfpTechnologyDomain?.length || 0)}
                        </span>
                        <span className={css.openSign}>
                          <IconPlus isOpen={showRfpTechnologyDomain} />
                        </span>
                      </span>
                    </button>
                  </div>
                  {showRfpTechnologyDomain && (
                    <div className={css.plain}>
                      <Select
                        mode="multiple"
                        allowClear
                        style={{ width: '100%' }}
                        placeholder="Select technology domains"
                        value={rfpTechnologyDomain}
                        onChange={handleRfpTechnologyDomainChange}
                        optionFilterProp="label"
                        showSearch
                      >
                        {technologyOptions.map(opt => (
                          <Select.Option key={opt.option} value={opt.option} label={opt.label}>
                            {opt.label}
                          </Select.Option>
                        ))}
                      </Select>
                      <button className={css.clearButton} type="button" onClick={() => handleRfpTechnologyDomainChange([])}>
                        <FormattedMessage id={'FilterPlain.clear'} />
                      </button>
                    </div>
                  )}
                </div>

              </>
            ) : null}

            {searchParams?.pub_listingType === 'candidate' ? (
              <>
                {/* Certifications Filter - shown at top for candidate search */}
                <div className={css.filter}>
                  <div className={css.filterHeader}>
                    <button className={css.labelButton} onClick={() => setShowCandidateCertifications(!showCandidateCertifications)}>
                      <span className={css.labelButtonContent}>
                        <span className={css.label}>
                          Certifications
                          {activeFilterIndicator(candidateCertifications ? 1 : 0)}
                        </span>
                        <span className={css.openSign}>
                          <IconPlus isOpen={showCandidateCertifications} />
                        </span>
                      </span>
                    </button>
                  </div>
                  {showCandidateCertifications && (
                    <FinalForm
                      onSubmit={() => { }}
                      initialValues={{ candidateCertifications: candidateCertifications }}
                      render={({ handleSubmit, form }) => (
                        <form onSubmit={handleSubmit}>
                          <FormSpy
                            subscription={{ values: true }}
                            onChange={formState => {
                              if (formState.values.candidateCertifications !== candidateCertifications) {
                                handleCandidateCertificationsChange(formState.values.candidateCertifications);
                              }
                            }}
                          />
                          <div className={css.plain}>
                            <FieldTextInput
                              id="candidateCertifications"
                              name="candidateCertifications"
                              type="text"
                              placeholder="e.g. PMP, AWS Certified"
                            />
                            <button className={css.clearButton} type="button" onClick={() => { form.change('candidateCertifications', ''); handleCandidateCertificationsChange(''); }}>
                              <FormattedMessage id={'FilterPlain.clear'} />
                            </button>
                          </div>
                        </form>
                      )}
                    />
                  )}
                </div>

                <div className={css.filter}>
                  <div className={css.filterHeader}>
                    <button className={css.labelButton} onClick={() => setShowAvailableToStartRange(!showAvailableToStartRange)}>
                      <span className={css.labelButtonContent}>
                        <span className={css.label}>
                          Date available to start
                          {activeFilterIndicator(availableToStartRange?.min || availableToStartRange?.max ? 1 : 0)}
                        </span>
                        <span className={css.openSign}>
                          <IconPlus isOpen={showAvailableToStartRange} />
                        </span>
                      </span>
                    </button>
                  </div>
                  {showAvailableToStartRange && (
                    <FinalForm
                      onSubmit={() => { }}
                      initialValues={{ availableToStart: availableToStartRange }}
                      render={({ handleSubmit, form }) => (
                        <form onSubmit={handleSubmit}>
                          <FormSpy
                            subscription={{ values: true }}
                            onChange={formState => {
                              if (formState.values.availableToStart !== availableToStartRange) {
                                handleAvailableToStartChange(formState.values.availableToStart);
                              }
                            }}
                          />
                          <div className={css.plain}>
                            <div className={css.inputsWrapper}>
                              <FieldSingleDatePicker id="availableToStart.min" name="availableToStart.min" placeholderText="Min date" isOutsideRange={() => false} useMobileMargins />
                              <span className={css.separator}>&nbsp;</span>
                              <FieldSingleDatePicker id="availableToStart.max" name="availableToStart.max" placeholderText="Max date" isOutsideRange={() => false} useMobileMargins />
                            </div>
                            <button className={css.clearButton} type="button" onClick={() => { form.change('availableToStart', { min: null, max: null }); handleAvailableToStartChange({ min: null, max: null }); }}>
                              <FormattedMessage id={'FilterPlain.clear'} />
                            </button>
                          </div>
                        </form>
                      )}
                    />
                  )}
                </div>

              </>
            ) : null}

            {searchParams?.pub_listingType === 'candidate' ? (
              <>
                <div className={css.filter}>
                  <div className={css.filterHeader}>
                    <button className={css.labelButton} onClick={() => setShowCurrentEmployer(!showCurrentEmployer)}>
                      <span className={css.labelButtonContent}>
                        <span className={css.label}>
                          Current Employer
                          {activeFilterIndicator(currentEmployer ? 1 : 0)}
                        </span>
                        <span className={css.openSign}>
                          <IconPlus isOpen={showCurrentEmployer} />
                        </span>
                      </span>
                    </button>
                  </div>
                  {showCurrentEmployer && (
                    <FinalForm
                      onSubmit={() => { }}
                      initialValues={{ currentEmployer: currentEmployer }}
                      render={({ handleSubmit, form }) => (
                        <form onSubmit={handleSubmit}>
                          <FormSpy
                            subscription={{ values: true }}
                            onChange={formState => {
                              if (formState.values.currentEmployer !== currentEmployer) {
                                handleCurrentEmployerChange(formState.values.currentEmployer);
                              }
                            }}
                          />
                          <div className={css.plain}>
                            <FieldTextInput
                              id="currentEmployer"
                              name="currentEmployer"
                              type="text"
                              placeholder="Employer name"
                            // className={css.location}
                            />
                            <button className={css.clearButton} type="button" onClick={() => { form.change('currentEmployer', ''); handleCurrentEmployerChange(''); }}>
                              <FormattedMessage id={'FilterPlain.clear'} />
                            </button>
                          </div>
                        </form>
                      )}
                    />
                  )}
                </div>

                {/* Education Level Filter */}
                <div className={css.filter}>
                  <div className={css.filterHeader}>
                    <button className={css.labelButton} onClick={() => setShowEducationLevel(!showEducationLevel)}>
                      <span className={css.labelButtonContent}>
                        <span className={css.label}>
                          Education Level
                          {activeFilterIndicator(educationLevel ? 1 : 0)}
                        </span>
                        <span className={css.openSign}>
                          <IconPlus isOpen={showEducationLevel} />
                        </span>
                      </span>
                    </button>
                  </div>
                  {showEducationLevel && (
                    <FinalForm
                      onSubmit={() => { }}
                      initialValues={{ educationLevel: educationLevel }}
                      render={({ handleSubmit, form }) => (
                        <form onSubmit={handleSubmit}>
                          <FormSpy
                            subscription={{ values: true }}
                            onChange={formState => {
                              if (formState.values.educationLevel !== educationLevel) {
                                handleEducationLevelChange(formState.values.educationLevel);
                              }
                            }}
                          />
                          <div className={css.plain}>
                            <FieldTextInput
                              id="educationLevel"
                              name="educationLevel"
                              type="text"
                              placeholder="e.g. Bachelor's, Master's"
                            />
                            <button className={css.clearButton} type="button" onClick={() => { form.change('educationLevel', ''); handleEducationLevelChange(''); }}>
                              <FormattedMessage id={'FilterPlain.clear'} />
                            </button>
                          </div>
                        </form>
                      )}
                    />
                  )}
                </div>

                {/* Job Role Filter */}
                <div className={css.filter}>
                  <div className={css.filterHeader}>
                    <button className={css.labelButton} onClick={() => setShowJobRole(!showJobRole)}>
                      <span className={css.labelButtonContent}>
                        <span className={css.label}>
                          Job Role
                          {activeFilterIndicator(jobRole ? 1 : 0)}
                        </span>
                        <span className={css.openSign}>
                          <IconPlus isOpen={showJobRole} />
                        </span>
                      </span>
                    </button>
                  </div>
                  {showJobRole && (
                    <FinalForm
                      onSubmit={() => { }}
                      initialValues={{ jobRole: jobRole }}
                      render={({ handleSubmit, form }) => (
                        <form onSubmit={handleSubmit}>
                          <FormSpy
                            subscription={{ values: true }}
                            onChange={formState => {
                              if (formState.values.jobRole !== jobRole) {
                                handleJobRoleChange(formState.values.jobRole);
                              }
                            }}
                          />
                          <div className={css.plain}>
                            <FieldTextInput
                              id="jobRole"
                              name="jobRole"
                              type="text"
                              placeholder="e.g. Software Engineer, Manager"
                            />
                            <button className={css.clearButton} type="button" onClick={() => { form.change('jobRole', ''); handleJobRoleChange(''); }}>
                              <FormattedMessage id={'FilterPlain.clear'} />
                            </button>
                          </div>
                        </form>
                      )}
                    />
                  )}
                </div>

              </>
            ) : null}

            {/* {(searchParams?.pub_listingType === 'candidate' && !!currentUser) ? (
                  <>
                    <div className={css.filter}>
                      <div className={css.filterHeader}>
                        <button className={css.labelButton} onClick={() => setShowWorkAuthExpiryRange(!showWorkAuthExpiryRange)}>
                          <span className={css.labelButtonContent}>
                            <span className={css.label}>
                              Work auth expiry date
                              {activeFilterIndicator(workAuthExpiryRange?.min || workAuthExpiryRange?.max ? 1 : 0)}
                            </span>
                            <span className={css.openSign}>
                              <IconPlus isOpen={showWorkAuthExpiryRange} />
                            </span>
                          </span>
                        </button>
                      </div>
                      {showWorkAuthExpiryRange && (
                        <FinalForm
                          onSubmit={() => {}}
                          initialValues={{ workAuthExpiry: workAuthExpiryRange }}
                          render={({ handleSubmit, form }) => (
                            <form onSubmit={handleSubmit}>
                              <FormSpy
                                subscription={{ values: true }}
                                onChange={formState => {
                                  if (formState.values.workAuthExpiry !== workAuthExpiryRange) {
                                    handleWorkAuthExpiryChange(formState.values.workAuthExpiry);
                                  }
                                }}
                              />
                              <div className={css.plain}>
                                <div className={css.inputsWrapper}>
                                  <FieldSingleDatePicker id="workAuthExpiry.min" name="workAuthExpiry.min" placeholderText="Min date" isOutsideRange={() => false} useMobileMargins />
                                  <span className={css.separator}>&nbsp;</span>
                                  <FieldSingleDatePicker id="workAuthExpiry.max" name="workAuthExpiry.max" placeholderText="Max date" isOutsideRange={() => false} useMobileMargins />
                                </div>
                                <button className={css.clearButton} type="button" onClick={() => { form.change('workAuthExpiry', { min: null, max: null }); handleWorkAuthExpiryChange({ min: null, max: null }); }}>
                                  <FormattedMessage id={'FilterPlain.clear'} />
                                </button>
                              </div>
                            </form>
                          )}
                        />
                      )}
                    </div>
                    
                  </>
                ) : null} */}

            {searchParams?.pub_listingType === 'candidate' ? (
              <>
                <div className={css.filter}>
                  <div className={css.filterHeader}>
                    <button className={css.labelButton} onClick={() => setShowBillingRateTargetRange(!showBillingRateTargetRange)}>
                      <span className={css.labelButtonContent}>
                        <span className={css.label}>Billing rate target (per hour)</span>
                        {activeFilterIndicator(billingRateTargetRange?.min || billingRateTargetRange?.max ? 1 : 0)}
                        <span className={css.openSign}>
                          <IconPlus isOpen={showBillingRateTargetRange} />
                        </span>
                      </span>
                    </button>
                  </div>
                  {showBillingRateTargetRange && (
                    <FinalForm
                      onSubmit={() => { }}
                      initialValues={{ billingRateTarget: billingRateTargetRange }}
                      render={({ handleSubmit, form }) => (
                        <form onSubmit={handleSubmit}>
                          <FormSpy
                            subscription={{ values: true }}
                            onChange={formState => {
                              if (formState.values.billingRateTarget !== billingRateTargetRange) {
                                handleBillingRateTargetChange(formState.values.billingRateTarget);
                              }
                            }}
                          />
                          <div className={css.plain}>
                            <div className={css.inputsWrapper}>
                              <FieldTextInput id="billingRateTarget.min" name="billingRateTarget.min" type="number" placeholder="Min" className={css.rangeInput} />
                              <span className={css.separator}>&nbsp;</span>
                              <FieldTextInput id="billingRateTarget.max" name="billingRateTarget.max" type="number" placeholder="Max" className={css.rangeInput} />
                            </div>
                            <button className={css.clearButton} type="button" onClick={() => { form.change('billingRateTarget', { min: null, max: null }); handleBillingRateTargetChange({ min: null, max: null }); }}>
                              <FormattedMessage id={'FilterPlain.clear'} />
                            </button>
                          </div>
                        </form>
                      )}
                    />
                  )}
                </div>

              </>
            ) : null}

            {/* {(searchParams?.pub_listingType === 'candidate' && !!currentUser) ? (
                  <>
                    <div className={css.filter}>
                      <div className={css.filterHeader}>
                        <button className={css.labelButton} onClick={() => setShowCountryOfCitizenship(!showCountryOfCitizenship)}>
                          <span className={css.labelButtonContent}>
                            <span className={css.label}>
                              Country of citizenship
                              {activeFilterIndicator(countryOfCitizenship ? 1 : 0)}
                            </span>
                            <span className={css.openSign}>
                              <IconPlus isOpen={showCountryOfCitizenship} />
                            </span>
                          </span>
                        </button>
                      </div>
                      {showCountryOfCitizenship && (
                        <FinalForm
                          onSubmit={() => {}}
                          initialValues={{ countryOfCitizenship: countryOfCitizenship }}
                          render={({ handleSubmit, form }) => (
                            <form onSubmit={handleSubmit}>
                              <FormSpy
                                subscription={{ values: true }}
                                onChange={formState => {
                                  if (formState.values.countryOfCitizenship !== countryOfCitizenship) {
                                    handleCountryOfCitizenshipChange(formState.values.countryOfCitizenship);
                                  }
                                }}
                              />
                              <div className={css.plain}>
                                <FieldSelect id="countryOfCitizenship" name="countryOfCitizenship">
                                  <option value="">All countries</option>
                                  {Country.getAllCountries().map(country => (
                                    <option key={country.isoCode} value={country.name}>
                                      {country.name}
                                    </option>
                                  ))}
                                </FieldSelect>
                                <button className={css.clearButton} type="button" onClick={() => { form.change('countryOfCitizenship', null); handleCountryOfCitizenshipChange(null); }}>
                                  <FormattedMessage id={'FilterPlain.clear'} />
                                </button>
                              </div>
                            </form>
                          )}
                        />
                      )}
                    </div>
                    
                  </>
                ) : null} */}

            {/* {(searchParams?.pub_listingType === 'candidate' && !!currentUser) ? (
                  <>
                    <div className={css.filter}>
                      <div className={css.filterHeader}>
                        <button className={css.labelButton} onClick={() => setShowCountryOfResidence(!showCountryOfResidence)}>
                          <span className={css.labelButtonContent}>
                            <span className={css.label}>
                              Country of residence
                              {activeFilterIndicator(countryOfResidence ? 1 : 0)}
                            </span>
                            <span className={css.openSign}>
                              <IconPlus isOpen={showCountryOfResidence} />
                            </span>
                          </span>
                        </button>
                      </div>
                      {showCountryOfResidence && (
                        <FinalForm
                          onSubmit={() => {}}
                          initialValues={{ countryOfResidence: countryOfResidence }}
                          render={({ handleSubmit, form }) => (
                            <form onSubmit={handleSubmit}>
                              <FormSpy
                                subscription={{ values: true }}
                                onChange={formState => {
                                  if (formState.values.countryOfResidence !== countryOfResidence) {
                                    handleCountryOfResidenceChange(formState.values.countryOfResidence);
                                  }
                                }}
                              />
                              <div className={css.plain}>
                                <FieldSelect id="countryOfResidence" name="countryOfResidence">
                                  <option value="">All countries</option>
                                  {Country.getAllCountries().map(country => (
                                    <option key={country.isoCode} value={country.name}>
                                      {country.name}
                                    </option>
                                  ))}
                                </FieldSelect>
                                <button className={css.clearButton} type="button" onClick={() => { form.change('countryOfResidence', null); handleCountryOfResidenceChange(null); }}>
                                  <FormattedMessage id={'FilterPlain.clear'} />
                                </button>
                              </div>
                            </form>
                          )}
                        />
                      )}
                    </div>
                    
                  </>
                ) : null} */}

            {searchParams?.pub_listingType === 'agency' ? (
              <>
                <div className={css.filter}>
                  <div className={css.filterHeader}>
                    <button className={css.labelButton} onClick={() => setShowCountryOfFormation(!showCountryOfFormation)}>
                      <span className={css.labelButtonContent}>
                        <span className={css.label}>
                          Country of Formation
                          {activeFilterIndicator(countryOfFormation ? 1 : 0)}
                        </span>
                        <span className={css.openSign}>
                          <IconPlus isOpen={showCountryOfFormation} />
                        </span>
                      </span>
                    </button>
                  </div>
                  {showCountryOfFormation && (
                    <FinalForm
                      onSubmit={() => { }}
                      initialValues={{ countryOfFormation: countryOfFormation }}
                      render={({ handleSubmit, form }) => (
                        <form onSubmit={handleSubmit}>
                          <FormSpy
                            subscription={{ values: true }}
                            onChange={formState => {
                              if (formState.values.countryOfFormation !== countryOfFormation) {
                                handleCountryOfFormationChange(formState.values.countryOfFormation);
                              }
                            }}
                          />
                          <div className={css.plain}>
                            <FieldSelect id="countryOfFormation" name="countryOfFormation">
                              <option value="">All countries</option>
                              {Country.getAllCountries().map(country => (
                                <option key={country.isoCode} value={country.name}>
                                  {country.name}
                                </option>
                              ))}
                            </FieldSelect>
                            <button className={css.clearButton} type="button" onClick={() => { form.change('countryOfFormation', null); handleCountryOfFormationChange(null); }}>
                              <FormattedMessage id={'FilterPlain.clear'} />
                            </button>
                          </div>
                        </form>
                      )}
                    />
                  )}
                </div>

                <div className={css.filter}>
                  <div className={css.filterHeader}>
                    <button className={css.labelButton} onClick={() => setShowStateOfIncorporation(!showStateOfIncorporation)}>
                      <span className={css.labelButtonContent}>
                        <span className={css.label}>
                          State of incorporation (Domestic)
                          {activeFilterIndicator(stateOfIncorpDomestic ? 1 : 0)}
                        </span>
                        <span className={css.openSign}>
                          <IconPlus isOpen={showStateOfIncorporation} />
                        </span>
                      </span>
                    </button>
                  </div>
                  {showStateOfIncorporation && (
                    <FinalForm
                      onSubmit={() => { }}
                      initialValues={{ stateOfIncorpDomestic: stateOfIncorpDomestic }}
                      render={({ handleSubmit, form }) => (
                        <form onSubmit={handleSubmit}>
                          <FormSpy
                            subscription={{ values: true }}
                            onChange={formState => {
                              if (formState.values.stateOfIncorpDomestic !== stateOfIncorpDomestic) {
                                handleStateOfIncorpDomesticChange(formState.values.stateOfIncorpDomestic);
                              }
                            }}
                          />
                          <div className={css.plain}>
                            <FieldSelect id="stateOfIncorpDomestic" name="stateOfIncorpDomestic">
                              <option value="">All states</option>
                              {State.getStatesOfCountry('US').map(state => (
                                <option key={state.isoCode} value={state.name}>
                                  {state.name}
                                </option>
                              ))}
                            </FieldSelect>
                            <button className={css.clearButton} type="button" onClick={() => { form.change('stateOfIncorpDomestic', null); handleStateOfIncorpDomesticChange(null); }}>
                              <FormattedMessage id={'FilterPlain.clear'} />
                            </button>
                          </div>
                        </form>
                      )}
                    />
                  )}
                </div>

                <div className={css.filter}>
                  <div className={css.filterHeader}>
                    <button className={css.labelButton} onClick={() => setShowRegisteredToOperateForeign(!showRegisteredToOperateForeign)}>
                      <span className={css.labelButtonContent}>
                        <span className={css.label}>
                          Registered to operate as foreign entity
                          {activeFilterIndicator(registeredToOperateForeign ? 1 : 0)}
                        </span>
                        <span className={css.openSign}>
                          <IconPlus isOpen={showRegisteredToOperateForeign} />
                        </span>
                      </span>
                    </button>
                  </div>
                  {showRegisteredToOperateForeign && (
                    <FinalForm
                      onSubmit={() => { }}
                      initialValues={{ registeredToOperateForeign: registeredToOperateForeign }}
                      render={({ handleSubmit, form }) => (
                        <form onSubmit={handleSubmit}>
                          <FormSpy
                            subscription={{ values: true }}
                            onChange={formState => {
                              if (formState.values.registeredToOperateForeign !== registeredToOperateForeign) {
                                handleRegisteredToOperateForeignChange(formState.values.registeredToOperateForeign);
                              }
                            }}
                          />
                          <div className={css.plain}>
                            <FieldSelect id="registeredToOperateForeign" name="registeredToOperateForeign">
                              <option value="">All states</option>
                              {State.getStatesOfCountry('US').map(state => (
                                <option key={state.isoCode} value={state.name}>
                                  {state.name}
                                </option>
                              ))}
                            </FieldSelect>
                            <button className={css.clearButton} type="button" onClick={() => { form.change('registeredToOperateForeign', null); handleRegisteredToOperateForeignChange(null); }}>
                              <FormattedMessage id={'FilterPlain.clear'} />
                            </button>
                          </div>
                        </form>
                      )}
                    />
                  )}
                </div>

                {/* Service Domains - Multiple select dropdown filter */}
                <div className={css.filter}>
                  <div className={css.filterHeader}>
                    <button className={css.labelButton} onClick={() => setShowServiceDomains(!showServiceDomains)}>
                      <span className={css.labelButtonContent}>
                        <span className={css.label}>
                          Service Domains
                          {activeFilterIndicator(serviceDomains?.length || 0)}
                        </span>
                        <span className={css.openSign}>
                          <IconPlus isOpen={showServiceDomains} />
                        </span>
                      </span>
                    </button>
                  </div>
                  {showServiceDomains && (
                    <div className={css.plain}>
                      <Select
                        mode="multiple"
                        allowClear
                        style={{ width: '100%' }}
                        placeholder="Select service domains"
                        value={serviceDomains}
                        onChange={handleServiceDomainsChange}
                        optionFilterProp="label"
                        showSearch
                      >
                        {serviceDomainOptions.map(opt => (
                          <Select.Option key={opt.option} value={opt.option} label={opt.label}>
                            {opt.label}
                          </Select.Option>
                        ))}
                      </Select>
                      <button className={css.clearButton} type="button" onClick={() => handleServiceDomainsChange([])}>
                        <FormattedMessage id={'FilterPlain.clear'} />
                      </button>
                    </div>
                  )}
                </div>

                {/* Technology Domains - Multiple select dropdown filter */}
                <div className={css.filter}>
                  <div className={css.filterHeader}>
                    <button className={css.labelButton} onClick={() => setShowTechnologyDomains(!showTechnologyDomains)}>
                      <span className={css.labelButtonContent}>
                        <span className={css.label}>
                          Technology Domains
                          {activeFilterIndicator(technologyDomains?.length || 0)}
                        </span>
                        <span className={css.openSign}>
                          <IconPlus isOpen={showTechnologyDomains} />
                        </span>
                      </span>
                    </button>
                  </div>
                  {showTechnologyDomains && (
                    <div className={css.plain}>
                      <Select
                        mode="multiple"
                        allowClear
                        style={{ width: '100%' }}
                        placeholder="Select technology domains"
                        value={technologyDomains}
                        onChange={handleTechnologyDomainsChange}
                        optionFilterProp="label"
                        showSearch
                      >
                        {technologyOptions.map(opt => (
                          <Select.Option key={opt.option} value={opt.option} label={opt.label}>
                            {opt.label}
                          </Select.Option>
                        ))}
                      </Select>
                      <button className={css.clearButton} type="button" onClick={() => handleTechnologyDomainsChange([])}>
                        <FormattedMessage id={'FilterPlain.clear'} />
                      </button>
                    </div>
                  )}
                </div>

              </>
            ) : null}

            {(!searchParams?.pub_listingType) || (searchParams?.pub_listingType === 'candidate') && (
              <FinalForm
                initialValues={{ addressDetail: '' }}
                onSubmit={values => {
                  // This onSubmit will be triggered when the form is submitted.
                  // You can add logic here to apply all filters at once.
                }}
                style={{ zIndex: '999' }}
                render={({ handleSubmit, form }) => {

                  // --- START: NEW STATE & HANDLERS FOR SKILL FILTER ---

                  // State to hold the list of skills to filter by
                  const [skillsToFilter, setSkillsToFilter] = useState([]);

                  // State for the current skill name input
                  const [candidateSkillName, setCandidateSkillName] = useState('');

                  // State for the current skill experience input
                  const [candidateSkillExp, setCandidateSkillExp] = useState(''); // Use string to handle empty input

                  useEffect(() => {
                    if (candidateSkillFilter.length === 0) {
                      setSkillsToFilter([]);
                    }
                  }, [candidateSkillFilter]);

                  // Function to add a skill to the filter list
                  const handleAddSkill = () => {
                    if (candidateSkillName && candidateSkillExp > 0 && skillsToFilter.length < 3) {
                      const newSkill = { name: candidateSkillName, exp: candidateSkillExp };

                      setSkillsToFilter([...skillsToFilter, newSkill]);

                      if (candidateSkillFilter.length > 0) {
                        setCandidateSkillFilter([...candidateSkillFilter, newSkill]);
                      } else {
                        setCandidateSkillFilter([newSkill]);
                      }

                      // Clear inputs
                      setCandidateSkillName('');
                      setCandidateSkillExp('');
                    }
                  };

                  // Function to remove a skill from both lists by its index
                  const handleRemoveSkill = (indexToRemove) => {
                    setSkillsToFilter(skillsToFilter.filter((_, index) => index !== indexToRemove));
                    setCandidateSkillFilter(candidateSkillFilter.filter((_, index) => index !== indexToRemove));
                  };

                  // --- END: NEW STATE & HANDLERS FOR SKILL FILTER ---

                  return (
                    <form onSubmit={handleSubmit} className={css.locationSearchForm}>
                      {/* The existing FormSpy for location */}
                      <FormSpy
                        onChange={async (formState) => {
                          // This logic is for the location filter
                          if (formState?.values?.addressDetail?.selectedPlace) {
                            const address = formState?.values?.addressDetail.selectedPlace.address;
                            const bounds = formState?.values?.addressDetail.selectedPlace.bounds;

                            if (address === '') {
                              // Get only URL params, not state-based custom filters
                              const urlFilters = validUrlQueryParamsFromProps(props);
                              const filtersWithoutLocation = { ...urlFilters };
                              delete filtersWithoutLocation.address;
                              delete filtersWithoutLocation.bounds;
                              history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, filtersWithoutLocation));
                              return;
                            }

                            const boundsParam = bounds
                              ? `${bounds.ne.lat},${bounds.ne.lng},${bounds.sw.lat},${bounds.sw.lng}`
                              : null;

                            // Get only URL params, not state-based custom filters
                            const urlFilters = validUrlQueryParamsFromProps(props);

                            if (searchParams?.keywords == '') {
                              delete searchParams.keywords;
                            }

                            // Only update URL with location params, not custom filters
                            const mergedFilters = {
                              ...urlFilters,
                              address,
                              bounds: boundsParam,
                            };

                            const searchPageURL = createResourceLocatorString(
                              'SearchPage',
                              routeConfiguration,
                              {},
                              mergedFilters
                            );
                            history.push(searchPageURL);
                          }
                        }}
                      />

                      {/* --- START: NEW SKILLS FILTER UI --- */}
                      <div className={css.filter}>
                        <div className={css.filterHeader}>
                          {/* For accessibility and semantics, use type="button" for non-submitting buttons */}
                          <button type="button" className={css.labelButton} onClick={() => setShowSkillsFilter(!showSkillsFilter)}>
                            <span className={css.labelButtonContent}>
                              <span className={css.label}>
                                Skills
                                {activeFilterIndicator(candidateSkillFilter.length)}
                              </span>
                              <span className={css.openSign}>
                                <IconPlus isOpen={showSkillsFilter} />
                              </span>
                            </span>
                          </button>
                        </div>

                        {showSkillsFilter && (
                          <div className={css.plain}>
                            {/* Display added skills */}
                            <div className={css.addedSkillsContainer}>
                              {skillsToFilter.map((skill, index) => (
                                <div key={index} className={css.skillTag}>
                                  {skill.name} ({skill.exp}+ yrs)
                                  <Button size='small' className={css.removeSkillButton} onClick={() => handleRemoveSkill(index)} shape="circle" icon={<CloseOutlined />} />
                                </div>
                              ))}
                            </div>

                            {/* Show input fields only if less than 3 skills have been added */}
                            {skillsToFilter.length < 3 && (
                              <div className={css.skillInputGroup}>
                                <div className={css.skillNameInput}>
                                  <label htmlFor="skillName">Skill Name</label>
                                  <input
                                    id="skillName"
                                    type="text"
                                    value={candidateSkillName}
                                    onChange={(e) => setCandidateSkillName(e.target.value)}
                                    placeholder="e.g., Java"
                                    className={css.input}
                                  />
                                </div>
                                <div className={css.skillExpInput}>
                                  <label htmlFor="skillExp">Experience (years)</label>
                                  <input
                                    id="skillExp"
                                    type="number"
                                    value={candidateSkillExp}
                                    onChange={(e) => setCandidateSkillExp(e.target.value)}
                                    placeholder="e.g., 2"
                                    min="0"
                                    className={css.input}
                                  />
                                </div>
                                <Button
                                  className={css.clearButton}
                                  onClick={handleAddSkill}
                                  disabled={!candidateSkillName || !candidateSkillExp}
                                >
                                  Add
                                </Button>
                              </div>
                            )}
                            {skillsToFilter.length >= 3 && (
                              <p className={css.limitMessage}>You can add a maximum of 3 skills.</p>
                            )}
                          </div>
                        )}
                      </div>
                      {/* --- END: NEW SKILLS FILTER UI --- */}

                    </form>
                  );
                }}
              />
            )}

            {(searchParams?.pub_listingType === 'job_listing') && (
              <FinalForm
                initialValues={{ addressDetail: '' }}
                onSubmit={values => {
                  // This onSubmit will be triggered when the form is submitted.
                  // You can add logic here to apply all filters at once.
                }}
                style={{ zIndex: '999' }}
                render={({ handleSubmit, form }) => {

                  // --- START: NEW STATE & HANDLERS FOR SKILL FILTER ---

                  // State to hold the list of skills to filter by
                  const [skillsToFilter, setSkillsToFilter] = useState([]);

                  // State for the current skill nRequired
                  const [requiredSkillName, setRequiredSkillName] = useState('');

                  // State for the current skill experience input
                  const [requiredSkillExp, setRequiredSkillExp] = useState(''); // Use string to handle empty input

                  useEffect(() => {
                    if (requiredSkillFilter.length === 0) {
                      setSkillsToFilter([]);
                    }
                  }, [requiredSkillFilter]);

                  // Function to add a skill to the filter list
                  const handleAddSkill = () => {
                    if (requiredSkillName && requiredSkillExp > 0 && skillsToFilter.length < 3) {
                      const newSkill = { name: requiredSkillName, exp: requiredSkillExp };

                      setSkillsToFilter([...skillsToFilter, newSkill]);

                      if (requiredSkillFilter.length > 0) {
                        setRequiredSkillFilter([...requiredSkillFilter, newSkill]);
                      } else {
                        setRequiredSkillFilter([newSkill]);
                      }

                      // Clear inputs
                      setRequiredSkillName('');
                      setRequiredSkillExp('');
                    }
                  };

                  // Function to remove a skill from both lists by its index
                  const handleRemoveSkill = (indexToRemove) => {
                    setSkillsToFilter(skillsToFilter.filter((_, index) => index !== indexToRemove));
                    setRequiredSkillFilter(requiredSkillFilter.filter((_, index) => index !== indexToRemove));
                  };

                  // --- END: NEW STATE & HANDLERS FOR SKILL FILTER ---

                  return (
                    <form onSubmit={handleSubmit} className={css.locationSearchForm}>
                      {/* The existing FormSpy for location */}
                      <FormSpy
                        onChange={async (formState) => {
                          // This logic is for the location filter
                          if (formState?.values?.addressDetail?.selectedPlace) {
                            const address = formState?.values?.addressDetail.selectedPlace.address;
                            const bounds = formState?.values?.addressDetail.selectedPlace.bounds;

                            if (address === '') {
                              // Get only URL params, not state-based custom filters
                              const urlFilters = validUrlQueryParamsFromProps(props);
                              const filtersWithoutLocation = { ...urlFilters };
                              delete filtersWithoutLocation.address;
                              delete filtersWithoutLocation.bounds;
                              history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, filtersWithoutLocation));
                              return;
                            }

                            const boundsParam = bounds
                              ? `${bounds.ne.lat},${bounds.ne.lng},${bounds.sw.lat},${bounds.sw.lng}`
                              : null;

                            // Get only URL params, not state-based custom filters
                            const urlFilters = validUrlQueryParamsFromProps(props);

                            if (searchParams?.keywords == '') {
                              delete searchParams.keywords;
                            }

                            // Only update URL with location params, not custom filters
                            const mergedFilters = {
                              ...urlFilters,
                              address,
                              bounds: boundsParam,
                            };

                            const searchPageURL = createResourceLocatorString(
                              'SearchPage',
                              routeConfiguration,
                              {},
                              mergedFilters
                            );
                            history.push(searchPageURL);
                          }
                        }}
                      />

                      {/* --- START: NEW SKILLS FILTER UI --- */}
                      <div className={css.filter}>
                        <div className={css.filterHeader}>
                          {/* For accessibility and semantics, use type="button" for non-submitting buttons */}
                          <button type="button" className={css.labelButton} onClick={() => setShowRequiredSkillsFilter(!showRequiredSkillsFilter)}>
                            <span className={css.labelButtonContent}>
                              <span className={css.label}>
                                Required skills
                                {activeFilterIndicator(requiredSkillFilter.length)}
                              </span>

                              <span className={css.openSign}>
                                <IconPlus isOpen={showRequiredSkillsFilter} />
                              </span>
                            </span>
                          </button>
                        </div>

                        {showRequiredSkillsFilter && (
                          <div className={css.plain}>
                            {/* Display added skills */}
                            <div className={css.addedSkillsContainer}>
                              {skillsToFilter.map((skill, index) => (
                                <div key={index} className={css.skillTag}>
                                  {skill.name} ({skill.exp}+ yrs)
                                  <Button size='small' className={css.removeSkillButton} onClick={() => handleRemoveSkill(index)} shape="circle" icon={<CloseOutlined />} />
                                </div>
                              ))}
                            </div>

                            {/* Show input fields only if less than 3 skills have been added */}
                            {skillsToFilter.length < 3 && (
                              <div className={css.skillInputGroup}>
                                <div className={css.skillNameInput}>
                                  <label htmlFor="skillName">Skill Name</label>
                                  <input
                                    id="skillName"
                                    type="text"
                                    value={requiredSkillName}
                                    onChange={(e) => setRequiredSkillName(e.target.value)}
                                    placeholder="e.g., Java"
                                    className={css.input}
                                  />
                                </div>
                                <div className={css.skillExpInput}>
                                  <label htmlFor="skillExp">Experience (years)</label>
                                  <input
                                    id="skillExp"
                                    type="number"
                                    value={requiredSkillExp}
                                    onChange={(e) => setRequiredSkillExp(e.target.value)}
                                    placeholder="e.g., 2"
                                    min="0"
                                    className={css.input}
                                  />
                                </div>
                                <Button
                                  className={css.clearButton}
                                  onClick={handleAddSkill}
                                  disabled={!requiredSkillName || !requiredSkillExp}
                                >
                                  Add
                                </Button>
                              </div>
                            )}
                            {skillsToFilter.length >= 3 && (
                              <p className={css.limitMessage}>You can add a maximum of 3 skills.</p>
                            )}
                          </div>
                        )}
                      </div>
                      {/* --- END: NEW SKILLS FILTER UI --- */}

                    </form>
                  );
                }}
              />
            )}
            {(searchParams?.pub_listingType === 'job_listing') && (
              <FinalForm
                initialValues={{ addressDetail: '' }}
                onSubmit={values => {
                  // This onSubmit will be triggered when the form is submitted.
                  // You can add logic here to apply all filters at once.
                }}
                style={{ zIndex: '999' }}
                render={({ handleSubmit, form }) => {

                  // --- START: NEW STATE & HANDLERS FOR SKILL FILTER ---

                  // State to hold the list of skills to filter by
                  const [skillsToFilter, setSkillsToFilter] = useState([]);

                  // State for the current skill nRequired
                  const [preferredSkillName, setPreferredSkillName] = useState('');

                  // State for the current skill experience input
                  const [preferredSkillExp, setPreferredSkillExp] = useState(''); // Use string to handle empty input

                  useEffect(() => {
                    if (preferredSkillFilter.length === 0) {
                      setSkillsToFilter([]);
                    }
                  }, [preferredSkillFilter]);

                  // Function to add a skill to the filter list
                  const handleAddSkill = () => {
                    if (preferredSkillName && preferredSkillExp > 0 && skillsToFilter.length < 3) {
                      const newSkill = { name: preferredSkillName, exp: preferredSkillExp };

                      setSkillsToFilter([...skillsToFilter, newSkill]);

                      if (preferredSkillFilter.length > 0) {
                        setPreferredSkillFilter([...preferredSkillFilter, newSkill]);
                      } else {
                        setPreferredSkillFilter([newSkill]);
                      }

                      // Clear inputs
                      setPreferredSkillName('');
                      setPreferredSkillExp('');
                    }
                  };

                  // Function to remove a skill from both lists by its index
                  const handleRemoveSkill = (indexToRemove) => {
                    setSkillsToFilter(skillsToFilter.filter((_, index) => index !== indexToRemove));
                    setPreferredSkillFilter(preferredSkillFilter.filter((_, index) => index !== indexToRemove));
                  };

                  // --- END: NEW STATE & HANDLERS FOR SKILL FILTER ---

                  return (
                    <form onSubmit={handleSubmit} className={css.locationSearchForm}>
                      {/* The existing FormSpy for location */}
                      <FormSpy
                        onChange={async (formState) => {
                          // This logic is for the location filter
                          if (formState?.values?.addressDetail?.selectedPlace) {
                            const address = formState?.values?.addressDetail.selectedPlace.address;
                            const bounds = formState?.values?.addressDetail.selectedPlace.bounds;
                            if (address === '') {
                              const previousFilters = getAllCurrentFilters();
                              const filtersWithoutLocation = { ...previousFilters };
                              delete filtersWithoutLocation.address;
                              delete filtersWithoutLocation.bounds;
                              history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, filtersWithoutLocation));
                              return;
                            }

                            const boundsParam = bounds
                              ? `${bounds.ne.lat},${bounds.ne.lng},${bounds.sw.lat},${bounds.sw.lng}`
                              : null;

                            const previousFilters = getAllCurrentFilters();

                            if (searchParams?.keywords == '') {
                              delete searchParams.keywords;
                            }

                            // IMPORTANT: You will need to add the skills filter to the URL here
                            // Example: pub_skills: skillsToFilter.map(s => `${s.name}_${s.exp}`).join(',')
                            const mergedFilters = {
                              ...previousFilters,
                              address,
                              bounds: boundsParam,
                              // pub_skills: skillsParam, // Add the skills parameter here
                            };

                            const searchPageURL = createResourceLocatorString(
                              'SearchPage',
                              routeConfiguration,
                              {},
                              mergedFilters
                            );
                            history.push(searchPageURL);
                          }
                        }}
                      />

                      {/* --- START: NEW SKILLS FILTER UI --- */}
                      <div className={css.filter}>
                        <div className={css.filterHeader}>
                          {/* For accessibility and semantics, use type="button" for non-submitting buttons */}
                          <button type="button" className={css.labelButton} onClick={() => setShowPreferredSkillsFilter(!showPreferredSkillsFilter)}>
                            <span className={css.labelButtonContent}>
                              <span className={css.label}>
                                Preferred skills
                                {activeFilterIndicator(preferredSkillFilter.length)}
                              </span>
                              <span className={css.openSign}>
                                <IconPlus isOpen={showPreferredSkillsFilter} />
                              </span>
                            </span>
                          </button>
                        </div>

                        {showPreferredSkillsFilter && (
                          <div className={css.plain}>
                            {/* Display added skills */}
                            <div className={css.addedSkillsContainer}>
                              {skillsToFilter.map((skill, index) => (
                                <div key={index} className={css.skillTag}>
                                  {skill.name} ({skill.exp}+ yrs)
                                  <Button size='small' className={css.removeSkillButton} onClick={() => handleRemoveSkill(index)} shape="circle" icon={<CloseOutlined />} />
                                </div>
                              ))}
                            </div>

                            {/* Show input fields only if less than 3 skills have been added */}
                            {skillsToFilter.length < 3 && (
                              <div className={css.skillInputGroup}>
                                <div className={css.skillNameInput}>
                                  <label htmlFor="skillName">Skill Name</label>
                                  <input
                                    id="skillName"
                                    type="text"
                                    value={preferredSkillName}
                                    onChange={(e) => setPreferredSkillName(e.target.value)}
                                    placeholder="e.g., Java"
                                    className={css.input}
                                  />
                                </div>
                                <div className={css.skillExpInput}>
                                  <label htmlFor="skillExp">Experience (years)</label>
                                  <input
                                    id="skillExp"
                                    type="number"
                                    value={preferredSkillExp}
                                    onChange={(e) => setPreferredSkillExp(e.target.value)}
                                    placeholder="e.g., 2"
                                    min="0"
                                    className={css.input}
                                  />
                                </div>
                                <Button
                                  className={css.clearButton}
                                  onClick={handleAddSkill}
                                  disabled={!preferredSkillName || !preferredSkillExp}
                                >
                                  Add
                                </Button>
                              </div>
                            )}
                            {skillsToFilter.length >= 3 && (
                              <p className={css.limitMessage}>You can add a maximum of 3 skills.</p>
                            )}
                          </div>
                        )}
                      </div>
                      {/* --- END: NEW SKILLS FILTER UI --- */}

                    </form>
                  );
                }}
              />
            )}

            {(searchParams?.pub_listingType !== 'rfp_listing') && (
              <FinalForm
                initialValues={{ addressDetail: '' }}
                onSubmit={values => {
                }}
                style={{ zIndex: '999' }}
                render={({ handleSubmit, form }) => {
                  // Store the form reference so we can clear it from resetAll
                  useEffect(() => {
                    locationFormRef.current = form;
                    return () => {
                      locationFormRef.current = null;
                    };
                  }, [form]);

                  return ( // This form seems to be for location search, which is already in Topbar. Maybe it's for mobile? No, it's inside filter column.
                    <form onSubmit={handleSubmit} className={css.locationSearchForm}>

                      <FormSpy
                        onChange={async (formState) => {

                          if (formState?.values?.addressDetail?.selectedPlace) {
                            const address = formState?.values?.addressDetail.selectedPlace.address;
                            const bounds = formState?.values?.addressDetail.selectedPlace.bounds; // This logic is complex and seems to be duplicated from TopbarSearchForm

                            if (address === '') {
                              setLocationSelected(false);
                              // Get only URL params, not state-based custom filters
                              const urlFilters = validUrlQueryParamsFromProps(props);
                              const filtersWithoutLocation = { ...urlFilters };
                              delete filtersWithoutLocation.address;
                              delete filtersWithoutLocation.bounds;
                              history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, filtersWithoutLocation));
                              return;
                            }

                            setLocationSelected(true);

                            const boundsParam = bounds
                              ? `${bounds.ne.lat},${bounds.ne.lng},${bounds.sw.lat},${bounds.sw.lng}`
                              : null;

                            // Get only URL params, not state-based custom filters
                            const urlFilters = validUrlQueryParamsFromProps(props);

                            if (searchParams?.keywords == '') {
                              delete searchParams.keywords;
                            }

                            // Only update URL with location params, not custom filters
                            const mergedFilters = {
                              ...urlFilters,
                              address,
                              bounds: boundsParam,
                            };


                            const searchPageURL = createResourceLocatorString(
                              'SearchPage',
                              routeConfiguration,
                              {},
                              mergedFilters
                            );

                            history.push(searchPageURL);
                          }
                        }}
                      >
                      </FormSpy>

                      <div className={css.filter}>
                        <div className={css.filterHeader}>
                          <button className={css.labelButton} onClick={() => setShowLocationSearch(!showLocationSearch)}>
                            <span className={css.labelButtonContent}>
                              <span className={css.label}>
                                Location
                                {locationSelected ? activeFilterIndicator(1) : null}
                              </span>
                              {/* <span className={css.label}>Custom Filters</span> */}
                              {/* {activeFilterIndicator(activeCustomFiltersCount)} */}
                              <span className={css.openSign}>
                                <IconPlus isOpen={showLocationSearch} />
                              </span>
                            </span>
                          </button>
                        </div>
                        {showLocationSearch && (
                          <div className={css.plain}>
                            <Field
                              id="addressDetail"
                              name="addressDetail"
                              format={identity}
                              component={LocationAutocompleteInput}
                              className={css.location}
                            // label={'Address'}
                            // validate={validators.required(
                            //   intl.formatMessage({ id: 'ProfileSettingsForm.cityAddressRequired' })
                            // )}
                            />
                            <button
                              className={css.clearButton}
                              type="button"
                              onClick={() => clearSearchLocation(form)}
                            >
                              <FormattedMessage id={'FilterPlain.clear'} />
                            </button>
                          </div>
                        )}
                      </div>


                    </form>
                  );
                }}
              />
            )}

            <button className={css.resetAllButton} onClick={e => handleResetAll(e)}>
              <FormattedMessage id={'SearchFiltersMobile.resetAll'} />
            </button>
          </div>
        </aside>

        <div className={css.layoutWrapperMain} role="main">
          <div className={css.searchResultContainer}>
            <SearchFiltersMobile
              className={css.searchFiltersMobileList}
              urlQueryParams={validQueryParams}
              sortByComponent={sortBy('mobile')}
              listingsAreLoaded={shouldShowListings}
              resultsCount={totalItems}
              searchInProgress={searchInProgress}
              searchListingsError={searchListingsError}
              showAsModalMaxWidth={MODAL_BREAKPOINT}
              onManageDisableScrolling={props.onManageDisableScrolling}
              onOpenModal={onOpenMobileModal}
              onCloseModal={onCloseMobileModal}
              resetAll={resetAll}
              selectedFiltersCount={selectedFiltersCountForMobile}
              isMapVariant={false}
              noResultsInfo={noResultsInfo}
            >

              {availableFilters.map(filterConfig => {
                const key = `SearchFiltersMobile.${filterConfig.scope || 'built-in'}.${filterConfig.key
                  }`;

                return (
                  <FilterComponent
                    key={key}
                    idPrefix="SearchFiltersMobile"
                    config={filterConfig}
                    listingCategories={listingCategories}
                    marketplaceCurrency={marketplaceCurrency}
                    urlQueryParams={validQueryParams}
                    initialValues={initialValues(props, currentQueryParams)}
                    getHandleChangedValueFn={getHandleChangedValueFn}
                    intl={intl}
                    liveEdit
                    showAsPopup={false}
                  />
                );
              })}

              {searchParams?.pub_listingType === 'job_listing' ? (
                <>
                  <div className={css.filter}>
                    <div className={css.filterHeader}>
                      <button className={css.labelButton} onClick={() => setShowExpectedStartDate(!showExpectedStartDate)}>
                        <span className={css.labelButtonContent}>
                          <span className={css.label}>
                            Expected start date
                            {activeFilterIndicator(expectedStartDate ? 1 : 0)}
                          </span>
                          <span className={css.openSign}>
                            <IconPlus isOpen={showExpectedStartDate} />
                          </span>
                        </span>
                      </button>
                    </div>
                    {showExpectedStartDate && (
                      <FinalForm
                        onSubmit={() => { }}
                        initialValues={{ expectedStartDateJob: expectedStartDate }}
                        render={({ handleSubmit, form }) => (
                          <form onSubmit={handleSubmit}>
                            <FormSpy
                              subscription={{ values: true }}
                              onChange={formState => {
                                if (formState.values.expectedStartDateJob !== expectedStartDate) {
                                  handleDateChange(formState.values.expectedStartDateJob);
                                }
                              }}
                            />
                            <div className={css.plain}>
                              <FieldSingleDatePicker
                                id="expectedStartDateJob"
                                name="expectedStartDateJob"
                                placeholderText="Select a date"
                                onChange={date => {
                                  setExpectedStartDate(date);
                                }}
                                isOutsideRange={() => false}
                                useMobileMargins
                              />
                              <button
                                className={css.clearButton}
                                type="button"
                                onClick={() => {
                                  form.change('expectedStartDateJob', null);
                                  setExpectedStartDate(null);
                                }}
                              >
                                <FormattedMessage id={'FilterPlain.clear'} />
                              </button>
                            </div>
                          </form>
                        )}
                      />
                    )}
                  </div>

                  <div className={css.filter}>
                    <div className={css.filterHeader}>
                      <button className={css.labelButton} onClick={() => setShowSalaryRange(!showSalaryRange)}>
                        <span className={css.labelButtonContent}>
                          <span className={css.label}>
                            Salary / Pay range (per month)
                            {activeFilterIndicator(salaryRange?.min || salaryRange?.max ? 1 : 0)}
                          </span>
                          <span className={css.openSign}>
                            <IconPlus isOpen={showSalaryRange} />
                          </span>
                        </span>
                      </button>
                    </div>
                    {showSalaryRange && (
                      <FinalForm
                        onSubmit={() => { }}
                        initialValues={{ salary: salaryRange }}
                        render={({ handleSubmit, form }) => (
                          <form onSubmit={handleSubmit}>
                            <FormSpy
                              subscription={{ values: true }}
                              onChange={formState => {
                                if (formState.values.salary !== salaryRange) {
                                  handleSalaryChange(formState.values.salary);
                                }
                              }}
                            />
                            <div className={css.plain}>
                              <div className={css.inputsWrapper}>
                                <FieldTextInput
                                  id="salary.min"
                                  name="salary.min"
                                  type="number"
                                  placeholder="Min"
                                  className={css.rangeInput}
                                />
                                <span className={css.separator}>&nbsp;</span>
                                <FieldTextInput
                                  id="salary.max"
                                  name="salary.max"
                                  type="number"
                                  placeholder="Max"
                                  className={css.rangeInput}
                                />
                              </div>
                              <button
                                className={css.clearButton}
                                type="button"
                                onClick={() => {
                                  form.change('salary', { min: null, max: null });
                                  handleSalaryChange({ min: null, max: null });
                                }}
                              >
                                <FormattedMessage id={'FilterPlain.clear'} />
                              </button>
                            </div>
                          </form>
                        )}
                      />
                    )}
                  </div>

                  <div className={css.filter}>
                    <div className={css.filterHeader}>
                      <button className={css.labelButton} onClick={() => setShowBillRateRange(!showBillRateRange)}>
                        <span className={css.labelButtonContent}>
                          <span className={css.label}>
                            Bill rate target (per hour)
                            {activeFilterIndicator(billRateRange?.min || billRateRange?.max ? 1 : 0)}
                          </span>
                          <span className={css.openSign}>
                            <IconPlus isOpen={showBillRateRange} />
                          </span>
                        </span>
                      </button>
                    </div>
                    {showBillRateRange && (
                      <FinalForm
                        onSubmit={() => { }}
                        initialValues={{ billRate: billRateRange }}
                        render={({ handleSubmit, form }) => (
                          <form onSubmit={handleSubmit}>
                            <FormSpy
                              subscription={{ values: true }}
                              onChange={formState => {
                                if (formState.values.billRate !== billRateRange) {
                                  handleBillRateChange(formState.values.billRate);
                                }
                              }}
                            />
                            <div className={css.plain}>
                              <div className={css.inputsWrapper}>
                                <FieldTextInput id="billRate.min" name="billRate.min" type="number" placeholder="Min" className={css.rangeInput} />
                                <span className={css.separator}>&nbsp;</span>
                                <FieldTextInput id="billRate.max" name="billRate.max" type="number" placeholder="Max" className={css.rangeInput} />
                              </div>
                              <button className={css.clearButton} type="button" onClick={() => { form.change('billRate', { min: null, max: null }); handleBillRateChange({ min: null, max: null }); }}>
                                <FormattedMessage id={'FilterPlain.clear'} />
                              </button>
                            </div>
                          </form>
                        )}
                      />
                    )}
                  </div>

                </>
              ) : null}

              {searchParams?.pub_listingType === 'rfp_listing' ? (
                <>
                  <div className={css.filter}>
                    <div className={css.filterHeader}>
                      <button className={css.labelButton} onClick={() => setShowSubmissionDeadlineRange(!showSubmissionDeadlineRange)}>
                        <span className={css.labelButtonContent}>
                          <span className={css.label}>
                            Submission deadline range
                            {activeFilterIndicator(submissionDeadlineRange?.min || submissionDeadlineRange?.max ? 1 : 0)}
                          </span>
                          <span className={css.openSign}>
                            <IconPlus isOpen={showSubmissionDeadlineRange} />
                          </span>
                        </span>
                      </button>
                    </div>
                    {showSubmissionDeadlineRange && (
                      <FinalForm
                        onSubmit={() => { }}
                        initialValues={{ submissionDeadline: submissionDeadlineRange }}
                        render={({ handleSubmit, form }) => (
                          <form onSubmit={handleSubmit}>
                            <FormSpy
                              subscription={{ values: true }}
                              onChange={formState => {
                                if (formState.values.submissionDeadline !== submissionDeadlineRange) {
                                  handleSubmissionDeadlineChange(formState.values.submissionDeadline);
                                }
                              }}
                            />
                            <div className={css.plain}>
                              <div className={css.inputsWrapper}>
                                <FieldSingleDatePicker id="submissionDeadline.min" name="submissionDeadline.min" placeholderText="Min date" isOutsideRange={() => false} useMobileMargins />
                                <span className={css.separator}>&nbsp;</span>
                                <FieldSingleDatePicker id="submissionDeadline.max" name="submissionDeadline.max" placeholderText="Max date" isOutsideRange={() => false} useMobileMargins />
                              </div>
                              <button
                                className={css.clearButton}
                                type="button"
                                onClick={() => {
                                  form.change('submissionDeadline', { min: null, max: null });
                                  handleSubmissionDeadlineChange({ min: null, max: null });
                                }}
                              >
                                <FormattedMessage id={'FilterPlain.clear'} />
                              </button>
                            </div>
                          </form>
                        )}
                      />
                    )}
                  </div>

                  <div className={css.filter}>
                    <div className={css.filterHeader}>
                      <button className={css.labelButton} onClick={() => setShowPostingDateRange(!showPostingDateRange)}>
                        <span className={css.labelButtonContent}>
                          <span className={css.label}>
                            Posting date range
                            {activeFilterIndicator(postingDateRange?.min || postingDateRange?.max ? 1 : 0)}
                          </span>
                          <span className={css.openSign}>
                            <IconPlus isOpen={showPostingDateRange} />
                          </span>
                        </span>
                      </button>
                    </div>
                    {showPostingDateRange && (
                      <FinalForm
                        onSubmit={() => { }}
                        initialValues={{ postingDate: postingDateRange }}
                        render={({ handleSubmit, form }) => (
                          <form onSubmit={handleSubmit}>
                            <FormSpy
                              subscription={{ values: true }}
                              onChange={formState => {
                                if (formState.values.postingDate !== postingDateRange) {
                                  handlePostingDateChange(formState.values.postingDate);
                                }
                              }}
                            />
                            <div className={css.plain}>
                              <div className={css.inputsWrapper}>
                                <FieldSingleDatePicker
                                  id="postingDate.min"
                                  name="postingDate.min"
                                  placeholderText="Min date"
                                  useMobileMargins
                                  isOutsideRange={() => false}
                                />
                                <span className={css.separator}>&nbsp;</span>
                                <FieldSingleDatePicker
                                  id="postingDate.max"
                                  name="postingDate.max"
                                  placeholderText="Max date"
                                  useMobileMargins
                                  isOutsideRange={() => false}
                                />
                              </div>
                              <button
                                className={css.clearButton}
                                type="button"
                                onClick={() => {
                                  form.change('postingDate', { min: null, max: null });
                                  handlePostingDateChange({ min: null, max: null });
                                }}
                              >
                                <FormattedMessage id={'FilterPlain.clear'} />
                              </button>
                            </div>
                          </form>
                        )}
                      />
                    )}
                  </div>

                  {/* RFP Service Domain - Multiple select dropdown filter */}
                  <div className={css.filter}>
                    <div className={css.filterHeader}>
                      <button className={css.labelButton} onClick={() => setShowRfpServiceDomain(!showRfpServiceDomain)}>
                        <span className={css.labelButtonContent}>
                          <span className={css.label}>
                            Service Domain
                            {activeFilterIndicator(rfpServiceDomain?.length || 0)}
                          </span>
                          <span className={css.openSign}>
                            <IconPlus isOpen={showRfpServiceDomain} />
                          </span>
                        </span>
                      </button>
                    </div>
                    {showRfpServiceDomain && (
                      <div className={css.plain}>
                        <Select
                          mode="multiple"
                          allowClear
                          style={{ width: '100%' }}
                          placeholder="Select service domains"
                          value={rfpServiceDomain}
                          onChange={handleRfpServiceDomainChange}
                          optionFilterProp="label"
                          showSearch
                        >
                          {serviceDomainOptions.map(opt => (
                            <Select.Option key={opt.option} value={opt.option} label={opt.label}>
                              {opt.label}
                            </Select.Option>
                          ))}
                        </Select>
                        <button className={css.clearButton} type="button" onClick={() => handleRfpServiceDomainChange([])}>
                          <FormattedMessage id={'FilterPlain.clear'} />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* RFP Technology Domain - Multiple select dropdown filter */}
                  <div className={css.filter}>
                    <div className={css.filterHeader}>
                      <button className={css.labelButton} onClick={() => setShowRfpTechnologyDomain(!showRfpTechnologyDomain)}>
                        <span className={css.labelButtonContent}>
                          <span className={css.label}>
                            Technology Domain
                            {activeFilterIndicator(rfpTechnologyDomain?.length || 0)}
                          </span>
                          <span className={css.openSign}>
                            <IconPlus isOpen={showRfpTechnologyDomain} />
                          </span>
                        </span>
                      </button>
                    </div>
                    {showRfpTechnologyDomain && (
                      <div className={css.plain}>
                        <Select
                          mode="multiple"
                          allowClear
                          style={{ width: '100%' }}
                          placeholder="Select technology domains"
                          value={rfpTechnologyDomain}
                          onChange={handleRfpTechnologyDomainChange}
                          optionFilterProp="label"
                          showSearch
                        >
                          {technologyOptions.map(opt => (
                            <Select.Option key={opt.option} value={opt.option} label={opt.label}>
                              {opt.label}
                            </Select.Option>
                          ))}
                        </Select>
                        <button className={css.clearButton} type="button" onClick={() => handleRfpTechnologyDomainChange([])}>
                          <FormattedMessage id={'FilterPlain.clear'} />
                        </button>
                      </div>
                    )}
                  </div>

                </>
              ) : null}

              {searchParams?.pub_listingType === 'candidate' ? (
                <>
                  {/* Certifications Filter - shown at top for candidate search */}
                  <div className={css.filter}>
                    <div className={css.filterHeader}>
                      <button className={css.labelButton} onClick={() => setShowCandidateCertifications(!showCandidateCertifications)}>
                        <span className={css.labelButtonContent}>
                          <span className={css.label}>
                            Certifications
                            {activeFilterIndicator(candidateCertifications ? 1 : 0)}
                          </span>
                          <span className={css.openSign}>
                            <IconPlus isOpen={showCandidateCertifications} />
                          </span>
                        </span>
                      </button>
                    </div>
                    {showCandidateCertifications && (
                      <FinalForm
                        onSubmit={() => { }}
                        initialValues={{ candidateCertifications: candidateCertifications }}
                        render={({ handleSubmit, form }) => (
                          <form onSubmit={handleSubmit}>
                            <FormSpy
                              subscription={{ values: true }}
                              onChange={formState => {
                                if (formState.values.candidateCertifications !== candidateCertifications) {
                                  handleCandidateCertificationsChange(formState.values.candidateCertifications);
                                }
                              }}
                            />
                            <div className={css.plain}>
                              <FieldTextInput
                                id="candidateCertifications"
                                name="candidateCertifications"
                                type="text"
                                placeholder="e.g. PMP, AWS Certified"
                              />
                              <button className={css.clearButton} type="button" onClick={() => { form.change('candidateCertifications', ''); handleCandidateCertificationsChange(''); }}>
                                <FormattedMessage id={'FilterPlain.clear'} />
                              </button>
                            </div>
                          </form>
                        )}
                      />
                    )}
                  </div>

                  <div className={css.filter}>
                    <div className={css.filterHeader}>
                      <button className={css.labelButton} onClick={() => setShowAvailableToStartRange(!showAvailableToStartRange)}>
                        <span className={css.labelButtonContent}>
                          <span className={css.label}>
                            Date available to start
                            {activeFilterIndicator(availableToStartRange?.min || availableToStartRange?.max ? 1 : 0)}
                          </span>
                          <span className={css.openSign}>
                            <IconPlus isOpen={showAvailableToStartRange} />
                          </span>
                        </span>
                      </button>
                    </div>
                    {showAvailableToStartRange && (
                      <FinalForm
                        onSubmit={() => { }}
                        initialValues={{ availableToStart: availableToStartRange }}
                        render={({ handleSubmit, form }) => (
                          <form onSubmit={handleSubmit}>
                            <FormSpy
                              subscription={{ values: true }}
                              onChange={formState => {
                                if (formState.values.availableToStart !== availableToStartRange) {
                                  handleAvailableToStartChange(formState.values.availableToStart);
                                }
                              }}
                            />
                            <div className={css.plain}>
                              <div className={css.inputsWrapper}>
                                <FieldSingleDatePicker id="availableToStart.min" name="availableToStart.min" placeholderText="Min date" isOutsideRange={() => false} useMobileMargins />
                                <span className={css.separator}>&nbsp;</span>
                                <FieldSingleDatePicker id="availableToStart.max" name="availableToStart.max" placeholderText="Max date" isOutsideRange={() => false} useMobileMargins />
                              </div>
                              <button className={css.clearButton} type="button" onClick={() => { form.change('availableToStart', { min: null, max: null }); handleAvailableToStartChange({ min: null, max: null }); }}>
                                <FormattedMessage id={'FilterPlain.clear'} />
                              </button>
                            </div>
                          </form>
                        )}
                      />
                    )}
                  </div>

                </>
              ) : null}

              {searchParams?.pub_listingType === 'candidate' ? (
                <>
                  <div className={css.filter}>
                    <div className={css.filterHeader}>
                      <button className={css.labelButton} onClick={() => setShowCurrentEmployer(!showCurrentEmployer)}>
                        <span className={css.labelButtonContent}>
                          <span className={css.label}>
                            Current Employer
                            {activeFilterIndicator(currentEmployer ? 1 : 0)}
                          </span>
                          <span className={css.openSign}>
                            <IconPlus isOpen={showCurrentEmployer} />
                          </span>
                        </span>
                      </button>
                    </div>
                    {showCurrentEmployer && (
                      <FinalForm
                        onSubmit={() => { }}
                        initialValues={{ currentEmployer: currentEmployer }}
                        render={({ handleSubmit, form }) => (
                          <form onSubmit={handleSubmit}>
                            <FormSpy
                              subscription={{ values: true }}
                              onChange={formState => {
                                if (formState.values.currentEmployer !== currentEmployer) {
                                  handleCurrentEmployerChange(formState.values.currentEmployer);
                                }
                              }}
                            />
                            <div className={css.plain}>
                              <FieldTextInput
                                id="currentEmployer"
                                name="currentEmployer"
                                type="text"
                                placeholder="Employer name"
                              // className={css.location}
                              />
                              <button className={css.clearButton} type="button" onClick={() => { form.change('currentEmployer', ''); handleCurrentEmployerChange(''); }}>
                                <FormattedMessage id={'FilterPlain.clear'} />
                              </button>
                            </div>
                          </form>
                        )}
                      />
                    )}
                  </div>

                  {/* Education Level Filter */}
                  <div className={css.filter}>
                    <div className={css.filterHeader}>
                      <button className={css.labelButton} onClick={() => setShowEducationLevel(!showEducationLevel)}>
                        <span className={css.labelButtonContent}>
                          <span className={css.label}>
                            Education Level
                            {activeFilterIndicator(educationLevel ? 1 : 0)}
                          </span>
                          <span className={css.openSign}>
                            <IconPlus isOpen={showEducationLevel} />
                          </span>
                        </span>
                      </button>
                    </div>
                    {showEducationLevel && (
                      <FinalForm
                        onSubmit={() => { }}
                        initialValues={{ educationLevel: educationLevel }}
                        render={({ handleSubmit, form }) => (
                          <form onSubmit={handleSubmit}>
                            <FormSpy
                              subscription={{ values: true }}
                              onChange={formState => {
                                if (formState.values.educationLevel !== educationLevel) {
                                  handleEducationLevelChange(formState.values.educationLevel);
                                }
                              }}
                            />
                            <div className={css.plain}>
                              <FieldTextInput
                                id="educationLevel"
                                name="educationLevel"
                                type="text"
                                placeholder="e.g. Bachelor's, Master's"
                              />
                              <button className={css.clearButton} type="button" onClick={() => { form.change('educationLevel', ''); handleEducationLevelChange(''); }}>
                                <FormattedMessage id={'FilterPlain.clear'} />
                              </button>
                            </div>
                          </form>
                        )}
                      />
                    )}
                  </div>

                  {/* Job Role Filter */}
                  <div className={css.filter}>
                    <div className={css.filterHeader}>
                      <button className={css.labelButton} onClick={() => setShowJobRole(!showJobRole)}>
                        <span className={css.labelButtonContent}>
                          <span className={css.label}>
                            Job Role
                            {activeFilterIndicator(jobRole ? 1 : 0)}
                          </span>
                          <span className={css.openSign}>
                            <IconPlus isOpen={showJobRole} />
                          </span>
                        </span>
                      </button>
                    </div>
                    {showJobRole && (
                      <FinalForm
                        onSubmit={() => { }}
                        initialValues={{ jobRole: jobRole }}
                        render={({ handleSubmit, form }) => (
                          <form onSubmit={handleSubmit}>
                            <FormSpy
                              subscription={{ values: true }}
                              onChange={formState => {
                                if (formState.values.jobRole !== jobRole) {
                                  handleJobRoleChange(formState.values.jobRole);
                                }
                              }}
                            />
                            <div className={css.plain}>
                              <FieldTextInput
                                id="jobRole"
                                name="jobRole"
                                type="text"
                                placeholder="e.g. Software Engineer, Manager"
                              />
                              <button className={css.clearButton} type="button" onClick={() => { form.change('jobRole', ''); handleJobRoleChange(''); }}>
                                <FormattedMessage id={'FilterPlain.clear'} />
                              </button>
                            </div>
                          </form>
                        )}
                      />
                    )}
                  </div>

                </>
              ) : null}

              {/* {(searchParams?.pub_listingType === 'candidate' && !!currentUser) ? (
                  <>
                    <div className={css.filter}>
                      <div className={css.filterHeader}>
                        <button className={css.labelButton} onClick={() => setShowWorkAuthExpiryRange(!showWorkAuthExpiryRange)}>
                          <span className={css.labelButtonContent}>
                            <span className={css.label}>
                              Work auth expiry date
                              {activeFilterIndicator(workAuthExpiryRange?.min || workAuthExpiryRange?.max ? 1 : 0)}
                            </span>
                            <span className={css.openSign}>
                              <IconPlus isOpen={showWorkAuthExpiryRange} />
                            </span>
                          </span>
                        </button>
                      </div>
                      {showWorkAuthExpiryRange && (
                        <FinalForm
                          onSubmit={() => {}}
                          initialValues={{ workAuthExpiry: workAuthExpiryRange }}
                          render={({ handleSubmit, form }) => (
                            <form onSubmit={handleSubmit}>
                              <FormSpy
                                subscription={{ values: true }}
                                onChange={formState => {
                                  if (formState.values.workAuthExpiry !== workAuthExpiryRange) {
                                    handleWorkAuthExpiryChange(formState.values.workAuthExpiry);
                                  }
                                }}
                              />
                              <div className={css.plain}>
                                <div className={css.inputsWrapper}>
                                  <FieldSingleDatePicker id="workAuthExpiry.min" name="workAuthExpiry.min" placeholderText="Min date" isOutsideRange={() => false} useMobileMargins />
                                  <span className={css.separator}>&nbsp;</span>
                                  <FieldSingleDatePicker id="workAuthExpiry.max" name="workAuthExpiry.max" placeholderText="Max date" isOutsideRange={() => false} useMobileMargins />
                                </div>
                                <button className={css.clearButton} type="button" onClick={() => { form.change('workAuthExpiry', { min: null, max: null }); handleWorkAuthExpiryChange({ min: null, max: null }); }}>
                                  <FormattedMessage id={'FilterPlain.clear'} />
                                </button>
                              </div>
                            </form>
                          )}
                        />
                      )}
                    </div>
                    
                  </>
                ) : null} */}

              {searchParams?.pub_listingType === 'candidate' ? (
                <>
                  <div className={css.filter}>
                    <div className={css.filterHeader}>
                      <button className={css.labelButton} onClick={() => setShowBillingRateTargetRange(!showBillingRateTargetRange)}>
                        <span className={css.labelButtonContent}>
                          <span className={css.label}>Billing rate target (per hour)</span>
                          {activeFilterIndicator(billingRateTargetRange?.min || billingRateTargetRange?.max ? 1 : 0)}
                          <span className={css.openSign}>
                            <IconPlus isOpen={showBillingRateTargetRange} />
                          </span>
                        </span>
                      </button>
                    </div>
                    {showBillingRateTargetRange && (
                      <FinalForm
                        onSubmit={() => { }}
                        initialValues={{ billingRateTarget: billingRateTargetRange }}
                        render={({ handleSubmit, form }) => (
                          <form onSubmit={handleSubmit}>
                            <FormSpy
                              subscription={{ values: true }}
                              onChange={formState => {
                                if (formState.values.billingRateTarget !== billingRateTargetRange) {
                                  handleBillingRateTargetChange(formState.values.billingRateTarget);
                                }
                              }}
                            />
                            <div className={css.plain}>
                              <div className={css.inputsWrapper}>
                                <FieldTextInput id="billingRateTarget.min" name="billingRateTarget.min" type="number" placeholder="Min" className={css.rangeInput} />
                                <span className={css.separator}>&nbsp;</span>
                                <FieldTextInput id="billingRateTarget.max" name="billingRateTarget.max" type="number" placeholder="Max" className={css.rangeInput} />
                              </div>
                              <button className={css.clearButton} type="button" onClick={() => { form.change('billingRateTarget', { min: null, max: null }); handleBillingRateTargetChange({ min: null, max: null }); }}>
                                <FormattedMessage id={'FilterPlain.clear'} />
                              </button>
                            </div>
                          </form>
                        )}
                      />
                    )}
                  </div>

                </>
              ) : null}

              {/* {(searchParams?.pub_listingType === 'candidate' && !!currentUser) ? (
                  <>
                    <div className={css.filter}>
                      <div className={css.filterHeader}>
                        <button className={css.labelButton} onClick={() => setShowCountryOfCitizenship(!showCountryOfCitizenship)}>
                          <span className={css.labelButtonContent}>
                            <span className={css.label}>
                              Country of citizenship
                              {activeFilterIndicator(countryOfCitizenship ? 1 : 0)}
                            </span>
                            <span className={css.openSign}>
                              <IconPlus isOpen={showCountryOfCitizenship} />
                            </span>
                          </span>
                        </button>
                      </div>
                      {showCountryOfCitizenship && (
                        <FinalForm
                          onSubmit={() => {}}
                          initialValues={{ countryOfCitizenship: countryOfCitizenship }}
                          render={({ handleSubmit, form }) => (
                            <form onSubmit={handleSubmit}>
                              <FormSpy
                                subscription={{ values: true }}
                                onChange={formState => {
                                  if (formState.values.countryOfCitizenship !== countryOfCitizenship) {
                                    handleCountryOfCitizenshipChange(formState.values.countryOfCitizenship);
                                  }
                                }}
                              />
                              <div className={css.plain}>
                                <FieldSelect id="countryOfCitizenship" name="countryOfCitizenship">
                                  <option value="">All countries</option>
                                  {Country.getAllCountries().map(country => (
                                    <option key={country.isoCode} value={country.name}>
                                      {country.name}
                                    </option>
                                  ))}
                                </FieldSelect>
                                <button className={css.clearButton} type="button" onClick={() => { form.change('countryOfCitizenship', null); handleCountryOfCitizenshipChange(null); }}>
                                  <FormattedMessage id={'FilterPlain.clear'} />
                                </button>
                              </div>
                            </form>
                          )}
                        />
                      )}
                    </div>
                    
                  </>
                ) : null} */}

              {/* {(searchParams?.pub_listingType === 'candidate' && !!currentUser) ? (
                  <>
                    <div className={css.filter}>
                      <div className={css.filterHeader}>
                        <button className={css.labelButton} onClick={() => setShowCountryOfResidence(!showCountryOfResidence)}>
                          <span className={css.labelButtonContent}>
                            <span className={css.label}>
                              Country of residence
                              {activeFilterIndicator(countryOfResidence ? 1 : 0)}
                            </span>
                            <span className={css.openSign}>
                              <IconPlus isOpen={showCountryOfResidence} />
                            </span>
                          </span>
                        </button>
                      </div>
                      {showCountryOfResidence && (
                        <FinalForm
                          onSubmit={() => {}}
                          initialValues={{ countryOfResidence: countryOfResidence }}
                          render={({ handleSubmit, form }) => (
                            <form onSubmit={handleSubmit}>
                              <FormSpy
                                subscription={{ values: true }}
                                onChange={formState => {
                                  if (formState.values.countryOfResidence !== countryOfResidence) {
                                    handleCountryOfResidenceChange(formState.values.countryOfResidence);
                                  }
                                }}
                              />
                              <div className={css.plain}>
                                <FieldSelect id="countryOfResidence" name="countryOfResidence">
                                  <option value="">All countries</option>
                                  {Country.getAllCountries().map(country => (
                                    <option key={country.isoCode} value={country.name}>
                                      {country.name}
                                    </option>
                                  ))}
                                </FieldSelect>
                                <button className={css.clearButton} type="button" onClick={() => { form.change('countryOfResidence', null); handleCountryOfResidenceChange(null); }}>
                                  <FormattedMessage id={'FilterPlain.clear'} />
                                </button>
                              </div>
                            </form>
                          )}
                        />
                      )}
                    </div>
                    
                  </>
                ) : null} */}

              {searchParams?.pub_listingType === 'agency' ? (
                <>
                  <div className={css.filter}>
                    <div className={css.filterHeader}>
                      <button className={css.labelButton} onClick={() => setShowCountryOfFormation(!showCountryOfFormation)}>
                        <span className={css.labelButtonContent}>
                          <span className={css.label}>
                            Country of Formation
                            {activeFilterIndicator(countryOfFormation ? 1 : 0)}
                          </span>
                          <span className={css.openSign}>
                            <IconPlus isOpen={showCountryOfFormation} />
                          </span>
                        </span>
                      </button>
                    </div>
                    {showCountryOfFormation && (
                      <FinalForm
                        onSubmit={() => { }}
                        initialValues={{ countryOfFormation: countryOfFormation }}
                        render={({ handleSubmit, form }) => (
                          <form onSubmit={handleSubmit}>
                            <FormSpy
                              subscription={{ values: true }}
                              onChange={formState => {
                                if (formState.values.countryOfFormation !== countryOfFormation) {
                                  handleCountryOfFormationChange(formState.values.countryOfFormation);
                                }
                              }}
                            />
                            <div className={css.plain}>
                              <FieldSelect id="countryOfFormation" name="countryOfFormation">
                                <option value="">All countries</option>
                                {Country.getAllCountries().map(country => (
                                  <option key={country.isoCode} value={country.name}>
                                    {country.name}
                                  </option>
                                ))}
                              </FieldSelect>
                              <button className={css.clearButton} type="button" onClick={() => { form.change('countryOfFormation', null); handleCountryOfFormationChange(null); }}>
                                <FormattedMessage id={'FilterPlain.clear'} />
                              </button>
                            </div>
                          </form>
                        )}
                      />
                    )}
                  </div>

                  <div className={css.filter}>
                    <div className={css.filterHeader}>
                      <button className={css.labelButton} onClick={() => setShowStateOfIncorporation(!showStateOfIncorporation)}>
                        <span className={css.labelButtonContent}>
                          <span className={css.label}>
                            State of incorporation (Domestic)
                            {activeFilterIndicator(stateOfIncorpDomestic ? 1 : 0)}
                          </span>
                          <span className={css.openSign}>
                            <IconPlus isOpen={showStateOfIncorporation} />
                          </span>
                        </span>
                      </button>
                    </div>
                    {showStateOfIncorporation && (
                      <FinalForm
                        onSubmit={() => { }}
                        initialValues={{ stateOfIncorpDomestic: stateOfIncorpDomestic }}
                        render={({ handleSubmit, form }) => (
                          <form onSubmit={handleSubmit}>
                            <FormSpy
                              subscription={{ values: true }}
                              onChange={formState => {
                                if (formState.values.stateOfIncorpDomestic !== stateOfIncorpDomestic) {
                                  handleStateOfIncorpDomesticChange(formState.values.stateOfIncorpDomestic);
                                }
                              }}
                            />
                            <div className={css.plain}>
                              <FieldSelect id="stateOfIncorpDomestic" name="stateOfIncorpDomestic">
                                <option value="">All states</option>
                                {State.getStatesOfCountry('US').map(state => (
                                  <option key={state.isoCode} value={state.name}>
                                    {state.name}
                                  </option>
                                ))}
                              </FieldSelect>
                              <button className={css.clearButton} type="button" onClick={() => { form.change('stateOfIncorpDomestic', null); handleStateOfIncorpDomesticChange(null); }}>
                                <FormattedMessage id={'FilterPlain.clear'} />
                              </button>
                            </div>
                          </form>
                        )}
                      />
                    )}
                  </div>

                  <div className={css.filter}>
                    <div className={css.filterHeader}>
                      <button className={css.labelButton} onClick={() => setShowRegisteredToOperateForeign(!showRegisteredToOperateForeign)}>
                        <span className={css.labelButtonContent}>
                          <span className={css.label}>
                            Registered to operate as foreign entity
                            {activeFilterIndicator(registeredToOperateForeign ? 1 : 0)}
                          </span>
                          <span className={css.openSign}>
                            <IconPlus isOpen={showRegisteredToOperateForeign} />
                          </span>
                        </span>
                      </button>
                    </div>
                    {showRegisteredToOperateForeign && (
                      <FinalForm
                        onSubmit={() => { }}
                        initialValues={{ registeredToOperateForeign: registeredToOperateForeign }}
                        render={({ handleSubmit, form }) => (
                          <form onSubmit={handleSubmit}>
                            <FormSpy
                              subscription={{ values: true }}
                              onChange={formState => {
                                if (formState.values.registeredToOperateForeign !== registeredToOperateForeign) {
                                  handleRegisteredToOperateForeignChange(formState.values.registeredToOperateForeign);
                                }
                              }}
                            />
                            <div className={css.plain}>
                              <FieldSelect id="registeredToOperateForeign" name="registeredToOperateForeign">
                                <option value="">All states</option>
                                {State.getStatesOfCountry('US').map(state => (
                                  <option key={state.isoCode} value={state.name}>
                                    {state.name}
                                  </option>
                                ))}
                              </FieldSelect>
                              <button className={css.clearButton} type="button" onClick={() => { form.change('registeredToOperateForeign', null); handleRegisteredToOperateForeignChange(null); }}>
                                <FormattedMessage id={'FilterPlain.clear'} />
                              </button>
                            </div>
                          </form>
                        )}
                      />
                    )}
                  </div>

                  {/* Service Domains - Multiple select dropdown filter */}
                  <div className={css.filter}>
                    <div className={css.filterHeader}>
                      <button className={css.labelButton} onClick={() => setShowServiceDomains(!showServiceDomains)}>
                        <span className={css.labelButtonContent}>
                          <span className={css.label}>
                            Service Domains
                            {activeFilterIndicator(serviceDomains?.length || 0)}
                          </span>
                          <span className={css.openSign}>
                            <IconPlus isOpen={showServiceDomains} />
                          </span>
                        </span>
                      </button>
                    </div>
                    {showServiceDomains && (
                      <div className={css.plain}>
                        <Select
                          mode="multiple"
                          allowClear
                          style={{ width: '100%' }}
                          placeholder="Select service domains"
                          value={serviceDomains}
                          onChange={handleServiceDomainsChange}
                          optionFilterProp="label"
                          showSearch
                        >
                          {serviceDomainOptions.map(opt => (
                            <Select.Option key={opt.option} value={opt.option} label={opt.label}>
                              {opt.label}
                            </Select.Option>
                          ))}
                        </Select>
                        <button className={css.clearButton} type="button" onClick={() => handleServiceDomainsChange([])}>
                          <FormattedMessage id={'FilterPlain.clear'} />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Technology Domains - Multiple select dropdown filter */}
                  <div className={css.filter}>
                    <div className={css.filterHeader}>
                      <button className={css.labelButton} onClick={() => setShowTechnologyDomains(!showTechnologyDomains)}>
                        <span className={css.labelButtonContent}>
                          <span className={css.label}>
                            Technology Domains
                            {activeFilterIndicator(technologyDomains?.length || 0)}
                          </span>
                          <span className={css.openSign}>
                            <IconPlus isOpen={showTechnologyDomains} />
                          </span>
                        </span>
                      </button>
                    </div>
                    {showTechnologyDomains && (
                      <div className={css.plain}>
                        <Select
                          mode="multiple"
                          allowClear
                          style={{ width: '100%' }}
                          placeholder="Select technology domains"
                          value={technologyDomains}
                          onChange={handleTechnologyDomainsChange}
                          optionFilterProp="label"
                          showSearch
                        >
                          {technologyOptions.map(opt => (
                            <Select.Option key={opt.option} value={opt.option} label={opt.label}>
                              {opt.label}
                            </Select.Option>
                          ))}
                        </Select>
                        <button className={css.clearButton} type="button" onClick={() => handleTechnologyDomainsChange([])}>
                          <FormattedMessage id={'FilterPlain.clear'} />
                        </button>
                      </div>
                    )}
                  </div>

                </>
              ) : null}

              {(!searchParams?.pub_listingType) || (searchParams?.pub_listingType === 'candidate') && (
                <FinalForm
                  initialValues={{ addressDetail: '' }}
                  onSubmit={values => {
                    // This onSubmit will be triggered when the form is submitted.
                    // You can add logic here to apply all filters at once.
                  }}
                  style={{ zIndex: '999' }}
                  render={({ handleSubmit, form }) => {

                    // --- START: NEW STATE & HANDLERS FOR SKILL FILTER ---

                    // State to hold the list of skills to filter by
                    const [skillsToFilter, setSkillsToFilter] = useState([]);

                    // State for the current skill name input
                    const [candidateSkillName, setCandidateSkillName] = useState('');

                    // State for the current skill experience input
                    const [candidateSkillExp, setCandidateSkillExp] = useState(''); // Use string to handle empty input

                    useEffect(() => {
                      if (candidateSkillFilter.length === 0) {
                        setSkillsToFilter([]);
                      }
                    }, [candidateSkillFilter]);

                    // Function to add a skill to the filter list
                    const handleAddSkill = () => {
                      if (candidateSkillName && candidateSkillExp > 0 && skillsToFilter.length < 3) {
                        const newSkill = { name: candidateSkillName, exp: candidateSkillExp };

                        setSkillsToFilter([...skillsToFilter, newSkill]);

                        if (candidateSkillFilter.length > 0) {
                          setCandidateSkillFilter([...candidateSkillFilter, newSkill]);
                        } else {
                          setCandidateSkillFilter([newSkill]);
                        }

                        // Clear inputs
                        setCandidateSkillName('');
                        setCandidateSkillExp('');
                      }
                    };

                    // Function to remove a skill from both lists by its index
                    const handleRemoveSkill = (indexToRemove) => {
                      setSkillsToFilter(skillsToFilter.filter((_, index) => index !== indexToRemove));
                      setCandidateSkillFilter(candidateSkillFilter.filter((_, index) => index !== indexToRemove));
                    };

                    // --- END: NEW STATE & HANDLERS FOR SKILL FILTER ---

                    return (
                      <form onSubmit={handleSubmit} className={css.locationSearchForm}>
                        {/* The existing FormSpy for location */}
                        <FormSpy
                          onChange={async (formState) => {
                            // This logic is for the location filter
                            if (formState?.values?.addressDetail?.selectedPlace) {
                              const address = formState?.values?.addressDetail.selectedPlace.address;
                              const bounds = formState?.values?.addressDetail.selectedPlace.bounds;

                              if (address === '') {
                                // Get only URL params, not state-based custom filters
                                const urlFilters = validUrlQueryParamsFromProps(props);
                                const filtersWithoutLocation = { ...urlFilters };
                                delete filtersWithoutLocation.address;
                                delete filtersWithoutLocation.bounds;
                                history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, filtersWithoutLocation));
                                return;
                              }

                              const boundsParam = bounds
                                ? `${bounds.ne.lat},${bounds.ne.lng},${bounds.sw.lat},${bounds.sw.lng}`
                                : null;

                              // Get only URL params, not state-based custom filters
                              const urlFilters = validUrlQueryParamsFromProps(props);

                              if (searchParams?.keywords == '') {
                                delete searchParams.keywords;
                              }

                              // Only update URL with location params, not custom filters
                              const mergedFilters = {
                                ...urlFilters,
                                address,
                                bounds: boundsParam,
                              };

                              const searchPageURL = createResourceLocatorString(
                                'SearchPage',
                                routeConfiguration,
                                {},
                                mergedFilters
                              );
                              history.push(searchPageURL);
                            }
                          }}
                        />

                        {/* --- START: NEW SKILLS FILTER UI --- */}
                        <div className={css.filter}>
                          <div className={css.filterHeader}>
                            {/* For accessibility and semantics, use type="button" for non-submitting buttons */}
                            <button type="button" className={css.labelButton} onClick={() => setShowSkillsFilter(!showSkillsFilter)}>
                              <span className={css.labelButtonContent}>
                                <span className={css.label}>
                                  Skills
                                  {activeFilterIndicator(candidateSkillFilter.length)}
                                </span>
                                <span className={css.openSign}>
                                  <IconPlus isOpen={showSkillsFilter} />
                                </span>
                              </span>
                            </button>
                          </div>

                          {showSkillsFilter && (
                            <div className={css.plain}>
                              {/* Display added skills */}
                              <div className={css.addedSkillsContainer}>
                                {skillsToFilter.map((skill, index) => (
                                  <div key={index} className={css.skillTag}>
                                    {skill.name} ({skill.exp}+ yrs)
                                    <Button size='small' className={css.removeSkillButton} onClick={() => handleRemoveSkill(index)} shape="circle" icon={<CloseOutlined />} />
                                  </div>
                                ))}
                              </div>

                              {/* Show input fields only if less than 3 skills have been added */}
                              {skillsToFilter.length < 3 && (
                                <div className={css.skillInputGroup}>
                                  <div className={css.skillNameInput}>
                                    <label htmlFor="skillName">Skill Name</label>
                                    <input
                                      id="skillName"
                                      type="text"
                                      value={candidateSkillName}
                                      onChange={(e) => setCandidateSkillName(e.target.value)}
                                      placeholder="e.g., Java"
                                      className={css.input}
                                    />
                                  </div>
                                  <div className={css.skillExpInput}>
                                    <label htmlFor="skillExp">Experience (years)</label>
                                    <input
                                      id="skillExp"
                                      type="number"
                                      value={candidateSkillExp}
                                      onChange={(e) => setCandidateSkillExp(e.target.value)}
                                      placeholder="e.g., 2"
                                      min="0"
                                      className={css.input}
                                    />
                                  </div>
                                  <Button
                                    className={css.clearButton}
                                    onClick={handleAddSkill}
                                    disabled={!candidateSkillName || !candidateSkillExp}
                                  >
                                    Add
                                  </Button>
                                </div>
                              )}
                              {skillsToFilter.length >= 3 && (
                                <p className={css.limitMessage}>You can add a maximum of 3 skills.</p>
                              )}
                            </div>
                          )}
                        </div>
                        {/* --- END: NEW SKILLS FILTER UI --- */}

                      </form>
                    );
                  }}
                />
              )}

              {(searchParams?.pub_listingType === 'job_listing') && (
                <FinalForm
                  initialValues={{ addressDetail: '' }}
                  onSubmit={values => {
                    // This onSubmit will be triggered when the form is submitted.
                    // You can add logic here to apply all filters at once.
                  }}
                  style={{ zIndex: '999' }}
                  render={({ handleSubmit, form }) => {

                    // --- START: NEW STATE & HANDLERS FOR SKILL FILTER ---

                    // State to hold the list of skills to filter by
                    const [skillsToFilter, setSkillsToFilter] = useState([]);

                    // State for the current skill nRequired
                    const [requiredSkillName, setRequiredSkillName] = useState('');

                    // State for the current skill experience input
                    const [requiredSkillExp, setRequiredSkillExp] = useState(''); // Use string to handle empty input

                    useEffect(() => {
                      if (requiredSkillFilter.length === 0) {
                        setSkillsToFilter([]);
                      }
                    }, [requiredSkillFilter]);

                    // Function to add a skill to the filter list
                    const handleAddSkill = () => {
                      if (requiredSkillName && requiredSkillExp > 0 && skillsToFilter.length < 3) {
                        const newSkill = { name: requiredSkillName, exp: requiredSkillExp };

                        setSkillsToFilter([...skillsToFilter, newSkill]);

                        if (requiredSkillFilter.length > 0) {
                          setRequiredSkillFilter([...requiredSkillFilter, newSkill]);
                        } else {
                          setRequiredSkillFilter([newSkill]);
                        }

                        // Clear inputs
                        setRequiredSkillName('');
                        setRequiredSkillExp('');
                      }
                    };

                    // Function to remove a skill from both lists by its index
                    const handleRemoveSkill = (indexToRemove) => {
                      setSkillsToFilter(skillsToFilter.filter((_, index) => index !== indexToRemove));
                      setRequiredSkillFilter(requiredSkillFilter.filter((_, index) => index !== indexToRemove));
                    };

                    // --- END: NEW STATE & HANDLERS FOR SKILL FILTER ---

                    return (
                      <form onSubmit={handleSubmit} className={css.locationSearchForm}>
                        {/* The existing FormSpy for location */}
                        <FormSpy
                          onChange={async (formState) => {
                            // This logic is for the location filter
                            if (formState?.values?.addressDetail?.selectedPlace) {
                              const address = formState?.values?.addressDetail.selectedPlace.address;
                              const bounds = formState?.values?.addressDetail.selectedPlace.bounds;

                              if (address === '') {
                                // Get only URL params, not state-based custom filters
                                const urlFilters = validUrlQueryParamsFromProps(props);
                                const filtersWithoutLocation = { ...urlFilters };
                                delete filtersWithoutLocation.address;
                                delete filtersWithoutLocation.bounds;
                                history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, filtersWithoutLocation));
                                return;
                              }

                              const boundsParam = bounds
                                ? `${bounds.ne.lat},${bounds.ne.lng},${bounds.sw.lat},${bounds.sw.lng}`
                                : null;

                              // Get only URL params, not state-based custom filters
                              const urlFilters = validUrlQueryParamsFromProps(props);

                              if (searchParams?.keywords == '') {
                                delete searchParams.keywords;
                              }

                              // Only update URL with location params, not custom filters
                              const mergedFilters = {
                                ...urlFilters,
                                address,
                                bounds: boundsParam,
                              };

                              const searchPageURL = createResourceLocatorString(
                                'SearchPage',
                                routeConfiguration,
                                {},
                                mergedFilters
                              );
                              history.push(searchPageURL);
                            }
                          }}
                        />

                        {/* --- START: NEW SKILLS FILTER UI --- */}
                        <div className={css.filter}>
                          <div className={css.filterHeader}>
                            {/* For accessibility and semantics, use type="button" for non-submitting buttons */}
                            <button type="button" className={css.labelButton} onClick={() => setShowRequiredSkillsFilter(!showRequiredSkillsFilter)}>
                              <span className={css.labelButtonContent}>
                                <span className={css.label}>
                                  Required skills
                                  {activeFilterIndicator(requiredSkillFilter.length)}
                                </span>

                                <span className={css.openSign}>
                                  <IconPlus isOpen={showRequiredSkillsFilter} />
                                </span>
                              </span>
                            </button>
                          </div>

                          {showRequiredSkillsFilter && (
                            <div className={css.plain}>
                              {/* Display added skills */}
                              <div className={css.addedSkillsContainer}>
                                {skillsToFilter.map((skill, index) => (
                                  <div key={index} className={css.skillTag}>
                                    {skill.name} ({skill.exp}+ yrs)
                                    <Button size='small' className={css.removeSkillButton} onClick={() => handleRemoveSkill(index)} shape="circle" icon={<CloseOutlined />} />
                                  </div>
                                ))}
                              </div>

                              {/* Show input fields only if less than 3 skills have been added */}
                              {skillsToFilter.length < 3 && (
                                <div className={css.skillInputGroup}>
                                  <div className={css.skillNameInput}>
                                    <label htmlFor="skillName">Skill Name</label>
                                    <input
                                      id="skillName"
                                      type="text"
                                      value={requiredSkillName}
                                      onChange={(e) => setRequiredSkillName(e.target.value)}
                                      placeholder="e.g., Java"
                                      className={css.input}
                                    />
                                  </div>
                                  <div className={css.skillExpInput}>
                                    <label htmlFor="skillExp">Experience (years)</label>
                                    <input
                                      id="skillExp"
                                      type="number"
                                      value={requiredSkillExp}
                                      onChange={(e) => setRequiredSkillExp(e.target.value)}
                                      placeholder="e.g., 2"
                                      min="0"
                                      className={css.input}
                                    />
                                  </div>
                                  <Button
                                    className={css.clearButton}
                                    onClick={handleAddSkill}
                                    disabled={!requiredSkillName || !requiredSkillExp}
                                  >
                                    Add
                                  </Button>
                                </div>
                              )}
                              {skillsToFilter.length >= 3 && (
                                <p className={css.limitMessage}>You can add a maximum of 3 skills.</p>
                              )}
                            </div>
                          )}
                        </div>
                        {/* --- END: NEW SKILLS FILTER UI --- */}

                      </form>
                    );
                  }}
                />
              )}
              {(searchParams?.pub_listingType === 'job_listing') && (
                <FinalForm
                  initialValues={{ addressDetail: '' }}
                  onSubmit={values => {
                    // This onSubmit will be triggered when the form is submitted.
                    // You can add logic here to apply all filters at once.
                  }}
                  style={{ zIndex: '999' }}
                  render={({ handleSubmit, form }) => {

                    // --- START: NEW STATE & HANDLERS FOR SKILL FILTER ---

                    // State to hold the list of skills to filter by
                    const [skillsToFilter, setSkillsToFilter] = useState([]);

                    // State for the current skill nRequired
                    const [preferredSkillName, setPreferredSkillName] = useState('');

                    // State for the current skill experience input
                    const [preferredSkillExp, setPreferredSkillExp] = useState(''); // Use string to handle empty input

                    useEffect(() => {
                      if (preferredSkillFilter.length === 0) {
                        setSkillsToFilter([]);
                      }
                    }, [preferredSkillFilter]);

                    // Function to add a skill to the filter list
                    const handleAddSkill = () => {
                      if (preferredSkillName && preferredSkillExp > 0 && skillsToFilter.length < 3) {
                        const newSkill = { name: preferredSkillName, exp: preferredSkillExp };

                        setSkillsToFilter([...skillsToFilter, newSkill]);

                        if (preferredSkillFilter.length > 0) {
                          setPreferredSkillFilter([...preferredSkillFilter, newSkill]);
                        } else {
                          setPreferredSkillFilter([newSkill]);
                        }

                        // Clear inputs
                        setPreferredSkillName('');
                        setPreferredSkillExp('');
                      }
                    };

                    // Function to remove a skill from both lists by its index
                    const handleRemoveSkill = (indexToRemove) => {
                      setSkillsToFilter(skillsToFilter.filter((_, index) => index !== indexToRemove));
                      setPreferredSkillFilter(preferredSkillFilter.filter((_, index) => index !== indexToRemove));
                    };

                    // --- END: NEW STATE & HANDLERS FOR SKILL FILTER ---

                    return (
                      <form onSubmit={handleSubmit} className={css.locationSearchForm}>
                        {/* The existing FormSpy for location */}
                        <FormSpy
                          onChange={async (formState) => {
                            // This logic is for the location filter
                            if (formState?.values?.addressDetail?.selectedPlace) {
                              const address = formState?.values?.addressDetail.selectedPlace.address;
                              const bounds = formState?.values?.addressDetail.selectedPlace.bounds;
                              if (address === '') {
                                const previousFilters = getAllCurrentFilters();
                                const filtersWithoutLocation = { ...previousFilters };
                                delete filtersWithoutLocation.address;
                                delete filtersWithoutLocation.bounds;
                                history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, filtersWithoutLocation));
                                return;
                              }

                              const boundsParam = bounds
                                ? `${bounds.ne.lat},${bounds.ne.lng},${bounds.sw.lat},${bounds.sw.lng}`
                                : null;

                              const previousFilters = getAllCurrentFilters();

                              if (searchParams?.keywords == '') {
                                delete searchParams.keywords;
                              }

                              // IMPORTANT: You will need to add the skills filter to the URL here
                              // Example: pub_skills: skillsToFilter.map(s => `${s.name}_${s.exp}`).join(',')
                              const mergedFilters = {
                                ...previousFilters,
                                address,
                                bounds: boundsParam,
                                // pub_skills: skillsParam, // Add the skills parameter here
                              };

                              const searchPageURL = createResourceLocatorString(
                                'SearchPage',
                                routeConfiguration,
                                {},
                                mergedFilters
                              );
                              history.push(searchPageURL);
                            }
                          }}
                        />

                        {/* --- START: NEW SKILLS FILTER UI --- */}
                        <div className={css.filter}>
                          <div className={css.filterHeader}>
                            {/* For accessibility and semantics, use type="button" for non-submitting buttons */}
                            <button type="button" className={css.labelButton} onClick={() => setShowPreferredSkillsFilter(!showPreferredSkillsFilter)}>
                              <span className={css.labelButtonContent}>
                                <span className={css.label}>
                                  Preferred skills
                                  {activeFilterIndicator(preferredSkillFilter.length)}
                                </span>
                                <span className={css.openSign}>
                                  <IconPlus isOpen={showPreferredSkillsFilter} />
                                </span>
                              </span>
                            </button>
                          </div>

                          {showPreferredSkillsFilter && (
                            <div className={css.plain}>
                              {/* Display added skills */}
                              <div className={css.addedSkillsContainer}>
                                {skillsToFilter.map((skill, index) => (
                                  <div key={index} className={css.skillTag}>
                                    {skill.name} ({skill.exp}+ yrs)
                                    <Button size='small' className={css.removeSkillButton} onClick={() => handleRemoveSkill(index)} shape="circle" icon={<CloseOutlined />} />
                                  </div>
                                ))}
                              </div>

                              {/* Show input fields only if less than 3 skills have been added */}
                              {skillsToFilter.length < 3 && (
                                <div className={css.skillInputGroup}>
                                  <div className={css.skillNameInput}>
                                    <label htmlFor="skillName">Skill Name</label>
                                    <input
                                      id="skillName"
                                      type="text"
                                      value={preferredSkillName}
                                      onChange={(e) => setPreferredSkillName(e.target.value)}
                                      placeholder="e.g., Java"
                                      className={css.input}
                                    />
                                  </div>
                                  <div className={css.skillExpInput}>
                                    <label htmlFor="skillExp">Experience (years)</label>
                                    <input
                                      id="skillExp"
                                      type="number"
                                      value={preferredSkillExp}
                                      onChange={(e) => setPreferredSkillExp(e.target.value)}
                                      placeholder="e.g., 2"
                                      min="0"
                                      className={css.input}
                                    />
                                  </div>
                                  <Button
                                    className={css.clearButton}
                                    onClick={handleAddSkill}
                                    disabled={!preferredSkillName || !preferredSkillExp}
                                  >
                                    Add
                                  </Button>
                                </div>
                              )}
                              {skillsToFilter.length >= 3 && (
                                <p className={css.limitMessage}>You can add a maximum of 3 skills.</p>
                              )}
                            </div>
                          )}
                        </div>
                        {/* --- END: NEW SKILLS FILTER UI --- */}

                      </form>
                    );
                  }}
                />
              )}

              {(searchParams?.pub_listingType !== 'rfp_listing') && (
                <FinalForm
                  initialValues={{ addressDetail: '' }}
                  onSubmit={values => {
                  }}
                  style={{ zIndex: '999' }}
                  render={({ handleSubmit, form }) => {
                    // Store the form reference so we can clear it from resetAll
                    useEffect(() => {
                      locationFormRef.current = form;
                      return () => {
                        locationFormRef.current = null;
                      };
                    }, [form]);

                    return ( // This form seems to be for location search, which is already in Topbar. Maybe it's for mobile? No, it's inside filter column.
                      <form onSubmit={handleSubmit} className={css.locationSearchForm}>

                        <FormSpy
                          onChange={async (formState) => {

                            if (formState?.values?.addressDetail?.selectedPlace) {
                              const address = formState?.values?.addressDetail.selectedPlace.address;
                              const bounds = formState?.values?.addressDetail.selectedPlace.bounds; // This logic is complex and seems to be duplicated from TopbarSearchForm

                              if (address === '') {
                                setLocationSelected(false);
                                // Get only URL params, not state-based custom filters
                                const urlFilters = validUrlQueryParamsFromProps(props);
                                const filtersWithoutLocation = { ...urlFilters };
                                delete filtersWithoutLocation.address;
                                delete filtersWithoutLocation.bounds;
                                history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, filtersWithoutLocation));
                                return;
                              }

                              setLocationSelected(true);

                              const boundsParam = bounds
                                ? `${bounds.ne.lat},${bounds.ne.lng},${bounds.sw.lat},${bounds.sw.lng}`
                                : null;

                              // Get only URL params, not state-based custom filters
                              const urlFilters = validUrlQueryParamsFromProps(props);

                              if (searchParams?.keywords == '') {
                                delete searchParams.keywords;
                              }

                              // Only update URL with location params, not custom filters
                              const mergedFilters = {
                                ...urlFilters,
                                address,
                                bounds: boundsParam,
                              };


                              const searchPageURL = createResourceLocatorString(
                                'SearchPage',
                                routeConfiguration,
                                {},
                                mergedFilters
                              );

                              history.push(searchPageURL);
                            }
                          }}
                        >
                        </FormSpy>

                        <div className={css.filter}>
                          <div className={css.filterHeader}>
                            <button className={css.labelButton} onClick={() => setShowLocationSearch(!showLocationSearch)}>
                              <span className={css.labelButtonContent}>
                                <span className={css.label}>
                                  Location
                                  {locationSelected ? activeFilterIndicator(1) : null}
                                </span>
                                {/* <span className={css.label}>Custom Filters</span> */}
                                {/* {activeFilterIndicator(activeCustomFiltersCount)} */}
                                <span className={css.openSign}>
                                  <IconPlus isOpen={showLocationSearch} />
                                </span>
                              </span>
                            </button>
                          </div>
                          {showLocationSearch && (
                            <div className={css.plain}>
                              <Field
                                id="addressDetail"
                                name="addressDetail"
                                format={identity}
                                component={LocationAutocompleteInput}
                                className={css.location}
                              // label={'Address'}
                              // validate={validators.required(
                              //   intl.formatMessage({ id: 'ProfileSettingsForm.cityAddressRequired' })
                              // )}
                              />
                              <button
                                className={css.clearButton}
                                type="button"
                                onClick={() => clearSearchLocation(form)}
                              >
                                <FormattedMessage id={'FilterPlain.clear'} />
                              </button>
                            </div>
                          )}
                        </div>


                      </form>
                    );
                  }}
                />
              )}

            </SearchFiltersMobile>
            <MainPanelHeader
              className={css.mainPanel}
              sortByComponent={sortBy('desktop')}
              isSortByActive={sortConfig.active}
              listingsAreLoaded={shouldShowListings}
              resultsCount={totalItems}
              searchInProgress={searchInProgress}
              searchListingsError={searchListingsError}
              noResultsInfo={noResultsInfo}
            />
            <div
              className={classNames(css.listingsForGridVariant, {
                [css.newSearchInProgress]: !(shouldShowListings || searchListingsError),
              })}
            >
              {searchListingsError ? (
                <H3 className={css.error}>
                  <FormattedMessage id="SearchPage.searchError" />
                </H3>
              ) : null}
              {!isValidDatesFilter ? (
                <H5>
                  <FormattedMessage id="SearchPage.invalidDatesFilter" />
                </H5>
              ) : null}
              {/* Pagination: all types now use server-side pagination (backend returns current page only) */}
              {(() => {
                if (manualPagination && shouldShowListings) {
                  // Backend already returned only this page's listings — show them directly.
                  const paginatedListings = processedListings;

                  return (
                    <SearchResultsPanel
                      className={css.searchListingsPanel}
                      listings={paginatedListings}
                      pagination={manualPagination}
                      search={parse(location.search)}
                      isMapVariant={false}
                      listingTypeParam={listingTypePathParam}
                      currentUser={currentUser}
                      currentUserAdminId={currentUserAdminId}
                      onInitiateApplyTransaction={onInitiateApplyTransaction}
                      acceptedConsultantsExist={acceptedConsultantsExist}
                      onSearchAcceptedConsultants={onSearchAcceptedConsultants}
                      profileListing={profileListing}
                      allApplyTransactions={allApplyTransactions}
                      subscriptionStatus={subscriptionStatus}
                      onPayForRFP={onPayForRFP}
                      subscriptionPrices={subscriptionPrices}
                      currentUserPersonaRecords={currentUserPersonaRecords}
                      onApplyAttemptWithoutAuth={handleApplyAttemptWithoutAuth}
                      onUploadBlobFile={onUploadBlobFile}
                      onUploadResumeBlobFile={onUploadResumeBlobFile}
                    />
                  );
                } else {
                  // Show placeholder or loading state
                  return (
                    <SearchResultsPanel
                      className={css.searchListingsPanel}
                      listings={shouldShowListings ? processedListings : []}
                      pagination={null}
                      search={parse(location.search)}
                      isMapVariant={false}
                      listingTypeParam={listingTypePathParam}
                      currentUser={currentUser}
                      currentUserAdminId={currentUserAdminId}
                      onInitiateApplyTransaction={onInitiateApplyTransaction}
                      acceptedConsultantsExist={acceptedConsultantsExist}
                      onSearchAcceptedConsultants={onSearchAcceptedConsultants}
                      profileListing={profileListing}
                      allApplyTransactions={allApplyTransactions}
                      subscriptionStatus={subscriptionStatus}
                      onPayForRFP={onPayForRFP}
                      subscriptionPrices={subscriptionPrices}
                      currentUserPersonaRecords={currentUserPersonaRecords}
                      onApplyAttemptWithoutAuth={handleApplyAttemptWithoutAuth}
                      onUploadBlobFile={onUploadBlobFile}
                      onUploadResumeBlobFile={onUploadResumeBlobFile}
                    />
                  );
                }
              })()}

              {/* Progressive loading indicator */}
              {progressiveLoading && (
                <div className={css.loadingMore}>
                  {/* <FormattedMessage id="SearchPage.loadingMoreResults" /> */}
                  Loading more results...
                  <span className={css.loadingType}>
                    for {searchParams?.pub_listingType || 'listings'}
                  </span>
                  <span className={css.loadingCount}>
                    ({listings.length} loaded so far)
                  </span>
                </div>
              )}

              {/* Progressive loading error */}
              {progressiveLoadingError && (
                <div className={css.loadingError}>
                  <FormattedMessage id="SearchPage.loadingError" />
                  <button
                    onClick={() => onLoadListingsProgressively(searchParams, config)}
                    className={css.retryButton}
                  >
                    {/* <FormattedMessage id="SearchPage.retry" /> */}
                    Retry loading...
                  </button>
                </div>
              )}

              {/* Loading completion indicator */}
              {hasAllListings && (
                <div className={css.allLoaded}>
                  {/* <FormattedMessage id="SearchPage.allResultsLoaded" /> */}
                  All results are loaded
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <FooterContainer />
      {/* ── Login popup for unauthenticated apply attempts ── */}
      <LoginPopupModal
        isOpen={isLoginPopupOpen}
        onClose={() => setIsLoginPopupOpen(false)}
        from={`/profile-settings?applyWorkId=${applyWorkId}`}
        applyWorkId={applyWorkId}
        showRoleSelect={true}
        agencyOnly={loginPopupAgencyOnly}
      />
    </Page>
  );
};

/**
 * SearchPage component with grid layout (no map)
 *
 * @param {Object} props
 * @param {propTypes.currentUser} [props.currentUser] - The current user
 * @param {Array<propTypes.listing>} [props.listings] - The listings
 * @param {propTypes.pagination} [props.pagination] - The pagination
 * @param {boolean} [props.scrollingDisabled] - Whether the scrolling is disabled
 * @param {boolean} [props.searchInProgress] - Whether the search is in progress
 * @param {propTypes.error} [props.searchListingsError] - The search listings error
 * @param {Object} [props.searchParams] - The search params from the Redux state
 * @param {Function} [props.onManageDisableScrolling] - The function to manage the disable scrolling
 * @returns {JSX.Element}
 */
const EnhancedSearchPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const searchListingsError = props.searchListingsError;
  if (isForbiddenError(searchListingsError)) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const { currentUser, ...restOfProps } = props;
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRightsUser = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(searchListingsError);
  const hasNoViewingRightsError = isErrorNoViewingPermission(searchListingsError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (hasNoViewingRightsUser || hasNoViewingRightsError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <SearchPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      currentUser={currentUser}
      {...restOfProps}
    />
  );
};

const mapStateToProps = state => {
  const { currentUser, manualFieldOptions, profileListing, allApplyTransactions, subscriptionsOfUser, personaRecords, currentUserPersonaRecords } = state.user;
  const { acceptedConsultantsExist } = state.linkedContacts;
  const {
    currentPageResultIds,
    pagination,
    searchInProgress,
    searchListingsError,
    searchParams,
    currentUserAdminId,
    // Progressive loading state
    allResultIds,
    progressiveLoading,
    progressiveLoadingError,
    hasAllListings,
  } = state.SearchPage;

  // Use allResultIds if available (progressive loading), otherwise fall back to currentPageResultIds
  const resultIds = allResultIds.length > 0 ? allResultIds : currentPageResultIds;
  const listings = getListingsById(state, resultIds);

  return {
    currentUser,
    listings,
    pagination,
    scrollingDisabled: isScrollingDisabled(state),
    searchInProgress,
    searchListingsError,
    searchParams,
    manualFieldOptions,
    currentUserAdminId,
    acceptedConsultantsExist,
    profileListing,
    allApplyTransactions,
    // Progressive loading state
    progressiveLoading,
    progressiveLoadingError,
    hasAllListings,
    subscriptionsOfUser,
    subscriptionPrices: state.stripe.prices,
    subscriptionError: state.stripe.subscriptionError,
    personaRecords,
    currentUserPersonaRecords,
  };
};

export const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onInitiateApplyTransaction: (listingId, acceptingInvitation, consultantId, consultantListingId, appliedByAgency, identityDocuments, workAuthorizationDocuments, applicationResume, status, consentId) => dispatch(initiateApplyTransaction(listingId, acceptingInvitation, consultantId, consultantListingId, appliedByAgency, identityDocuments, workAuthorizationDocuments, applicationResume, status, null, null, consentId)),
  onSaveRFPPayment: sessionId => dispatch(saveRFPPayments(sessionId)),
  onSearchAcceptedConsultants: q => dispatch(searchAcceptedConsultants(q)),
  onFetchAllApplyTxns: () => dispatch(getAllApplyTransactions()),
  onLoadListingsProgressively: (searchParams, config) => dispatch(loadListingsProgressively(searchParams, config)),
  onSearchWithFilters: (searchParams, filterParams, config) => dispatch(searchWithFilters(searchParams, filterParams, config)),
  onPayForRFP: (priceId, quantity, type, rfpListingId) => dispatch(payForRFPDuck(priceId, quantity, type, rfpListingId)),
  getSubscriptionPrices: () => dispatch(handleGetSubscriptionPrice()),
  onUploadBlobFile: (data) => dispatch(uploadBlobFile(data)),
  onUploadResumeBlobFile: data => dispatch(uploadResumeBlobFile(data)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const SearchPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnhancedSearchPage);

export default SearchPage;
